import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  MapaParcelas,
  CAPAS_DEMO_FULL,
  parcelaIdsDentroDeAoi,
  type ParcelaFeatureMeta,
  type ParcelaLiveState,
  type FiltrosVisor,
} from '@aosenuma/sig-viewer';
import { EstadoBadge, FilterGroup, showToast } from '@aosenuma/ui';
import type { EstadoGestion, Gravedad, SubtipoIrregularidad } from '@aosenuma/lib-types';
import { ESTADOS_GESTION, GRAVEDADES, isUsoComunParcelaId, numParcelaCampo, tituloParcelaCampo, tituloParcelaConTitular } from '@aosenuma/lib-types';
import {
  bffFetch, BffError, getSupabase, invalidateCache, isSupabaseConfigured,
  uploadExpedienteFile, useCapturaHydrate, useCapturaHydrateUnmasked, useParcelasTecnicas,
  useCapas,
  useExpedientesList, mapSchemaTipoDocToCatalogDocId,
  MfaUnlockModal,
  useUser,
  type CapturaHydrateData,
  type CapturaHydrateUnmaskedData,
} from '@aosenuma/lib-supabase';
import {
  normalizeCURP, validateCURP,
  normalizeRFC, validateRFC,
  normalizeCLABE, validateCLABE,
  normalizeClaveElector, validateClaveElector,
  normalizeTelefonoMx, validateTelefonoMx,
  normalizeEmail, validateEmail,
  validateFolioDerechos, validateFolioTierras, normalizeFolioRan,
  validateClaveCatastral, normalizeClaveCatastral,
  normalizeTitular,
  GESTION_NOTAS_JSON_MAX_CHARS,
  type ValidationResult,
} from '@aosenuma/lib-validation';
import {
  buildModeloNegocio,
  formatMxn,
  formatHa,
  parseHaInput,
  PRELIMINAR_MXN_POR_HA,
  CONSTRUCCION_PLAZO_DEFAULT_ANIOS,
  CONSTRUCCION_TARIFA_DEFAULT_MXN_POR_UNIDAD,
} from './modeloNegocio';
import {
  type VisitaNarrativa,
  narrativaPreview,
  coerceNarrativa,
  formatVisitaFechaFull,
  formatVisitaFechaNodo,
  NARRATIVA_FIELD_MAX,
} from './visitaNarrativa';
import { buildReporteSeguimientoDTO } from './buildReporteSeguimientoDto';
import {
  decideTipoDocChange,
  evaluateParcelaClickGuard,
  tipoDocFromParcelaId,
} from './decideTipoDocChange';
import { UploadParcelaPrivada } from './UploadParcelaPrivada';
import { useParcelasMergedCaptura } from '@/hooks/useParcelasMergedCaptura';
import type { ReporteSeguimientoData } from './ReporteSeguimientoDocument';
import { downloadResumenEconomicoXlsx } from './resumenEconomicoXlsx';
import {
  resolveParcelaInput,
  type EjidoFilter,
  type ParcelaCandidate,
} from './resolveParcelaInput';
import {
  DOCS_EJIDAL,
  DOCS_PRIVADA,
  DOCS_EJIDAL_GROUPS,
  DOCS_PRIVADA_GROUPS,
} from '../expedienteDocs/catalogoDocumentos';

type TipoDoc   = 'ejido' | 'privado';
type WizardStep = 1 | 2 | 3;
// Recorte del visor en Captura. Mismas opciones que el dashboard cliente
// (FilterBar.tsx) para coherencia entre paneles.
//   - 'all'         → sin filtro
//   - 'san_pedro'   → AOI espacial (polígono GeoJSON)
//   - 'HUIMILPAN'   → filtro por columna DB parcelas.municipio
//   - 'CORREGIDORA' → ídem
type AreaMapaCaptura = 'all' | 'san_pedro' | 'HUIMILPAN' | 'CORREGIDORA';

const STEP_LABELS = ['Propietario', 'Negociación y estado', 'Documentación'];

/** Límite alineado con `UploadUrlInputSchema` / `upload/complete` en el BFF. */
const CONTRATO_FIRMADO_MAX_BYTES = 50 * 1024 * 1024;

const ESTADO_LABEL: Record<EstadoGestion, string> = {
  sin_prospectar:  'Sin prospectar',
  en_valoracion:   'En valoración',
  en_negociacion:  'En negociación activa',
  contratada:      'Contratada',
  inviable:        'Inviable',
};

/** Color de semáforo por estado — el chip del selector pinta el dot con este
 *  token, alineado con `colorEstado()` del dashboard cliente y EstadoBadge. */
const ESTADO_COLOR: Record<EstadoGestion, string> = {
  sin_prospectar:  'var(--color-sin-prospectar)',
  en_valoracion:   'var(--color-en-valoracion)',
  en_negociacion:  'var(--color-en-negociacion)',
  contratada:      'var(--color-contratada)',
  inviable:        'var(--color-inviable)',
};

/** Label corto para el chip (el long va al title/aria-label). Evita line-wrap
 *  feo en chips estrechos. */
const ESTADO_CHIP_LABEL: Record<EstadoGestion, string> = {
  sin_prospectar:  'Sin prospectar',
  en_valoracion:   'En valoración',
  en_negociacion:  'En negociación',
  contratada:      'Contratada',
  inviable:        'Inviable',
};

/** Solo para migrar borradores viejos que guardaban la clave del catálogo. */
const IRREGULARIDAD_LABEL: Record<SubtipoIrregularidad, string> = {
  sin_certificado_parcelario:  'Parcela sin certificado parcelario · no asignada por el ejido',
  conflicto_limites_vecinos:   'Conflicto de límites con predios vecinos',
  conflicto_familiar_sucesion: 'Proceso sucesorio pendiente · conflicto familiar de herencia',
  posesion_sin_titulo:         'Posesión sin título formal · compra o cesión no formalizada ante RPP',
  gravamen_activo:             'Gravamen activo (hipoteca, embargo) · juicio vigente sobre el predio',
  adeudo_predial:              'Adeudo predial vigente',
  documentacion_incompleta:    'Documentación incompleta · inconsistente o de difícil obtención',
  identidad_inconsistente:     'Identidad inconsistente (INE vs. acta) · representante sin poder notarial',
  doble_titularidad_ran:       'Duplicidad de propietarios · copropiedad · doble titularidad RAN',
  subdivision_irregular:       'Subdivisión irregular del predio',
};

/** Límites `IrregularidadInputSchema` (BFF). */
const IRREGULARIDAD_SUBTIPO_MAX = 80;
const IRREGULARIDAD_DESCRIPCION_MAX = 2000;

function buildIrregularidadesPayload(
  detalle: string,
  gravedadRaw: string,
): { subtipo: string; gravedad: Gravedad; descripcion: string | null }[] {
  const det = detalle.trim();
  if (det.length < 2 || !gravedadRaw) return [];
  const subtipo = det.slice(0, IRREGULARIDAD_SUBTIPO_MAX);
  const descripcion =
    det.length > IRREGULARIDAD_SUBTIPO_MAX
      ? det.slice(0, IRREGULARIDAD_DESCRIPCION_MAX)
      : null;
  return [{ subtipo, gravedad: gravedadRaw as Gravedad, descripcion }];
}

const GRAVEDAD_LABEL: Record<string, string> = {
  leve:       'Leve — no bloquea el proceso',
  media:      'Media — requiere gestión adicional',
  alta:       'Alta — puede retrasar la firma',
  bloqueante: 'Bloqueante — debe resolverse antes de firmar',
};

const TODAY = new Date().toISOString().slice(0, 10);
// TODO Diego: reemplazar con usuario real de Supabase auth
const GESTOR_MOCK = 'tecnico_aosenuma';

function fmtFechaReportePdf(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * NOTA: La función `resolveParcelaInput` vivía aquí inline. Se movió a
 * `./resolveParcelaInput.ts` (con tests) cuando se agregó el filtro por
 * ejido + el discriminated union `ResolveResult` para cubrir colisiones
 * reales del RAN (ver docs/handoff-captura-ejido-filter-2026-05-08.md).
 */

/**
 * Key de localStorage para persistir el último ejido seleccionado en la tab
 * "Número o ID". Se limpia en SIGNED_OUT (no se cruza entre técnicos en la
 * misma máquina). NO se incluye en el draft del wizard porque es preferencia
 * personal del técnico, no parte del expediente de una parcela.
 */
const EJIDO_FILTER_LS_KEY = 'aosenuma:captura:ejido-filter';

export function CapturaPage() {
  const { parcela_id: paramId } = useParams<{ parcela_id?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [parcelaId, setParcelaId] = useState<string | undefined>(paramId);

  useEffect(() => {
    setParcelaId(paramId);
  }, [paramId]);

  const { data: parcelasApi, loading: parcelasLoading, error: parcelasErr, refetch: refetchParcelas } =
    useParcelasTecnicas();
  const parcelasState: ParcelaLiveState[] | undefined = useMemo(() => {
    if (!parcelasApi.length) return undefined;
    return parcelasApi.map((p) => ({
      parcela_id: p.parcela_id,
      estado: p.estado,
      firmado: p.firmado,
      irregular: p.irregular,
    }));
  }, [parcelasApi]);

  const [areaMapaCaptura, setAreaMapaCaptura] = useState<AreaMapaCaptura>('all');
  const [aoiSanPedroCaptura, setAoiSanPedroCaptura] = useState<{ features: Array<{ geometry?: unknown }> } | null>(null);
  const [parcelaIdsSanPedroCaptura, setParcelaIdsSanPedroCaptura] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetch(`${import.meta.env.BASE_URL}maps/aoi_san_pedro_unificado.geojson`)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json() as Promise<{ features: Array<{ geometry?: unknown }> }>;
      })
      .then((j) => {
        if (!cancelled) setAoiSanPedroCaptura(j);
      })
      .catch(() => {
        if (!cancelled) setAoiSanPedroCaptura(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (areaMapaCaptura !== 'san_pedro') {
      setParcelaIdsSanPedroCaptura(undefined);
      return;
    }
    if (!aoiSanPedroCaptura) return;
    let cancelled = false;
    fetch(`${import.meta.env.BASE_URL}maps/parcelas_unificado.geojson`)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json() as Promise<{ features: ReadonlyArray<{ properties?: unknown; geometry?: unknown }> }>;
      })
      .then((parcelasFc) => {
        if (!cancelled) {
          setParcelaIdsSanPedroCaptura(parcelaIdsDentroDeAoi(parcelasFc, aoiSanPedroCaptura));
        }
      })
      .catch(() => {
        if (!cancelled) setParcelaIdsSanPedroCaptura([]);
      });
    return () => {
      cancelled = true;
    };
  }, [areaMapaCaptura, aoiSanPedroCaptura]);

  const filtrosCapturaMapa = useMemo((): FiltrosVisor => {
    const f: FiltrosVisor = {};
    if (areaMapaCaptura === 'san_pedro' && parcelaIdsSanPedroCaptura !== undefined) {
      f.parcela_id_in = parcelaIdsSanPedroCaptura;
    } else if (areaMapaCaptura === 'HUIMILPAN' || areaMapaCaptura === 'CORREGIDORA') {
      // Filtro por columna DB parcelas.municipio (mismo backend que dashboard).
      f.municipio = [areaMapaCaptura];
    }
    return f;
  }, [areaMapaCaptura, parcelaIdsSanPedroCaptura]);

  const parcelaMeta = useMemo(
    () => (parcelasApi.length ? parcelasApi.find((r) => r.parcela_id === parcelaId) : undefined),
    [parcelasApi, parcelaId],
  );
  const { user } = useUser();
  const elaboradoPorLabel = useMemo(() => {
    const meta = user?.user_metadata as { full_name?: string } | undefined;
    const name = typeof meta?.full_name === 'string' ? meta.full_name.trim() : '';
    const email = user?.email?.trim() ?? '';
    if (name) return `${name}${email ? ` · ${email}` : ''}`;
    return email || 'Usuario equipo aosenuma';
  }, [user]);
  const [step, setStep]           = useState<WizardStep>(1);

  /**
   * Deep-link desde Expedientes: cuando un técnico clickea una fila
   * «Pendiente» del checklist documental, navegamos aquí con
   * `?step=3&doc=<docId>`. Saltamos directo a paso 3, hacemos scroll a la
   * fila y disparamos highlight transitorio. Limpiamos los query params tras
   * aplicarlos para que un refresh no vuelva a saltar — el draft ya
   * persiste el step real.
   */
  const [focusDocId, setFocusDocId] = useState<string | null>(null);
  useEffect(() => {
    const sp = searchParams.get('step');
    const dq = searchParams.get('doc');
    if (!sp && !dq) return;
    if (sp === '3' || dq) setStep(3);
    if (dq) setFocusDocId(dq);
    // Limpia los params (no preserva otros que pudieran existir).
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando el paso 3 termina de montar y hay focusDocId pendiente, scroll a
  // la fila y aplica clase de highlight (la quita el listener `animationend`
  // en el JSX). rAF doble = espera al primer paint del paso, evita que el
  // scroll caiga en posición pre-layout.
  useEffect(() => {
    if (step !== 3 || !focusDocId) return;
    const id = focusDocId;
    let raf1 = 0, raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        const el = document.getElementById(`doc-row-${id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('is-doc-focus-flash');
          window.setTimeout(() => el.classList.remove('is-doc-focus-flash'), 1800);
        }
        setFocusDocId(null);
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [step, focusDocId]);

  // Estado de submit: spinner + disabled previenen double-submit y dan feedback.
  const [submitting, setSubmitting] = useState(false);
  /**
   * Modal de éxito post-submit. Ofrece "Cargar otro" (reset wizard) y
   * "Ver expediente de esta parcela". null = oculto.
   */
  const [successInfo, setSuccessInfo] = useState<{
    parcelaId: string;
    nomNucleo: string;
    numParcela: number | null;
    propietarioNombre: string | null;
    reportePdf: ReporteSeguimientoData;
  } | null>(null);
  // Ref del form para poder reset() desde "Cargar otro" — eso
  // limpia los inputs no controlados (FormData) sin tocar todos los states.
  const formRef = useRef<HTMLFormElement>(null);
  /**
   * Timestamp del último auto-save del draft. Mostramos un pill discreto
   * "Borrador guardado" en el header del wizard para que el técnico sepa
   * que su trabajo está a salvo aunque cierre la pestaña — el draft vive
   * en localStorage y se recupera en el próximo mount. Solo se borra al
   * submit OK explícito o al "Cargar otro" del modal éxito.
   */
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  /**
   * Errores de validación por campo. Key = name del input, value = mensaje
   * legible para el user. Se llenan en onBlur (validación on-the-fly) y en
   * onSubmit (validación de submit). Se limpian al editar el campo.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Validators por campo, derivados de mxFormats. Usados en onBlur + onSubmit. */
  const FIELD_VALIDATORS: Record<string, (v: string) => ValidationResult> = useMemo(() => ({
    curp:            validateCURP,
    rfc:             validateRFC,
    clabe:           validateCLABE,
    numero_ine:      validateClaveElector,
    telefono:        validateTelefonoMx,
    email:           validateEmail,
    folio_derechos:  validateFolioDerechos,
    folio_tierras:   validateFolioTierras,
    clave_catastral: validateClaveCatastral,
  }), []);

  /** Validación on-blur. Si el campo está vacío y no es required, queda válido. */
  function handleFieldBlur(e: React.FocusEvent<HTMLInputElement>) {
    const name = e.target.name;
    const value = e.target.value;
    const validator = FIELD_VALIDATORS[name];
    if (!validator) return;
    const res = validator(value);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (res.valid) delete next[name];
      else next[name] = res.reason ?? 'Valor inválido';
      return next;
    });
  }
  /** Limpiar error al empezar a editar (no esperar al blur). */
  function handleFieldChange(e: React.ChangeEvent<HTMLInputElement>) {
    const name = e.target.name;
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  // Paso 1
  // tipoDoc=null → "sin elegir": cards abiertas, ninguna seleccionada. El
  // técnico elige clickeando una card o seleccionando una parcela del mapa
  // (auto-sync). Tras "Cargar otro" vuelve a null. Antes era 'ejido' default
  // (§1.2 handoff v2) — superseded por Q4 del prompt 2026-05-11: elección
  // explícita evita que clicks accidentales en privadas/ejidales pasen sin
  // que el técnico lo note. Reads concretos (docs, payload) defaultean a
  // 'ejido' cuando null por seguridad — pero en la práctica nunca llegan
  // a step 3 con null porque el auto-sync sincroniza desde parcelaId.
  const [tipoDoc, setTipoDoc]                 = useState<TipoDoc | null>(null);
  // Cambio pendiente de tipoDoc cuando hay datos en pasos avanzados
  // (handoff parcelas privadas v2, Fase 1.5). Si != null, modal abierto
  // pidiendo confirmación. Sin datos → cambio directo, sin modal.
  const [pendingTipoDocChange, setPendingTipoDocChange] = useState<TipoDoc | null>(null);
  const [esElPropietario, setEsElPropietario] = useState<'si' | 'no' | ''>('');

  // Selector de parcela — modo y estado de búsqueda manual
  const [selMode, setSelMode] = useState<'mapa' | 'id' | 'manual'>('mapa');
  const [idInput, setIdInput] = useState('');

  // Filtro de ejido para la tab "Número o ID". Resuelve la ambigüedad
  // cuando el técnico escribe solo dígitos y el num_parcela existe en
  // varios ejidos (caso real: 54 en SAN ISIDRO + LOMA DE SAN ISIDRO).
  // Persistido en localStorage porque el técnico típicamente trabaja la
  // misma jornada en el mismo ejido. Se limpia en SIGNED_OUT.
  const [ejidoFilter, setEjidoFilter] = useState<EjidoFilter | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(EJIDO_FILTER_LS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as EjidoFilter | null;
      if (parsed && typeof parsed.nom_nucleo === 'string' && typeof parsed.municipio === 'string') {
        return parsed;
      }
      return null;
    } catch { /* corrupto */ return null; }
  });

  // Candidatos cuando el resolver devuelve `ambiguous`. Lista clickable
  // bajo el input — sin esto el técnico no sabría que hubo colisión real.
  const [ambiguousCandidates, setAmbiguousCandidates] = useState<ParcelaCandidate[]>([]);

  // Metadata del SIG hidratada al hacer click en el mapa (area, poligono,
  // nucleo, municipio) — se muestra como info-pill al inicio del wizard.
  const [sigMeta, setSigMeta] = useState<ParcelaFeatureMeta | null>(null);

  // Edición del polígono de un predio privado: cuando el técnico clickea
  // "Reemplazar polígono" en el chip, mostramos UploadParcelaPrivada en
  // modo replace (PUT al backend). El sigMeta se refresca al guardar para
  // reflejar la nueva area_ha en Términos económicos.
  const [showReplacePrivadaUI, setShowReplacePrivadaUI] = useState(false);
  const [descargandoGeoJSON, setDescargandoGeoJSON] = useState(false);

  // Eliminación TOTAL del predio privado: muestra modal con resumen + input
  // de confirmación (escribir el parcela_id literal). Bloqueado si la
  // gestión está 'contratada'. Llama DELETE /v1/captura/parcela-privada/:id.
  const [showDeletePrivadaUI, setShowDeletePrivadaUI] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [eliminandoPrivada, setEliminandoPrivada] = useState(false);

  // GeoJSON merged: RAN estático + privadas del tenant. Permite que el
  // técnico vea en el mapa la parcela privada recién cargada con border
  // dashed teal (Fase 5 Fix 2). Si undefined, MapaParcelas cae al fetch
  // interno por URL (sin privadas, comportamiento pre-Fase-5).
  // `usoComun`: tierras de uso común enriquecidas (mig 0068) — capa violeta
  // clickeable con semáforo, mismo flujo de captura que parcelas.
  const { data: parcelasGeoJSONMerged, usoComun: usoComunGeoJSONMerged, refetchPrivadas } = useParcelasMergedCaptura();
  const { data: capasInformativas } = useCapas();

  // ¿La unidad seleccionada es un área de uso común (HUM-UC####)? Ajusta
  // labels del paso 1 (titular → comunidad/representante) y el payload
  // (notas.contrato.tipo_predio). El flujo documental sigue siendo 'ejido'.
  const esUsoComun = isUsoComunParcelaId(parcelaId);

  /**
   * Lista de ejidos derivada de `parcelasApi`. Deduplica por (municipio,
   * nom_nucleo), trae el conteo por ejido (señal de tamaño para el técnico)
   * y se ordena por municipio + nombre con locale es-MX. Resultado: 14
   * opciones en el dataset Elawan.
   */
  const ejidos = useMemo(() => {
    const map = new Map<string, { nom_nucleo: string; municipio: string; count: number }>();
    for (const p of parcelasApi) {
      if (!p.nom_nucleo) continue;
      const key = `${p.municipio}__${p.nom_nucleo}`;
      const cur = map.get(key);
      if (cur) cur.count++;
      else map.set(key, { nom_nucleo: p.nom_nucleo, municipio: p.municipio, count: 1 });
    }
    return [...map.values()].sort(
      (a, b) =>
        a.municipio.localeCompare(b.municipio) ||
        a.nom_nucleo.localeCompare(b.nom_nucleo, 'es-MX'),
    );
  }, [parcelasApi]);

  // Persistir el filtro de ejido en localStorage en cada cambio. Hidratación
  // sincrónica vive en el `useState(() => ...)` del state arriba.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (ejidoFilter) {
        window.localStorage.setItem(EJIDO_FILTER_LS_KEY, JSON.stringify(ejidoFilter));
      } else {
        window.localStorage.removeItem(EJIDO_FILTER_LS_KEY);
      }
    } catch { /* QuotaExceeded o storage corrupto */ }
  }, [ejidoFilter]);

  // Limpiar el filtro en SIGNED_OUT — el siguiente técnico que use la misma
  // máquina/perfil no debe heredar el ejido del anterior. La key se borra
  // del localStorage Y del state (para reflejarse inmediato en UI).
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const sb = getSupabase();
    const { data } = sb.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        try { window.localStorage.removeItem(EJIDO_FILTER_LS_KEY); } catch { /* ignore */ }
        setEjidoFilter(null);
        setAmbiguousCandidates([]);
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  /**
   * Handler compartido entre Enter en el input y click en "Confirmar".
   * Invoca `resolveParcelaInput` con el filtro de ejido actual y reacciona
   * a las 3 ramas del `ResolveResult`:
   *   - `ok`           → set parcelaId + reset input + cerrar lista de candidatos.
   *   - `ambiguous`    → mostrar lista clickable bajo el input (sin toast).
   *   - `not_found`    → toast con razón específica (incluye nombre del ejido
   *                       cuando aplica), limpiar lista de candidatos.
   * Sin esto la lógica viviría duplicada entre `onKeyDown=Enter` y `onClick`.
   */
  const handleConfirmIdInput = useCallback(() => {
    const trimmed = idInput.trim();
    if (!trimmed) return;
    const result = resolveParcelaInput(trimmed, parcelasApi, ejidoFilter);
    if (result.kind === 'ok') {
      // Mismo guard que handleParcelaClick: el tab "ID" también puede
      // intentar seleccionar una parcela del tipo "incorrecto" cuando
      // tipoDoc está activo.
      const guard = evaluateParcelaClickGuard({
        currentTipoDoc: tipoDoc,
        clickedParcelaId: result.parcela_id,
      });
      if (guard.kind === 'reject') {
        showToast(guard.reason, { variant: 'error' });
        setAmbiguousCandidates([]);
        return;
      }
      setParcelaId(result.parcela_id);
      setIdInput('');
      setAmbiguousCandidates([]);
    } else if (result.kind === 'ambiguous') {
      setAmbiguousCandidates(result.candidates);
    } else {
      setAmbiguousCandidates([]);
      showToast(
        result.reason || 'No se encontró parcela con ese número/ID',
        { variant: 'error' },
      );
    }
  }, [idInput, parcelasApi, ejidoFilter, tipoDoc]);

  // Paso 2
  const [estado, setEstado]               = useState<EstadoGestion>('sin_prospectar');
  const [irregularidadDetalle, setIrregularidadDetalle] = useState('');
  const [gravedad, setGravedad]           = useState('');

  // Paso 2 · Términos económicos (modelo_negocio)
  // Hectáreas preliminares: si la parcela trae area_ha, se usa ese valor (read-only);
  // si no, el técnico puede capturar manualmente este monto.
  const [superficieManualHa, setSuperficieManualHa] = useState<number | null>(null);
  const [bonoFirmaMxn, setBonoFirmaMxn]             = useState<number | null>(null);
  const [plazoConstruccionAnios, setPlazoConstruccionAnios] =
    useState<number>(CONSTRUCCION_PLAZO_DEFAULT_ANIOS);
  const [tarifaConstruccionMxn, setTarifaConstruccionMxn]   =
    useState<number>(CONSTRUCCION_TARIFA_DEFAULT_MXN_POR_UNIDAD);
  const [cantidadUnidades, setCantidadUnidades]     = useState<number | null>(null);
  /** Mensualidades para diferir el subtotal de construcción.
   *  null o 0 = al contado. Sustituye al array de tramos arbitrarios:
   *  un solo control concreto, calcula `monto_mensual = subtotal / meses`. */
  const [diferirMeses, setDiferirMeses] = useState<number | null>(null);
  /** Toggle de seguimiento mensual al cliente — solo metadata, no afecta cálculos. */
  const [seguimientoMensual, setSeguimientoMensual] = useState<boolean>(false);
  /** Evita pisar la superficie aplicable si el técnico ya la editó en esta parcela. */
  const [cantidadUnidadesDirty, setCantidadUnidadesDirty] = useState(false);
  const lastParcelaIdForCantidadRef = useRef<string | undefined>(undefined);

  // ─────────────────────────────────────────────────────────────────────────
  // Estado controlado para los inputs del paso 1 y 2 que ANTES eran no
  // controlados (defaultValue + lectura por FormData en submit). Causa raíz:
  // React desmonta el árbol del paso al cambiar de step (`{step === N && ...}`),
  // así que al volver de paso 2/3 a paso 1 los nodos perdían su valor;
  // y `new FormData(form)` desde el paso 3 NO veía los inputs de paso 1/2 que
  // ya no estaban en el DOM. Resultado: payload con propietario/etc. todo en
  // null. Ahora los valores viven en este `formData` plano (key→string),
  // sobreviven al unmount y se persisten en el draft localStorage.
  // Inicial: defaults que antes vivían en `defaultValue` del JSX.
  const [formData, setFormData] = useState<Partial<Record<string, string>>>(() => ({
    tipo_propietario: 'ejidatario', // sobreescrito por effect cuando tipoDoc cambia a 'privado'
  }));
  // Cuando cambia tipoDoc (ejido ↔ privado), las options del <select> de
  // tipo_propietario son distintas. Si el user había elegido p.ej.
  // "posesionario" en ejido y cambia a privado, la option ya no existe →
  // forzamos al default del nuevo tipoDoc para evitar valor huérfano.
  // Con tipoDoc=null no toca formData (no hay tipo activo todavía).
  useEffect(() => {
    if (tipoDoc === null) return;
    setFormData((prev) => ({
      ...prev,
      // Uso común (mig 0068): el titular típico es el órgano de
      // representación del núcleo — default 'comisariado'. El técnico
      // puede cambiarlo a 'comunero' en el select.
      tipo_propietario: tipoDoc === 'privado' ? 'pp' : esUsoComun ? 'comisariado' : 'ejidatario',
    }));
  }, [tipoDoc, esUsoComun]);

  /** Setter granular para un input/select/textarea controlado. Devuelve
   *  `{value, onChange}` listo para spread en el JSX. Ejemplo:
   *    `<input {...bindInput('telefono')} placeholder="..." />`. */
  function bindInput(key: string) {
    return {
      value: formData[key] ?? '',
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const v = e.target.value;
        setFormData((prev) => ({ ...prev, [key]: v }));
      },
    };
  }
  /** Variante para campos del propietario con validación blur (telefono,
   *  email, curp, rfc, ine_numero, clabe). Combina escritura al state con
   *  los handlers `handleFieldChange` (limpia error) y `handleFieldBlur`
   *  (valida). Usar este en lugar de `bindInput` cuando ya había
   *  onBlur/onChange de validación. */
  function bindFieldValidated(key: string) {
    return {
      value: formData[key] ?? '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [key]: e.target.value }));
        handleFieldChange(e);
      },
      onBlur: handleFieldBlur,
    };
  }
  /** Lee un campo del state como string ya trimado o null si vacío. */
  function pickField(key: string): string | null {
    const v = formData[key];
    if (typeof v !== 'string') return null;
    const t = v.trim();
    return t === '' ? null : t;
  }

  // Historial de visitas para la parcela seleccionada
  type VisitaHistorial = {
    id: string;
    fecha_visita: string;
    notas: string | null;
    narrativa: VisitaNarrativa | null;
    /** GPS capturado en campo (geom_punto, PostGIS Point 4326). null si la visita no registró GPS. */
    lat: number | null;
    lng: number | null;
  };
  const [visitas, setVisitas]               = useState<VisitaHistorial[]>([]);
  const [visitasLoading, setVisitasLoading] = useState(false);
  const [visitaExpandedId, setVisitaExpandedId] = useState<string | null>(null);

  // Match de búsqueda por CURP — banner que aparece en step 1 cuando el
  // técnico tipea un CURP que ya existe como propietario en otra parcela.
  // Permite cargar datos del titular existente sin re-tipear PII.
  type PropMatch = {
    id: string;
    titular_masked: string | null;
    parcelas_count: number;
    parcelas: string[];
  };
  const [propietarioMatch, setPropietarioMatch] = useState<PropMatch | null>(null);
  const [propietarioMatchDismissed, setPropietarioMatchDismissed] = useState(false);
  const [propietarioMatchLoading, setPropietarioMatchLoading] = useState(false);

  // Paso 3
  const [done, setDone]       = useState<Record<string, boolean>>({});
  const [docFiles, setDocFiles] = useState<Record<string, File>>({});
  /**
   * Docs ya subidos al expediente en el SERVIDOR (R2 + tabla expedientes).
   * Indexado por catalog docId. Si existingDocs[docId] está, el checklist
   * se marca como done y muestra el filename del archivo persistido más
   * reciente. El user puede:
   *   - dejarlo (no toca nada, se preserva en DB)
   *   - subir un archivo nuevo (docFiles[docId] toma precedencia y reemplaza
   *     en el siguiente submit; el viejo queda en R2 como historial — la
   *     tabla expedientes es append-only por mig 0036_drop_audit_trigger)
   *   - descargar el actual (botón abre signedUrl del BFF)
   *
   * Hidrata desde useExpedientesList filtrado por parcelaId actual.
   * Reset al cambiar de parcela (clearFormFields).
   */
  type ExistingDoc = { id: string; r2_key: string; filename: string };
  const [existingDocs, setExistingDocs] = useState<Record<string, ExistingDoc>>({});
  const [drag, setDrag]       = useState(false);
  const [queued, setQueued]   = useState<File[]>([]);
  /** Cola de archivos con `tipo_doc` contrato_firmado (mismo que «Ver contrato» en el dashboard cliente). */
  const [contratoFirmadoDrag, setContratoFirmadoDrag]     = useState(false);
  const [contratoFirmadoQueued, setContratoFirmadoQueued] = useState<File[]>([]);
  /**
   * Contratos firmados YA persistidos en R2 para la parcela actual.
   * Lista (no mapa): a diferencia del checklist, aquí pueden coexistir
   * múltiples versiones (re-uploads) y queremos mostrarlas TODAS.
   *
   * Bug Diego 2026-05-11: el dropzone "Contrato firmado" no listaba lo
   * ya subido → el técnico abría el form y, al no ver nada, asumía que
   * el contrato no existía y volvía a subirlo, duplicando el archivo en
   * R2. Fix: hidratar esta lista desde allExpedientes y renderizarla
   * debajo del dropzone con botón "dar de baja" (mismo flow que el
   * checklist usa para sus docs requeridos).
   */
  const [existingContratos, setExistingContratos] = useState<ExistingDoc[]>([]);
  /**
   * Archivos sueltos (tipo_doc='otro') YA persistidos en R2 para la
   * parcela actual. Misma motivación que `existingContratos`: el dropzone
   * "Subir archivos al expediente" tampoco listaba los archivos previos,
   * provocando que el técnico re-subiera el mismo .kml/.png/.pdf
   * duplicándolo. Bug Diego 2026-05-11 (segunda parte del mismo issue).
   */
  const [existingOtros, setExistingOtros] = useState<ExistingDoc[]>([]);

  // ─────────────────────────────────────────────────────────────────────────
  // Draft persistence: el técnico puede llenar la mitad del wizard, cerrar
  // pestaña y volver mañana. Persistimos solo los campos JSON-serializables;
  // los Files (docFiles, queued) NO se pueden serializar y el técnico
  // tendrá que re-seleccionarlos. Se limpia tras submit OK.
  // ─────────────────────────────────────────────────────────────────────────
  const DRAFT_KEY = 'aosenuma-draft:captura-form';
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw) as {
        parcelaId?: string; step?: WizardStep; tipoDoc?: TipoDoc | null;
        esElPropietario?: 'si' | 'no' | ''; selMode?: 'mapa' | 'id' | 'manual';
        idInput?: string; estado?: EstadoGestion;
        irregularidadDetalle?: string;
        /** Legacy: clave del catálogo antes del campo abierto. */
        irregularidad?: string;
        gravedad?: string;
        done?: Record<string, boolean>;
        superficieManualHa?: number | null;
        bonoFirmaMxn?: number | null;
        plazoConstruccionAnios?: number;
        tarifaConstruccionMxn?: number;
        cantidadUnidades?: number | null;
        cantidadUnidadesDirty?: boolean;
        diferirMeses?: number | null;
        seguimientoMensual?: boolean;
        /** Legacy (pre-may 2026): array de tramos abiertos, ahora ignorado.
         *  Se conserva en el tipo solo para parsear drafts viejos sin error. */
        pagosDiferidos?: unknown;
        /** Inputs controlados de paso 1 y 2 (key → valor). */
        formData?: Record<string, string>;
      };
      if (d.parcelaId && !paramId) setParcelaId(d.parcelaId);
      // Deep-link `?step=` o `?doc=` (de Expedientes → fila «Pendiente»)
      // tiene prioridad sobre el step del draft: el técnico viene aquí a
      // hacer una acción concreta, restaurar paso 1 lo confundiría.
      // `searchParams` aquí es el snapshot inicial del mount — para entonces
      // el otro effect ya leyó esos params, pero la URL podría seguir
      // teniéndolos si el setSearchParams({}) aún no aplicó. Por eso
      // consultamos `window.location.search` directamente, que ya refleja
      // el momento del mount sin depender del orden de effects.
      const initSearch = typeof window !== 'undefined' ? window.location.search : '';
      const hasDeepLink = /[?&](?:step|doc)=/.test(initSearch);
      if (d.step && !hasDeepLink) setStep(d.step);
      if (d.tipoDoc) setTipoDoc(d.tipoDoc);
      if (d.esElPropietario != null) setEsElPropietario(d.esElPropietario);
      if (d.selMode) setSelMode(d.selMode);
      if (d.idInput != null) setIdInput(d.idInput);
      if (d.estado) setEstado(d.estado);
      if (typeof d.irregularidadDetalle === 'string') {
        setIrregularidadDetalle(d.irregularidadDetalle);
      } else if (typeof d.irregularidad === 'string' && d.irregularidad) {
        const k = d.irregularidad as SubtipoIrregularidad;
        setIrregularidadDetalle(
          k in IRREGULARIDAD_LABEL ? IRREGULARIDAD_LABEL[k] : d.irregularidad,
        );
      }
      if (d.gravedad != null) setGravedad(d.gravedad);
      if (d.done) setDone(d.done);
      if (d.superficieManualHa !== undefined) setSuperficieManualHa(d.superficieManualHa);
      if (d.bonoFirmaMxn !== undefined) setBonoFirmaMxn(d.bonoFirmaMxn);
      if (typeof d.plazoConstruccionAnios === 'number' && Number.isFinite(d.plazoConstruccionAnios)) {
        setPlazoConstruccionAnios(d.plazoConstruccionAnios);
      }
      if (typeof d.tarifaConstruccionMxn === 'number' && Number.isFinite(d.tarifaConstruccionMxn)) {
        setTarifaConstruccionMxn(d.tarifaConstruccionMxn);
      }
      if (d.cantidadUnidades !== undefined) setCantidadUnidades(d.cantidadUnidades);
      if (typeof d.cantidadUnidadesDirty === 'boolean') setCantidadUnidadesDirty(d.cantidadUnidadesDirty);
      else if (d.cantidadUnidades !== undefined) setCantidadUnidadesDirty(true);
      if (typeof d.diferirMeses === 'number' && Number.isFinite(d.diferirMeses) && d.diferirMeses > 0) {
        setDiferirMeses(Math.round(d.diferirMeses));
      } else if (d.diferirMeses === null) {
        setDiferirMeses(null);
      }
      if (typeof d.seguimientoMensual === 'boolean') setSeguimientoMensual(d.seguimientoMensual);
      // d.pagosDiferidos (legacy array de tramos) se ignora a propósito.
      if (d.formData && typeof d.formData === 'object') {
        // Merge sobre defaults (tipo_propietario / modalidad_pago) para no
        // perderlos si el draft es viejo y no los traía.
        setFormData((prev) => ({ ...prev, ...d.formData }));
      }
      if (d.parcelaId || d.idInput) {
        showToast('Restauramos tu borrador anterior.', 3500);
      }
    } catch { /* draft corrupto */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({
            parcelaId, step, tipoDoc, esElPropietario, selMode, idInput,
            estado, irregularidadDetalle, gravedad, done,
            superficieManualHa, bonoFirmaMxn, plazoConstruccionAnios,
            tarifaConstruccionMxn, cantidadUnidades, cantidadUnidadesDirty,
            diferirMeses, seguimientoMensual,
            formData,
          }),
        );
        setDraftSavedAt(Date.now());
      } catch { /* QuotaExceeded */ }
    }, 400);
    return () => window.clearTimeout(t);
  }, [parcelaId, step, tipoDoc, esElPropietario, selMode, idInput,
      estado, irregularidadDetalle, gravedad, done,
      superficieManualHa, bonoFirmaMxn, plazoConstruccionAnios,
      tarifaConstruccionMxn, cantidadUnidades, cantidadUnidadesDirty,
      diferirMeses, seguimientoMensual,
      formData]);
  function clearDraft() {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setDraftSavedAt(null);
  }

  // Semáforo paso 2 desde el inventario. Va ANTES del efecto de hidratación
  // en el código para que, al aplicarse captura del servidor, `applyHydration`
  // deje `estado` alineado con `gestiones.estado` (última palabra en ese ciclo).
  useEffect(() => {
    if (!parcelaMeta) return;
    setEstado(parcelaMeta.estado);
  }, [parcelaMeta]);

  // ─────────────────────────────────────────────────────────────────────────
  // Hidratación desde el servidor cuando el técnico abre una parcela con
  // captura previa. Estrategia en dos capas (mayo 2026):
  //
  //   CAPA 1 (no-PII, sin MFA): `useCapturaHydrate` → consulta directa a
  //   Supabase con RLS. Hidrata tipo de tenencia, folios, clave catastral,
  //   estado de gestión, montos, riesgo, notas (JSON parseado), irregularidad
  //   principal. Se aplica automáticamente si el form está "limpio".
  //
  //   CAPA 2 (PII descifrada, requiere MFA reciente): `useCapturaHydrateUnmasked`
  //   → BFF GET /v1/captura/:parcela_id que descifra titular/teléfono/email/
  //   INE/CURP/RFC/dirección/CLABE con la llave AES-256-GCM en el Worker.
  //   Solo se dispara cuando `enableUnmasked === true`, lo cual ocurre:
  //     • automáticamente al abrir una parcela ya capturada en estado
  //       limpio Y con MFA reciente (`isAal2 && session.mfaTimestamp`
  //       dentro del TTL);
  //     • o cuando el user confirma el modal MFA tras un 403 mfa_required.
  //
  // Política "no pisar trabajo en curso":
  //   • Se aplica al ABRIR la parcela y solo si `formData` está en sus
  //     defaults (sólo `tipo_propietario` + `modalidad_pago`). Cualquier
  //     edición humana posterior bloquea la próxima hidratación de esa
  //     misma parcela hasta navegar a otra.
  //   • Si llega data del servidor mientras el técnico ya empezó a editar,
  //     mostramos un toast con CTA "Ver y aplicar" (en futura iteración —
  //     hoy: no aplicamos para evitar el destructor).
  // ─────────────────────────────────────────────────────────────────────────
  const { data: hydrateData, loading: hydrateLoading } = useCapturaHydrate(parcelaId);
  const hydrateAppliedRef = useRef<string | null>(null);
  /** Estado del modal MFA: lo abrimos cuando el BFF devuelve 403 mfa_required.
   *  Tras `onSuccess` del modal, refetch del hook unmasked. */
  const [showMfaModal, setShowMfaModal] = useState<boolean>(false);
  /** Marca si el caller pidió explícitamente la hidratación con PII. Se
   *  setea true al abrir una parcela con captura previa (effect abajo) y
   *  sigue true tras refetch post-MFA. */
  const [enableUnmasked, setEnableUnmasked] = useState<boolean>(false);
  const {
    data: unmaskedData,
    loading: unmaskedLoading,
    mfaRequired: unmaskedMfaRequired,
    refetch: refetchUnmasked,
  } = useCapturaHydrateUnmasked(parcelaId, enableUnmasked);
  /** Anti-doble-aplicación de unmasked por parcela. */
  const unmaskedAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!parcelaId || !hydrateData) return;
    if (hydrateLoading) return;
    if (hydrateAppliedRef.current === parcelaId) return;
    if (!hydrateData.parcela_existe) return;
    if (!hydrateData.gestion && !hydrateData.propietario && hydrateData.irregularidades.length === 0) {
      // Parcela existe pero sin captura previa — no hay nada que hidratar.
      hydrateAppliedRef.current = parcelaId;
      return;
    }
    // Política mayo 2026 (post-feedback usuario): el SERVIDOR es fuente de
    // verdad. Si la parcela tiene captura previa, hidratamos SIEMPRE al
    // primer mount, descartando cualquier draft local. El draft local
    // cumple su función entre recargas DENTRO de la misma sesión: tras
    // editar y antes de guardar, no se pierde ante un crash. Pero al
    // RE-ABRIR la parcela (mismo o otro browser/cuenta), los datos
    // persistidos en DB ganan — eso es lo que el técnico/cliente espera.
    //
    // Si había draft (formData con datos previos), avisamos al técnico
    // explícitamente que se descartó a favor de los datos del servidor.
    // Su trabajo nuevo se va a un buffer mental: si quería esos cambios,
    // los re-tipea y guarda. Lossy pero predecible.
    const hadDraft = (() => {
      if (gravedad !== '' || irregularidadDetalle !== '') return true;
      if (diferirMeses != null || seguimientoMensual || bonoFirmaMxn != null) return true;
      const formKeys = Object.keys(formData);
      return formKeys.some((k) => {
        // Defaults auto-asignados por tipoDoc/uso común NO cuentan como
        // draft del técnico ('comisariado' es el default de uso común).
        if (k === 'tipo_propietario') {
          return formData[k] !== 'ejidatario' && formData[k] !== 'pp' && formData[k] !== 'comisariado';
        }
        return !!formData[k];
      });
    })();

    applyHydration(hydrateData);
    hydrateAppliedRef.current = parcelaId;

    if (hadDraft) {
      // Draft descartado — el técnico necesita saber por qué cambió el form.
      showToast('Borrador local descartado: cargamos datos del servidor', { variant: 'info' });
    } else if (hydrateData.propietario || hydrateData.gestion) {
      showToast('Datos cargados desde captura previa', { variant: 'success' });
    }
    // Si la parcela tiene propietario, intentamos también hidratar PII.
    // Esto activa el hook unmasked → si MFA reciente, BFF responde 200 y
    // pisamos el `titular_masked` con el cleartext. Si MFA expirado,
    // `mfaRequired=true` y abrimos modal.
    if (hydrateData.propietario) {
      setEnableUnmasked(true);
    }
  }, [parcelaId, hydrateData, hydrateLoading]);

  // Effect: aplicar unmasked cuando llega del BFF.
  useEffect(() => {
    if (!parcelaId) return;
    if (!unmaskedData) return;
    if (unmaskedLoading) return;
    if (unmaskedAppliedRef.current === parcelaId) return;
    applyUnmaskedHydration(unmaskedData);
    unmaskedAppliedRef.current = parcelaId;
    // UX: confirmar al técnico que descifró PII después del MFA. Sin esto
    // el campo "Nombre completo" cambia de "JUAN P." a "JUAN PEREZ" sin
    // explicación visible. (Agent 3 audit, F7).
    if (unmaskedData.propietario?.titular) {
      showToast('Datos del titular descifrados', { variant: 'success' });
    }
  }, [parcelaId, unmaskedData, unmaskedLoading]);

  // Effect: buscar propietario existente por CURP cuando el técnico la
  // termina de tipear (18 chars + formato válido). Si hay match y NO es el
  // mismo propietario que ya está vinculado a la parcela actual, ofrecemos
  // banner con "Cargar datos". Debounce 600ms para evitar spam mientras
  // tipea. Reset al cambiar parcela (ya cubierto por clearFormFields).
  const _curpFromForm = formData.curp ?? '';
  useEffect(() => {
    const curpUpper = _curpFromForm.trim().toUpperCase();
    // Validación de CURP: solo dispara con 18 chars + regex
    if (curpUpper.length !== 18) {
      // CURP incompleta: limpia match si lo había (el user borró)
      if (propietarioMatch) setPropietarioMatch(null);
      return;
    }
    if (!/^[A-ZÑ&]{4}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/.test(curpUpper)) return;

    const handle = window.setTimeout(() => {
      void (async () => {
        setPropietarioMatchLoading(true);
        try {
          const resp = await bffFetch<{ results: PropMatch[] }>(
            `/v1/propietarios/search?curp=${encodeURIComponent(curpUpper)}`,
          );
          if (resp.results && resp.results.length > 0) {
            const match = resp.results[0];
            if (!match) {
              setPropietarioMatch(null);
              return;
            }
            // Si la parcela actual ya está en el listado del match,
            // no es un descubrimiento — el match es el titular actual.
            if (parcelaId && match.parcelas.includes(parcelaId)) {
              setPropietarioMatch(null);
              return;
            }
            setPropietarioMatch(match);
            setPropietarioMatchDismissed(false);
          } else {
            setPropietarioMatch(null);
          }
        } catch (e) {
          // 403 mfa_required no debería pasar (search no requiere AAL2),
          // pero si la red falla, dejamos silencioso — el banner es UX.
          console.warn('[propietario-search] failed:', e);
        } finally {
          setPropietarioMatchLoading(false);
        }
      })();
    }, 600);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_curpFromForm, parcelaId]);

  /**
   * Handler del botón "Cargar datos del titular": descifra PII via BFF
   * (requiere AAL2) y rellena el form. Si el MFA está expirado, abre
   * el modal — tras success se retira el banner y se completa la carga.
   */
  async function loadFromPropietarioMatch(match: PropMatch) {
    try {
      const unmasked = await bffFetch<{
        id: string; titular: string | null; telefono: string | null;
        email: string | null; curp: string | null; rfc: string | null;
        ine_numero: string | null; direccion: string | null; clabe: string | null;
        titular_masked: string | null;
      }>(`/v1/propietarios/${match.id}/unmask`);
      // Aplicar al form (mismo patrón que applyUnmaskedHydration pero por
      // propietario, no por parcela). bindInput es controlado vía formData,
      // así que basta setFormData merge.
      setFormData((prev) => ({
        ...prev,
        propietario: unmasked.titular ?? prev.propietario ?? '',
        telefono:    unmasked.telefono ?? prev.telefono ?? '',
        email:       unmasked.email ?? prev.email ?? '',
        curp:        unmasked.curp ?? prev.curp ?? '',
        rfc:         unmasked.rfc ?? prev.rfc ?? '',
        numero_ine:  unmasked.ine_numero ?? prev.numero_ine ?? '',
        direccion:   unmasked.direccion ?? prev.direccion ?? '',
        clabe:       unmasked.clabe ?? prev.clabe ?? '',
      }));
      setPropietarioMatch(null);
      setPropietarioMatchDismissed(true);
      showToast(`Datos del titular cargados desde otra parcela del expediente.`, { variant: 'success' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (e instanceof BffError && e.status === 403) {
        // MFA requerido: abrir modal y al success reintentar carga.
        setShowMfaModal(true);
        // El user cierra el modal y vuelve a clicar — flujo simple.
        showToast('Verifica tu MFA para cargar datos del titular.', { variant: 'info' });
      } else {
        showToast(`No se pudieron cargar los datos: ${msg}`, { variant: 'error' });
      }
    }
  }

  // Effect: hidratar `existingDocs` y `done[docId]` desde expedientes en R2.
  // Cuando otro técnico (o el mismo en otra sesión) ya subió archivos para
  // esta parcela, el checklist los muestra como completados sin necesidad
  // de re-subirlos. El user puede reemplazar (sube uno nuevo → docFiles
  // toma precedencia), descargar el actual (signed URL del BFF en demand),
  // o dejarlo igual.
  const { data: allExpedientes, refetch: refetchExpedientes } = useExpedientesList();
  useEffect(() => {
    if (!parcelaId) {
      setExistingDocs({});
      setExistingContratos([]);
      setExistingOtros([]);
      return;
    }
    const docsForParcela = allExpedientes.filter((e) => e.parcela_id === parcelaId);
    if (docsForParcela.length === 0) {
      setExistingDocs({});
      setExistingContratos([]);
      setExistingOtros([]);
      return;
    }
    // Si una parcela tiene múltiples archivos para el mismo tipo_doc
    // (re-subidas), el más reciente gana. expedientes ya viene ordenado
    // descendente por created_at desde useExpedientesList.
    const map: Record<string, ExistingDoc> = {};
    const contratos: ExistingDoc[] = [];
    const otros: ExistingDoc[] = [];
    for (const e of docsForParcela) {
      // Contratos firmados se manejan en lista separada (pueden ser múltiples).
      if (e.tipo_doc === 'contrato_firmado') {
        const slash = e.r2_key.lastIndexOf('/');
        contratos.push({
          id: e.id,
          r2_key: e.r2_key,
          filename: slash >= 0 ? e.r2_key.slice(slash + 1) : e.r2_key,
        });
        continue;
      }
      // Archivos sueltos (dropzone genérico) — lista separada similar a
      // contratos. Sin esto el técnico no veía sus uploads previos y los
      // duplicaba.
      if (e.tipo_doc === 'otro') {
        const slash = e.r2_key.lastIndexOf('/');
        otros.push({
          id: e.id,
          r2_key: e.r2_key,
          filename: slash >= 0 ? e.r2_key.slice(slash + 1) : e.r2_key,
        });
        continue;
      }
      const docId = mapSchemaTipoDocToCatalogDocId(e.tipo_doc);
      if (!docId || docId === 'otro') continue;
      if (map[docId]) continue; // primer hit ya gana (más reciente)
      const slash = e.r2_key.lastIndexOf('/');
      map[docId] = {
        id: e.id,
        r2_key: e.r2_key,
        filename: slash >= 0 ? e.r2_key.slice(slash + 1) : e.r2_key,
      };
    }
    setExistingDocs(map);
    setExistingContratos(contratos);
    setExistingOtros(otros);
    // Marca como done los checks que tienen archivo persistido server-side
    // (preserva los que ya están en docFiles — el user los acaba de adjuntar
    // en esta sesión y todavía no envió).
    setDone((prev) => {
      const next = { ...prev };
      for (const docId of Object.keys(map)) {
        next[docId] = true;
      }
      return next;
    });
  }, [parcelaId, allExpedientes]);

  // Effect: si el BFF indicó mfa_required, abrir modal.
  useEffect(() => {
    if (unmaskedMfaRequired) setShowMfaModal(true);
  }, [unmaskedMfaRequired]);

  /**
   * Effect defensivo: activar `enableUnmasked` cuando hay una parcela
   * seleccionada CON titular en el inventario, aunque la hidratación de
   * captura previa (useCapturaHydrate) no haya disparado el activador
   * principal (línea 833).
   *
   * Bug Diego 2026-05-11: el header del wizard seguía mostrando "Parcela
   * N · J. T. V." (iniciales) en parcelas con badge "Contratada". El
   * activador original solo corre cuando hydrateData.propietario está
   * populated Y el useEffect supera sus 5 guard clauses (parcela_existe,
   * applyRef, captura no vacía, etc.). Bajo React Strict Mode, HMR, o
   * cuando el user navega directo a la parcela sin pasar por bandeja,
   * alguna guarda falla y enableUnmasked queda en false → unmask NUNCA
   * se solicita → header masked permanente aunque haya MFA fresco.
   *
   * Este effect paralelo se dispara con `parcelaMeta?.titular_masked`:
   * si hay titular en el inventario, hay propietario, hay PII que
   * descifrar. Idempotente (activar a true es no-op si ya está).
   */
  useEffect(() => {
    if (!parcelaId) return;
    if (enableUnmasked) return;
    const tieneTitular =
      (parcelaMeta?.titular_masked && parcelaMeta.titular_masked.trim() !== '')
      || hydrateData?.propietario != null;
    if (tieneTitular) {
      setEnableUnmasked(true);
    }
  }, [parcelaId, parcelaMeta?.titular_masked, hydrateData?.propietario, enableUnmasked]);

  /** Mapea el shape de `useCapturaHydrate` a los states del wizard.
   *  No-PII: tipo, folios, titular_masked (read-only), gestion (estado/
   *  riesgo/plazo/notas), irregularidad principal. */
  function applyHydration(data: CapturaHydrateData): void {
    const patches: Partial<Record<string, string>> = {};
    let touched = false;
    if (data.propietario) {
      const p = data.propietario;
      // Usamos el titular_masked como prefill del input (será visible al
      // técnico; si quiere actualizarlo, edita y se re-cifra al guardar).
      if (p.titular_masked) patches.propietario = p.titular_masked;
      if (p.tipo) patches.tipo_propietario = p.tipo === 'propietario' ? 'pp' : p.tipo;
      if (p.folio_derechos) patches.folio_derechos = p.folio_derechos;
      if (p.folio_tierras) patches.folio_tierras = p.folio_tierras;
      if (p.clave_catastral) patches.clave_catastral = p.clave_catastral;
      touched = true;
    }
    if (data.gestion) {
      const g = data.gestion;
      if (g.estado === 'sin_prospectar' || g.estado === 'en_valoracion'
          || g.estado === 'en_negociacion' || g.estado === 'contratada'
          || g.estado === 'inviable') {
        setEstado(g.estado as EstadoGestion);
      }
      if (typeof g.plazo_anos === 'number' && Number.isFinite(g.plazo_anos)) {
        setPlazoConstruccionAnios(g.plazo_anos);
      }
      // Sub-bloques desde notas JSON
      const c = g.notas.contrato;
      if (c) {
        // modalidad_pago retirado de la UI 2026-05-09; se ignora si viene en
        // notas legacy. fecha_firma_est es el único compromiso temporal que
        // sigue capturándose.
        if (c.fecha_firma_est) patches.fecha_firma_est = c.fecha_firma_est;
        if (c.situacion_legal) patches.situacion_legal = c.situacion_legal;
        if (c.condiciones) patches.condiciones = c.condiciones;
        if (c.colindancias.norte)    patches.col_norte    = c.colindancias.norte;
        if (c.colindancias.sur)      patches.col_sur      = c.colindancias.sur;
        if (c.colindancias.oriente)  patches.col_oriente  = c.colindancias.oriente;
        if (c.colindancias.poniente) patches.col_poniente = c.colindancias.poniente;
      }
      const cmp = g.notas.campo;
      if (cmp) {
        if (cmp.es_el_propietario) setEsElPropietario(cmp.es_el_propietario);
        if (cmp.signatario_nombre) patches.signatario_nombre = cmp.signatario_nombre;
        if (cmp.tercero_relacion)  patches.tercero_relacion = cmp.tercero_relacion;
        if (cmp.calidad_posesionario) patches.calidad_posesionario = cmp.calidad_posesionario;
        if (cmp.relacion_ejido)    patches.relacion_ejido = cmp.relacion_ejido;
        if (cmp.liderazgo_ejido)   patches.liderazgo_ejido = cmp.liderazgo_ejido;
        if (cmp.acta_nacimiento_folio) patches.acta_nacimiento_folio = cmp.acta_nacimiento_folio;
      }
      const gn = g.notas.gestion;
      if (gn) {
        if (gn.postura_propietario) patches.postura_propietario = gn.postura_propietario;
        if (gn.motivo_inviable)     patches.motivo_inviable = gn.motivo_inviable;
      }
      const narr = g.notas.narrativa;
      if (narr) {
        if (narr.descripcion_visita)       patches.descripcion_visita = narr.descripcion_visita;
        if (narr.descripcion_negociacion)  patches.descripcion_negociacion = narr.descripcion_negociacion;
        if (narr.reqs_area_legal)          patches.reqs_area_legal = narr.reqs_area_legal;
        if (narr.observaciones_tecnico)    patches.observaciones_tecnico = narr.observaciones_tecnico;
      }
      if (g.notas.bono_firma_mxn != null) setBonoFirmaMxn(g.notas.bono_firma_mxn);
      if (g.notas.construccion?.cantidad_unidades != null) {
        setCantidadUnidades(g.notas.construccion.cantidad_unidades);
        setCantidadUnidadesDirty(true);
      }
      if (g.notas.construccion?.tarifa_mxn_unidad != null) {
        setTarifaConstruccionMxn(g.notas.construccion.tarifa_mxn_unidad);
      }
      if (g.riesgo) patches.riesgo = g.riesgo;
      touched = true;
    }
    // Primera irregularidad activa (UI todavía no soporta multi-row).
    const irr = data.irregularidades[0];
    if (irr) {
      setIrregularidadDetalle(irr.descripcion ?? IRREGULARIDAD_LABEL[irr.subtipo as SubtipoIrregularidad] ?? irr.subtipo);
      setGravedad(irr.gravedad);
      touched = true;
    }
    if (Object.keys(patches).length > 0) {
      setFormData((prev) => ({ ...prev, ...patches }));
    }
    if (touched) {
      showToast('Captura previa cargada · todos los campos siguen editables.', 4000);
    }
  }

  /**
   * Aplica la hidratación CON PII descifrada (resultado del BFF
   * GET /v1/captura/:parcela_id). Pisa el `titular_masked` del prefill no-PII
   * con el cleartext canónico UPPERCASE (ej. "MARIA LOPEZ PEREZ" en vez de
   * "MARIA L. P.") y rellena teléfono, email, INE, CURP, RFC, dirección, CLABE.
   *
   * Política: solo se llama tras `applyHydration` (Capa 1) y solo si el
   * técnico aún no editó esos campos. Para detectar edición humana en los
   * campos PII, comparamos contra el `titular_masked` que `applyHydration`
   * dejó como prefill — si el input "propietario" sigue siendo ese masked,
   * podemos pisarlo con el cleartext sin perder trabajo.
   */
  function applyUnmaskedHydration(data: CapturaHydrateUnmaskedData): void {
    const p = data.propietario;
    if (!p) return;
    const patches: Partial<Record<string, string>> = {};
    // Pisar el titular si el input está vacío o si solo tiene el masked
    // que el primer applyHydration dejó (ej. "MARIA L. P."). Si el técnico
    // ya escribió algo distinto, NO pisamos.
    const currentTitular = (formData.propietario ?? '').trim();
    const wasMasked = currentTitular === '' || currentTitular === (p.titular_masked ?? '');
    if (p.titular && wasMasked) {
      patches.propietario = p.titular;
    } else if (p.titular == null && wasMasked && currentTitular !== '') {
      // Caso post-borrado WYSIWYG (ver captura.ts: pisar titularMasked con null):
      // el server reporta titular descifrado=null Y el form tiene el masked
      // stale que applyHydration puso. Limpiamos para que el técnico vea el
      // estado real de DB (vacío). Sin esto, el field "Nombre completo"
      // muestra "TOMÁS S." aunque el titular esté borrado en propietarios.
      patches.propietario = '';
    }
    // Para el resto, política simple: pisar solo si el campo está vacío.
    // Evita destruir cualquier corrección manual (ej. técnico cambió el
    // teléfono a uno actualizado y todavía no guardó).
    if (p.telefono && !(formData.telefono ?? '').trim()) patches.telefono = p.telefono;
    if (p.email    && !(formData.email    ?? '').trim()) patches.email    = p.email;
    if (p.curp     && !(formData.curp     ?? '').trim()) patches.curp     = p.curp;
    if (p.rfc      && !(formData.rfc      ?? '').trim()) patches.rfc      = p.rfc;
    if (p.ine_numero && !(formData.numero_ine ?? '').trim()) patches.numero_ine = p.ine_numero;
    if (p.direccion && !(formData.direccion ?? '').trim()) patches.direccion = p.direccion;
    if (p.clabe    && !(formData.clabe    ?? '').trim()) patches.clabe    = p.clabe;
    if (Object.keys(patches).length === 0) return;
    setFormData((prev) => ({ ...prev, ...patches }));
    showToast('Datos del propietario cargados desde el servidor.', 3500);
  }

  /** Handler del modal MFA — el user verificó TOTP, refetcheamos el endpoint. */
  function onMfaSuccess(): void {
    setShowMfaModal(false);
    // Sigue habilitado para que `refetchUnmasked` corra. Si no estaba
    // habilitado, lo habilitamos aquí (caso raro: parcela sin previa
    // captura no debería pedir MFA).
    setEnableUnmasked(true);
    void refetchUnmasked();
  }

  /** Cancelar el modal MFA — no hay datos del servidor; dejamos al técnico
   *  con los datos no-PII y un toast que le explica que puede capturar
   *  manualmente o re-intentar el unlock más tarde. */
  function onMfaCancel(): void {
    setShowMfaModal(false);
    setEnableUnmasked(false);
    showToast(
      'No se cargaron los datos previos del propietario. Puedes capturar manualmente o reintentar más tarde.',
      4500,
    );
  }

  /** Reset completo del wizard tras "Cargar otro" en el modal de éxito.
   *  Navega a `/captura` con `replace` para no rehidratar parcela desde la URL,
   *  limpia states, draft y inputs no controlados del `<form>`. */
  /**
   * Limpia TODOS los campos del wizard sin navegar ni tocar `parcelaId`.
   * Reusada por:
   *   - resetWizardForNew (post-submit "Cargar otro" → reset + navigate)
   *   - effect que dispara cuando parcelaId cambia (× clear, click otro
   *     polígono): el form viejo se limpia ANTES de hidratar el nuevo,
   *     evitando que datos del titular anterior contaminen el nuevo
   *     formulario aunque sea brevemente visualmente.
   */
  function clearFormFields() {
    setSigMeta(null);
    setStep(1);
    setEsElPropietario('');
    setEstado('sin_prospectar');
    setIrregularidadDetalle('');
    setGravedad('');
    setSuperficieManualHa(null);
    setBonoFirmaMxn(null);
    setPlazoConstruccionAnios(CONSTRUCCION_PLAZO_DEFAULT_ANIOS);
    setTarifaConstruccionMxn(CONSTRUCCION_TARIFA_DEFAULT_MXN_POR_UNIDAD);
    setCantidadUnidades(null);
    setCantidadUnidadesDirty(false);
    setDiferirMeses(null);
    setSeguimientoMensual(false);
    setDone({});
    setDocFiles({});
    setExistingDocs({});
    setPropietarioMatch(null);
    setPropietarioMatchDismissed(false);
    setQueued([]);
    setContratoFirmadoQueued([]);
    setVisitas([]);
    setFieldErrors({});
    clearDraft();
    setFormData({ tipo_propietario: tipoDoc === 'privado' ? 'pp' : 'ejidatario' });
    formRef.current?.reset();
    hydrateAppliedRef.current = null;
    unmaskedAppliedRef.current = null;
    setEnableUnmasked(false);
    setShowMfaModal(false);
  }

  /**
   * Click handler de las cards "Ejidal" / "Privado". Si el técnico ya
   * empezó a llenar datos (step > 1, archivos en docFiles, o eligió
   * parcela), pedimos confirmación antes de cambiar tipo — el cambio
   * borra datos económicos + checklist de docs + parcela seleccionada
   * (ejido y privado son flujos distintos del paso 1).
   *
   * Handoff parcelas privadas v2 §4.1.5 / Fase 1.5.
   */
  function requestTipoDocChange(next: TipoDoc) {
    const action = decideTipoDocChange({
      currentTipoDoc: tipoDoc,
      nextTipoDoc: next,
      step,
      docFilesCount: Object.keys(docFiles).length,
      hasParcelaSelected: parcelaId !== undefined,
    });
    if (action === 'noop') return;
    if (action === 'confirm') {
      setPendingTipoDocChange(next);
    } else {
      setTipoDoc(next);
    }
  }

  /** Confirma el cambio pendiente: reset completo + cambiar tipoDoc. */
  function confirmTipoDocChange() {
    if (pendingTipoDocChange === null) return;
    const next = pendingTipoDocChange;
    clearFormFields();
    setParcelaId(undefined);
    setTipoDoc(next);
    setPendingTipoDocChange(null);
  }

  function cancelTipoDocChange() {
    setPendingTipoDocChange(null);
  }

  function resetWizardForNew() {
    // Bug 2 del prompt diagnóstico 2026-05-11: el reset dependía del cambio
    // de URL (paramId) para limpiar parcelaId, pero cuando el técnico
    // aterrizaba en /captura y elegía parcela vía mapa/dropzone, la URL
    // nunca tuvo el :parcela_id → navigate('/captura') era no-op a nivel
    // browser history → useEffect([paramId]) no se disparaba → parcelaId
    // quedaba vivo, useCapturaHydrate re-fetcheaba la gestión y repobla
    // los campos. Fix: limpiar state directamente, sin depender del URL.
    setSuccessInfo(null);
    setPendingTipoDocChange(null);
    setParcelaId(undefined);
    setTipoDoc(null);
    setSelMode('mapa');
    setIdInput('');
    setAmbiguousCandidates([]);
    // navigate sigue cubriendo el caso /captura/:parcela_id (URL directa).
    navigate('/captura', { replace: true });
    clearFormFields();
    // Devolver foco al inicio del form.
    setTimeout(() => {
      document.querySelector('.form-body')?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 30);
  }

  // Effect: reset del form cuando parcelaId cambia.
  // Casos cubiertos:
  //   1. × clear chip → parcelaId pasa de "HUM-PXXXX" a undefined → limpiar.
  //   2. Click otro polígono en mapa → parcelaId cambia "HUM-A" → "HUM-B" → limpiar.
  //   3. Cambio via input "Número o ID" → mismo patrón.
  // En todos, después del clear, los efects de hidratación detectarán que
  // hydrateAppliedRef.current === null para la nueva parcela y volverán a
  // hidratar desde server. Si parcelaId pasa a undefined, simplemente
  // queda el form vacío (estado correcto para selección).
  const lastParcelaIdRef = useRef<string | undefined>(parcelaId);
  useEffect(() => {
    if (lastParcelaIdRef.current === parcelaId) return;
    // Solo limpiar si ya HABÍA una parcela seleccionada (lastRef !== undefined).
    // En el primer mount, parcelaId puede venir del URL paramId — no queremos
    // limpiar antes de la primera hidratación legítima.
    if (lastParcelaIdRef.current !== undefined) {
      clearFormFields();
    }
    lastParcelaIdRef.current = parcelaId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelaId]);

  // Auto-sync tipoDoc desde el prefijo de parcelaId.
  // Casos cubiertos: URL param /captura/HUM-P0042, draft restore, click en
  // mapa, dropzone PRV, click en candidato ambiguo, etc. — todos terminan
  // pasando por setParcelaId y este efecto reconcilia tipoDoc.
  // Si el prefijo no es reconocible (REF-*, formato custom), no toca tipoDoc.
  // Bug 1 fix · prompt diagnóstico tipoDoc/reset 2026-05-11.
  useEffect(() => {
    if (!parcelaId) return;
    const detected = tipoDocFromParcelaId(parcelaId);
    if (detected === undefined) {
      // eslint-disable-next-line no-console
      console.warn('[captura] parcelaId sin prefijo conocido; tipoDoc no se auto-detecta:', parcelaId);
      return;
    }
    if (detected !== tipoDoc) setTipoDoc(detected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelaId]);

  // Superficie preliminar: prefiere area_ha del inventario API, luego del SIG (click
  // en mapa), y como último recurso lo capturado manualmente. Read-only en UI cuando
  // la fuente es la parcela; editable cuando no hay dato.
  const superficieParcelaHa = useMemo<number | null>(() => {
    const fromApi = Number(parcelaMeta?.area_ha);
    if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
    const fromSig = Number(sigMeta?.area_ha);
    if (Number.isFinite(fromSig) && fromSig > 0) return fromSig;
    return null;
  }, [parcelaMeta?.area_ha, sigMeta?.area_ha]);
  const superficiePreliminarHa = superficieParcelaHa ?? superficieManualHa;
  const superficieEsAutomatica = superficieParcelaHa != null;

  // Superficie aplicable (construcción): por defecto igual al total preliminar; al
  // cambiar de parcela se resetea el "dirty" para volver a copiar el total.
  // También se reinicia el flujo de hidratación unmasked: cada parcela
  // empieza con `enableUnmasked=false` y se vuelve a habilitar dentro del
  // effect de `useCapturaHydrate` cuando detecta que la nueva parcela tiene
  // captura previa con propietario.
  useEffect(() => {
    if (parcelaId !== lastParcelaIdForCantidadRef.current) {
      lastParcelaIdForCantidadRef.current = parcelaId;
      setCantidadUnidadesDirty(false);
      // Reiniciar refs de hidratación para que la nueva parcela vuelva a
      // entrar al flujo. Sin esto, navegar de HUM-P0001 a HUM-P0002
      // dejaba `unmaskedAppliedRef.current === HUM-P0001` y bloqueaba la
      // hidratación de la siguiente.
      unmaskedAppliedRef.current = null;
      setEnableUnmasked(false);
      setShowMfaModal(false);
    }
  }, [parcelaId]);

  useEffect(() => {
    if (cantidadUnidadesDirty) return;
    setCantidadUnidades(superficiePreliminarHa);
  }, [superficiePreliminarHa, cantidadUnidadesDirty]);

  // Objeto modelo_negocio derivado del estado (Opción A: solo UI + draft).
  const modeloNegocio = useMemo(
    () =>
      buildModeloNegocio({
        preliminar_superficie_ha: superficiePreliminarHa,
        bono_firma_mxn: bonoFirmaMxn,
        construccion_plazo_anios: plazoConstruccionAnios,
        construccion_tarifa_mxn_por_unidad: tarifaConstruccionMxn,
        construccion_cantidad_unidades: cantidadUnidades,
        diferir_meses: diferirMeses,
        seguimiento_mensual: seguimientoMensual,
      }),
    [
      superficiePreliminarHa,
      bonoFirmaMxn,
      plazoConstruccionAnios,
      tarifaConstruccionMxn,
      cantidadUnidades,
      diferirMeses,
      seguimientoMensual,
    ],
  );

  // Carga visitas históricas cuando cambia la parcela seleccionada. Resuelve
  // primero parcela_codigo → parcelas.id (UUID FK), luego query visitas
  // ordenadas DESC por fecha_visita (más reciente primero).
  const fetchVisitasParcela = useCallback(async (codigo: string): Promise<void> => {
    if (!isSupabaseConfigured()) return;
    setVisitasLoading(true);
    try {
      const sb = getSupabase();
      const { data: parRow } = await sb
        .from('parcelas')
        .select('id')
        .eq('parcela_id', codigo)
        .maybeSingle();
      if (!parRow?.id) {
        setVisitas([]);
        return;
      }
      const { data, error } = await sb
        .from('visitas')
        .select('id, fecha_visita, notas, narrativa, geom_punto')
        .eq('parcela_id', parRow.id)
        .order('fecha_visita', { ascending: false })
        .limit(50);
      if (error) throw error;
      type Row = { id: string; fecha_visita: string; notas: string | null; narrativa: unknown; geom_punto: unknown };
      // Extrae lng/lat de un GeoJSON Point devuelto por PostGIS. Tolerante a
      // forma `{type:'Point', coordinates:[lng,lat]}` y a null cuando la
      // visita no registró GPS.
      const extractLngLat = (geom: unknown): { lng: number | null; lat: number | null } => {
        if (!geom || typeof geom !== 'object' || !('coordinates' in geom)) return { lng: null, lat: null };
        const c = (geom as { coordinates?: unknown }).coordinates;
        if (Array.isArray(c) && c.length >= 2 && typeof c[0] === 'number' && typeof c[1] === 'number') {
          return { lng: c[0], lat: c[1] };
        }
        return { lng: null, lat: null };
      };
      const rows = ((data ?? []) as Row[]).map<VisitaHistorial>((r) => {
        const { lng, lat } = extractLngLat(r.geom_punto);
        return {
          id: r.id,
          fecha_visita: r.fecha_visita,
          notas: r.notas,
          narrativa: r.narrativa == null ? null : coerceNarrativa(r.narrativa),
          lat,
          lng,
        };
      });
      setVisitas(rows);
    } catch {
      setVisitas([]);
    } finally {
      setVisitasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!parcelaId) {
      setVisitas([]);
      return;
    }
    void fetchVisitasParcela(parcelaId);
  }, [parcelaId, fetchVisitasParcela]);

  // Default a ejidal cuando tipoDoc=null (caso defensivo: el step 3 nunca
  // se ve con null porque hay parcela seleccionada y el auto-sync ya corrió).
  const docs       = tipoDoc === 'privado' ? DOCS_PRIVADA : DOCS_EJIDAL;
  const docGroups  = tipoDoc === 'privado' ? DOCS_PRIVADA_GROUPS : DOCS_EJIDAL_GROUPS;
  const doneCount  = docs.filter((d) => done[d.id]).length;

  function handleDocFile(docId: string, files: FileList | null) {
    if (!files?.length) return;
    const file = files[0];
    if (!file) return;
    setDocFiles((p) => Object.assign({}, p, { [docId]: file }) as Record<string, File>);
    setDone((p) => ({ ...p, [docId]: true }));
    showToast(`${file.name} · subido`);
  }

  /**
   * Da de baja un expediente ya persistido en R2 + tabla expedientes.
   * Caso: el técnico subió por error el archivo equivocado y necesita
   * quitarlo limpiamente (no solo "reemplazar"). Hard-delete por diseño:
   * la tabla expedientes no tiene `status` y el caso "ocultar pero
   * conservar" complica la UI sin valor real para la operación.
   * El audit log preserva el rastro completo (filename, r2_key, sha256,
   * uploaded_by) para investigación post-mortem.
   */
  async function handleRemoveExistingDoc(docId: string, doc: ExistingDoc): Promise<void> {
    const ok = typeof window !== 'undefined'
      ? window.confirm(`¿Dar de baja "${doc.filename}"? Esta acción borra el archivo del expediente. Queda registro en la bitácora.`)
      : false;
    if (!ok) return;
    try {
      await bffFetch(`/v1/upload/expediente/${doc.id}`, { method: 'DELETE' });
      // Optimistic UI: limpiar localmente antes de que llegue el refetch.
      setExistingDocs((prev) => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
      // Si NO hay un docFiles[docId] pendiente local, también desmarcamos
      // el `done` (volver al estado pendiente). Si sí hay uno local, el
      // user está en medio de un reemplazo y conservamos su selección.
      setDone((prev) => {
        if (docFiles[docId]) return prev;
        const next = { ...prev };
        delete next[docId];
        return next;
      });
      showToast(`${doc.filename} · dado de baja`);
      // Refrescar el listado real para alinearse con el server.
      void refetchExpedientes();
    } catch (e) {
      const msg = e instanceof BffError ? e.message : (e as Error).message;
      showToast(`No se pudo dar de baja: ${msg}`, { variant: 'error' });
    }
  }

  /**
   * Dar de baja un contrato firmado ya persistido en R2. Mismo flujo que
   * handleRemoveExistingDoc pero sobre la lista (no mapa) — contrato_firmado
   * puede tener múltiples versiones por parcela. Audit trail intacto: la
   * tabla expedientes es append-only y el DELETE del BFF deja registro en
   * bitácora vía trigger.
   */
  async function handleRemoveExistingContrato(doc: ExistingDoc): Promise<void> {
    const ok = typeof window !== 'undefined'
      ? window.confirm(`¿Dar de baja "${doc.filename}"? Esta acción borra el contrato firmado del expediente. Queda registro en la bitácora.`)
      : false;
    if (!ok) return;
    try {
      await bffFetch(`/v1/upload/expediente/${doc.id}`, { method: 'DELETE' });
      // Optimistic UI: filtra fuera localmente antes del refetch.
      setExistingContratos((prev) => prev.filter((d) => d.id !== doc.id));
      showToast(`${doc.filename} · dado de baja`);
      void refetchExpedientes();
    } catch (e) {
      const msg = e instanceof BffError ? e.message : (e as Error).message;
      showToast(`No se pudo dar de baja: ${msg}`, { variant: 'error' });
    }
  }

  /** Mismo flujo que handleRemoveExistingContrato pero para archivos sueltos
   *  (tipo_doc='otro') del dropzone genérico. */
  async function handleRemoveExistingOtro(doc: ExistingDoc): Promise<void> {
    const ok = typeof window !== 'undefined'
      ? window.confirm(`¿Dar de baja "${doc.filename}"? Esta acción borra el archivo del expediente. Queda registro en la bitácora.`)
      : false;
    if (!ok) return;
    try {
      await bffFetch(`/v1/upload/expediente/${doc.id}`, { method: 'DELETE' });
      setExistingOtros((prev) => prev.filter((d) => d.id !== doc.id));
      showToast(`${doc.filename} · dado de baja`);
      void refetchExpedientes();
    } catch (e) {
      const msg = e instanceof BffError ? e.message : (e as Error).message;
      showToast(`No se pudo dar de baja: ${msg}`, { variant: 'error' });
    }
  }

  function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    const arr = Array.from(files);
    setQueued((p) => [...p, ...arr]);
    showToast(`${arr.length} archivo(s) en cola`);
  }

  function removeQueued(name: string) {
    setQueued((p) => p.filter((f) => f.name !== name));
  }

  // El upload de expedientes (contrato firmado, checklist, dropzone genérico)
  // se delega al helper compartido `uploadExpedienteFile` en
  // @aosenuma/lib-supabase: presigned PUT R2 + INSERT a expedientes vía BFF
  // + invalidación de cache. Antes había una copia local solo para
  // contrato_firmado; el resto de archivos se quedaba en state sin subir.

  function handleContratoFirmadoFiles(files: FileList | null) {
    if (!files?.length) return;
    const arr = Array.from(files).filter((f) => {
      if (f.size > CONTRATO_FIRMADO_MAX_BYTES) {
        showToast(`«${f.name}» supera 50 MiB`, { variant: 'error' });
        return false;
      }
      return true;
    });
    if (!arr.length) return;
    setContratoFirmadoQueued((p) => [...p, ...arr]);
    showToast(`Contrato firmado: ${arr.length} archivo(s) en cola`);
  }

  function removeContratoFirmadoQueued(name: string) {
    setContratoFirmadoQueued((p) => p.filter((f) => f.name !== name));
  }

  function handleParcelaClick(id: string, meta: ParcelaFeatureMeta) {
    // Guard de tipo: si el técnico ya eligió tipoDoc (ejido o privado) y
    // clickea una parcela del otro tipo → rechazar con warning. Permite el
    // click cuando tipoDoc=null (auto-sync lo asigna después) o cuando el
    // prefijo del ID no se reconoce (REF-*, etc.). Bug 1 + Q4 prompt
    // diagnóstico 2026-05-11.
    const guard = evaluateParcelaClickGuard({ currentTipoDoc: tipoDoc, clickedParcelaId: id });
    if (guard.kind === 'reject') {
      showToast(guard.reason, { variant: 'error' });
      return;
    }
    setParcelaId(id);
    setSigMeta(meta);
    const areaLabel = typeof meta.area_ha === 'number' ? ` · ${meta.area_ha.toFixed(2)} ha` : '';
    showToast(
      isUsoComunParcelaId(id)
        ? `Uso común seleccionado: ${id}${areaLabel}`
        : `Parcela seleccionada: ${id}${areaLabel}`,
    );
    // Auto-fill conservador de colindancias: solo si la parcela trae el
    // campo en el GeoJSON Y los 4 inputs estaban vacíos (cero pisado).
    // Si el técnico ya escribió en alguno, no tocamos nada — usa el botón
    // "Aplicar colindancias desde capa" más abajo (con confirmación).
    autoFillColindanciasIfEmpty(meta);
  }

  /** Lee `meta.colindancias` y, si los 4 inputs `col_*` están vacíos en
   *  `formData`, los rellena. No-op si meta no trae el campo o si el
   *  técnico ya capturó alguno. */
  function autoFillColindanciasIfEmpty(meta: ParcelaFeatureMeta): void {
    const c = meta.colindancias;
    if (!c) return;
    const yaEscritos = ['col_norte', 'col_sur', 'col_oriente', 'col_poniente'].some(
      (k) => (formData[k] ?? '').trim().length > 0,
    );
    if (yaEscritos) return;
    const next = colindanciasToFormPatch(c);
    if (Object.keys(next).length === 0) return;
    setFormData((prev) => ({ ...prev, ...next }));
    showToast('Colindancias autollenadas desde la capa.');
  }

  /** Aplica `meta.colindancias` aunque haya valores capturados, previa
   *  confirmación del usuario. Usado por el botón "Aplicar desde capa". */
  function applyColindanciasFromMeta(): void {
    const c = sigMeta?.colindancias;
    if (!c) return;
    const next = colindanciasToFormPatch(c);
    if (Object.keys(next).length === 0) {
      showToast('La parcela no trae texto de colindancias en la capa.');
      return;
    }
    const yaEscritos = ['col_norte', 'col_sur', 'col_oriente', 'col_poniente'].some(
      (k) => (formData[k] ?? '').trim().length > 0,
    );
    if (yaEscritos) {
      const ok = window.confirm(
        'Vas a sobreescribir las colindancias que ya capturaste con los valores de la capa. ¿Continuar?',
      );
      if (!ok) return;
    }
    setFormData((prev) => ({ ...prev, ...next }));
    showToast('Colindancias aplicadas desde la capa.');
  }

  /** Aplana `colindancias` del meta al patch que setFormData espera. Llaves
   *  con `null` se traducen a string vacío (deja el input visualmente
   *  limpio). `undefined` se omite (no toca el input). */
  function colindanciasToFormPatch(
    c: NonNullable<ParcelaFeatureMeta['colindancias']>,
  ): Partial<Record<string, string>> {
    const out: Partial<Record<string, string>> = {};
    const setIf = (key: string, value: string | null | undefined) => {
      if (value === undefined) return;
      out[key] = value === null ? '' : value;
    };
    setIf('col_norte', c.norte);
    setIf('col_sur', c.sur);
    setIf('col_oriente', c.oriente);
    setIf('col_poniente', c.poniente);
    return out;
  }

  function scrollFormTop() {
    setTimeout(() => {
      document.querySelector('.form-body')?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 30);
  }

  function goNext() {
    if (step === 1 && !parcelaId) {
      showToast('Toca una parcela en el mapa para continuar.');
      return;
    }
    setStep((s) => Math.min(s + 1, 3) as WizardStep);
    scrollFormTop();
  }

  function goPrev() {
    setStep((s) => Math.max(s - 1, 1) as WizardStep);
    scrollFormTop();
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Guard contra submits accidentales: el user presiona Enter en un input
    // del paso 1 o 2 → el form se submita por default browser. El "Guardar
    // reporte" solo debe disparar desde el botón submit del paso 3. Si
    // llegamos aquí desde otro step, ignoramos silently.
    if (step !== 3) {
      return;
    }
    if (!parcelaId) {
      showToast('Selecciona una parcela');
      return;
    }
    if (!isSupabaseConfigured()) {
      showToast('Supabase no configurado');
      return;
    }

    // Captura de campo es laxa: si el técnico anotó una irregularidad pero
    // olvidó escoger gravedad, `buildIrregularidadesPayload` la descarta
    // silenciosamente en lugar de bloquear el submit. El reporte se guarda
    // y la irregularidad puede capturarse después con gravedad correcta.

    // ─── Lectura del form desde state controlado ──────────────────────────
    // ANTES leíamos via `new FormData(form)`. Bug crítico: el wizard
    // monta/desmonta el árbol del paso al cambiar step (`{step === N && ...}`),
    // por lo que al hacer submit desde paso 3 los inputs del paso 1 y 2 ya
    // no estaban en el DOM y `fd.get('propietario')` devolvía null.
    // Resultado: payload con propietario/etc. todo en null.
    // Fuente de verdad: el state `formData` (key→string), poblado por
    // `bindInput`/`bindFieldValidated` y persistido en el draft localStorage.

    // ─── Validación final pre-submit ───────────────────────────────────────
    // Política: ningún campo del expediente es obligatorio para guardar
    // (Diego: el técnico debe poder capturar parcialmente sin que la app
    // lo bloquee). Solo validamos FORMATOS — si llenó CURP y está mal,
    // bloqueamos para que no entre data corrupta a la DB; pero un campo
    // vacío siempre es válido. Datos parciales no perdidos: el draft
    // localStorage los retiene incluso si decide volver después.
    //
    // Política de persistencia (mayo 2026 — actualizada): se manda
    // `propietarioPayload` al BFF si AL MENOS UN campo del propietario
    // (nombre, teléfono, INE, CURP, RFC, email, dirección, CLABE, folios,
    // clave catastral) está lleno — sin importar el estado de gestión.
    // Antes: solo si había titular. Eso causaba pérdida silenciosa de
    // teléfonos/INEs ya tipeados cuando el técnico no había completado
    // el nombre. Ahora: cualquier dato persiste; `titular_masked` queda
    // NULL hasta que el técnico vuelva a editar y agregue el nombre.
    const errs: Record<string, string> = {};
    for (const [name, validator] of Object.entries(FIELD_VALIDATORS)) {
      const raw = formData[name] ?? '';
      const res = validator(raw);
      if (!res.valid && res.reason) errs[name] = res.reason;
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const firstKey = Object.keys(errs)[0];
      // Todos los validators (telefono/email/numero_ine/curp/rfc/clabe)
      // viven en paso 1, y el guard de arriba garantiza que estamos en
      // paso 3. Saltar a paso 1 para que el querySelector encuentre el
      // input — el árbol del paso actual está desmontado.
      setStep(1);
      // Esperar al re-render antes de enfocar.
      requestAnimationFrame(() => {
        const firstInput = formRef.current?.querySelector<HTMLInputElement>(`[name="${firstKey}"]`);
        firstInput?.focus({ preventScroll: false });
        firstInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      showToast(
        `Hay ${Object.keys(errs).length} campo(s) con formato inválido. Revisa los marcados en rojo.`,
        { variant: 'error' },
      );
      return;
    }
    // ──────────────────────────────────────────────────────────────────────


    // ─── Propietario (solo si hay titular; schema BFF valida formato) ─────
    // El select de tenencia usa "ejidatario" / "posesionario" / "pp" /
    // "comisariado" / "comunero" (uso común, mig 0068); el schema BFF acepta
    // ejidatario/comunero/posesionario/propietario/avecindado/comisariado.
    // Mapeamos "pp" (pequeña propiedad RPP) → "propietario".
    const titular = pickField('propietario');
    const tipoRaw = pickField('tipo_propietario') ?? 'ejidatario';
    const tipoTenenciaMap: Record<string, 'ejidatario' | 'comunero' | 'posesionario' | 'propietario' | 'avecindado' | 'comisariado'> = {
      ejidatario: 'ejidatario',
      comunero: 'comunero',
      posesionario: 'posesionario',
      avecindado: 'avecindado',
      propietario: 'propietario',
      comisariado: 'comisariado',
      pp: 'propietario',
    };
    const tipoTenencia = tipoTenenciaMap[tipoRaw] ?? 'ejidatario';

    // Aplicamos normalizers oficiales (uppercase CURP/RFC/INE, strip de
    // teléfono, lowercase email). Los validators ya pasaron arriba; aquí
    // solo dejamos los valores en formato canónico para el BFF y la DB.
    const _telefonoRaw = pickField('telefono');
    const _emailRaw    = pickField('email');
    const _curpRaw     = pickField('curp');
    const _rfcRaw      = pickField('rfc');
    const _ineRaw      = pickField('numero_ine');
    const _clabeRaw    = pickField('clabe');
    const orNull = (s: string): string | null => (s === '' ? null : s);

    // `normalizeTitular` canoniza el nombre completo a UPPERCASE + NFC
    // antes de enviarlo al BFF. Sin esto, "María López" un día y
    // "MARIA LOPEZ" otro día cifran a bytes distintos en `titular_v2`
    // (AES-256-GCM) y rompen el lookup por equality. La capitalización
    // canónica también garantiza que el `titular_masked` derivado en el
    // BFF sea estable entre re-capturas. El BFF re-aplica defensivamente
    // por si llega un cliente que olvidó normalizar.
    //
    // `titular = null` cuando el técnico no llenó el nombre. El BFF acepta
    // ahora propietario sin titular — los demás datos se cifran/persisten
    // igual; `titular_masked` queda NULL en la DB hasta que se complete.
    const titularNormalizado = normalizeTitular(titular) || null;
    const folioDerechosRaw = pickField('folio_derechos');
    const folioTierrasRaw = pickField('folio_tierras');
    const claveCatastralRaw = pickField('clave_catastral');
    const direccionRaw = pickField('direccion');

    // Construir payload con nulls explícitos en cada campo no llenado.
    // Persistir SIEMPRE que haya AL MENOS UN campo no-vacío (cualquiera).
    // Esto previene la pérdida silenciosa de datos parciales: si el técnico
    // llenó solo teléfono+INE pero olvidó el nombre, igual se guarda lo
    // capturado. La columna `titular_masked` queda NULL — la UI muestra
    // "Sin titular registrado" pero los demás datos están vivos en DB.
    const propPayloadCandidate = {
      titular: titularNormalizado,
      tipo: tipoTenencia,
      telefono:    _telefonoRaw ? orNull(normalizeTelefonoMx(_telefonoRaw)) : null,
      email:       _emailRaw    ? orNull(normalizeEmail(_emailRaw))         : null,
      ine_numero:  _ineRaw      ? orNull(normalizeClaveElector(_ineRaw))    : null,
      direccion:   direccionRaw,
      curp:        _curpRaw     ? orNull(normalizeCURP(_curpRaw))           : null,
      rfc:         _rfcRaw      ? orNull(normalizeRFC(_rfcRaw))             : null,
      clabe:       _clabeRaw    ? orNull(normalizeCLABE(_clabeRaw))         : null,
      folio_derechos: folioDerechosRaw,
      folio_tierras: folioTierrasRaw,
      clave_catastral: claveCatastralRaw,
    };
    // WYSIWYG semantics:
    //   - Si la parcela YA tiene propietario en DB Y el PII está descifrado
    //     (técnico completó MFA → unmaskedAppliedRef confirmó hidratación),
    //     el form refleja el estado real. Mandar SIEMPRE el payload aunque
    //     todos los campos sean null (intención explícita de borrar).
    //
    //   - Si NO está descifrado (sin MFA / no hidratado todavía / parcela
    //     nueva sin propietario), preservar la semántica defensiva: solo
    //     mandar si hay algún campo sustantivo. Esto evita borrar PII
    //     accidentalmente desde un estado donde el form no muestra los
    //     valores reales (técnico ve campos vacíos no porque los borró,
    //     sino porque no se descifraron todavía).
    //
    // Reportado 2026-05-15: con la semántica vieja, borrar TODOS los
    // campos PII de un propietario existente saltaba upsertPropietarioV2
    // en el server → telefono_v2 (y resto de PII) quedaban con el valor
    // cifrado anterior. Ahora con MFA activo + PII hidratado, el borrado
    // se persiste correctamente para los 11 campos (titular, telefono,
    // email, curp, rfc, ine_numero, direccion, clabe, folio_derechos,
    // folio_tierras, clave_catastral).
    const haySustantivo =
      titularNormalizado != null
      || propPayloadCandidate.telefono != null
      || propPayloadCandidate.email != null
      || propPayloadCandidate.curp != null
      || propPayloadCandidate.rfc != null
      || propPayloadCandidate.ine_numero != null
      || propPayloadCandidate.direccion != null
      || propPayloadCandidate.clabe != null
      || propPayloadCandidate.folio_derechos != null
      || propPayloadCandidate.folio_tierras != null
      || propPayloadCandidate.clave_catastral != null;
    const propietarioYaExiste = hydrateData?.propietario != null;
    const piiDescifrado = unmaskedAppliedRef.current === parcelaId;
    const propietarioPayload =
      (propietarioYaExiste && piiDescifrado)
        ? propPayloadCandidate
        : (haySustantivo ? propPayloadCandidate : undefined);

    // ─── Negociación: económicos + extras del wizard como JSON en notas ───
    // monto_renta_ha = tarifa preliminar (constante). plazo_anos del slider
    // de construcción. El resto de campos del wizard que el schema BFF no
    // tiene columnas dedicadas (bono firma, tarifa construcción, signatario,
    // calidad posesionario, liderazgo ejido, modalidad de pago, fecha de firma,
    // situación legal, colindancias, condiciones, postura, motivo_inviable…)
    // se persisten como JSON estructurado en `gestiones.notas` para no
    // perderlos. Si más adelante agregamos columnas dedicadas, migrar desde
    // notas.
    const notasObj: Record<string, unknown> = {
      bono_firma_mxn: bonoFirmaMxn,
      construccion: {
        plazo_anos: plazoConstruccionAnios,
        tarifa_mxn_unidad: tarifaConstruccionMxn,
        cantidad_unidades: cantidadUnidades,
        subtotal_mxn: modeloNegocio.construccion_operacion.subtotal_mxn,
      },
      pagos_diferidos: {
        meses: modeloNegocio.pagos_diferidos.meses,
        monto_mensual_mxn: modeloNegocio.pagos_diferidos.monto_mensual_mxn,
        seguimiento_mensual: modeloNegocio.pagos_diferidos.seguimiento_mensual,
      },
      modelo_negocio_total_mxn: modeloNegocio.totales.suma_componentes_mxn,
      campo: {
        es_el_propietario: esElPropietario || null,
        signatario_nombre: pickField('signatario_nombre'),
        tercero_relacion: pickField('tercero_relacion'),
        calidad_posesionario: pickField('calidad_posesionario'),
        relacion_ejido: pickField('relacion_ejido'),
        liderazgo_ejido: pickField('liderazgo_ejido'),
        acta_nacimiento_folio: pickField('acta_nacimiento_folio'),
      },
      contrato: {
        // modalidad_pago retirado del form 2026-05-09; se sigue
        // aceptando si viene en notas legacy pero ya no se captura desde UI.
        fecha_firma_est: pickField('fecha_firma_est'),
        situacion_legal: pickField('situacion_legal'),
        colindancias: {
          norte: pickField('col_norte'),
          sur: pickField('col_sur'),
          oriente: pickField('col_oriente'),
          poniente: pickField('col_poniente'),
        },
        condiciones: pickField('condiciones'),
        // Clase de predio (RF5, mig 0068): USO_COMUN marca contratos sobre
        // tierras del núcleo agrario (firma vía asamblea/comisariado, no
        // ejidatario individual). null en parcelas normales.
        tipo_predio: esUsoComun ? 'USO_COMUN' : null,
      },
      gestion: {
        postura_propietario: pickField('postura_propietario'),
        motivo_inviable: estado === 'inviable' ? pickField('motivo_inviable') : null,
      },
      narrativa: {
        descripcion_visita: pickField('descripcion_visita'),
        descripcion_negociacion: pickField('descripcion_negociacion'),
        reqs_area_legal: pickField('reqs_area_legal'),
        observaciones_tecnico: pickField('observaciones_tecnico'),
      },
      tipo_doc: tipoDoc ?? 'ejido',
    };
    const notasJsonRaw = JSON.stringify(notasObj);
    if (notasJsonRaw.length > GESTION_NOTAS_JSON_MAX_CHARS) {
      showToast(
        `Las notas superan ${GESTION_NOTAS_JSON_MAX_CHARS} caracteres. Acorta sobre todo las cuatro narrativas del paso 2.`,
        { variant: 'error' },
      );
      return;
    }
    const notasJson = notasJsonRaw;

    // Riesgo: el select de paso 2 alimenta directamente la columna
    // `negociacion.riesgo` (Zod schema acepta bajo|medio|alto|null).
    const riesgoRaw = pickField('riesgo');
    const riesgoVal: 'bajo' | 'medio' | 'alto' | null =
      riesgoRaw === 'bajo' || riesgoRaw === 'medio' || riesgoRaw === 'alto' ? riesgoRaw : null;

    const payload = {
      parcela_id: parcelaId,
      propietario: propietarioPayload,
      negociacion: {
        estado,
        monto_renta_ha: PRELIMINAR_MXN_POR_HA,
        plazo_anos: plazoConstruccionAnios > 0 && plazoConstruccionAnios <= 99 ? plazoConstruccionAnios : null,
        enganche_pct: null as number | null,
        notas: notasJson,
        riesgo: riesgoVal,
      },
      irregularidades: buildIrregularidadesPayload(irregularidadDetalle, gravedad),
    };

    if (submitting) return; // double-submit guard
    setSubmitting(true);
    try {
      await bffFetch('/v1/captura', { method: 'POST', body: payload });

      // ─── Subida de archivos ──────────────────────────────────────────────
      // Tres colas distintas, todas vacían tras subir:
      //   1. `contratoFirmadoQueued` → tipo_doc=contrato_firmado (bucket
      //      aosenuma-expedientes). El cliente Elawan los ve con "Ver
      //      contrato".
      //   2. `docFiles` (checklist) → tipo_doc según docId del checklist.
      //      Mapper en lib-supabase normaliza al schema BFF
      //      (identificacion_oficial → ine, rfc_homoclave → rfc, etc).
      //   3. `queued` (dropzone genérico "Subir expediente") → tipo_doc=otro.
      // Si una subida falla, marcamos el reporte como guardado pero
      // notificamos qué cola falló — no abortamos el éxito del /v1/captura.
      const uploadFailures: string[] = [];

      const contratoFiles = [...contratoFirmadoQueued];
      if (contratoFiles.length > 0) {
        try {
          for (const f of contratoFiles) {
            await uploadExpedienteFile(parcelaId, f, 'contrato_firmado');
          }
          setContratoFirmadoQueued([]);
        } catch (upErr) {
          uploadFailures.push(`contrato: ${upErr instanceof Error ? upErr.message : String(upErr)}`);
        }
      }

      // Checklist: cada entrada en `docFiles` mapea docId → File.
      const checklistEntries = Object.entries(docFiles);
      if (checklistEntries.length > 0) {
        const subidos: string[] = [];
        for (const [docId, file] of checklistEntries) {
          try {
            await uploadExpedienteFile(parcelaId, file, docId);
            subidos.push(docId);
          } catch (upErr) {
            uploadFailures.push(`${docId}: ${upErr instanceof Error ? upErr.message : String(upErr)}`);
          }
        }
        if (subidos.length > 0) {
          setDocFiles((prev) => {
            const next = { ...prev };
            for (const id of subidos) delete next[id];
            return next;
          });
        }
      }

      // Dropzone genérico: archivos sueltos sin docId → tipo_doc=otro.
      const queuedFiles = [...queued];
      if (queuedFiles.length > 0) {
        const subidosNombres: string[] = [];
        for (const f of queuedFiles) {
          try {
            await uploadExpedienteFile(parcelaId, f, 'otro');
            subidosNombres.push(f.name);
          } catch (upErr) {
            uploadFailures.push(`${f.name}: ${upErr instanceof Error ? upErr.message : String(upErr)}`);
          }
        }
        if (subidosNombres.length > 0) {
          setQueued((prev) => prev.filter((f) => !subidosNombres.includes(f.name)));
        }
      }

      if (uploadFailures.length > 0) {
        const detalle = uploadFailures.slice(0, 3).join(' · ');
        const extra = uploadFailures.length > 3 ? ` y ${uploadFailures.length - 3} más` : '';
        showToast(
          `Reporte guardado, pero ${uploadFailures.length} archivo(s) fallaron al subir: ${detalle}${extra}`,
          { variant: 'error' },
        );
      }

      // Invalidar caches de TODOS los hooks que dependen de gestiones/
      // expedientes/inventario/KPIs. Otras pestañas (cliente Elawan en
      // otra ventana, dashboard del mismo user) reciben el broadcast y
      // refetch sin esperar TTL. Sin esto el cliente veía datos viejos
      // hasta 60s + reload manual.
      invalidateCache([
        'parcelas_tecnicas',
        'parcelas_publicas',
        'inventario',
        'kpi_dashboard',
        'expedientes_list',
        // Bloque "Términos contractuales" del dashboard cliente — la cache
        // se indexa por código público para que cada parcela tenga su
        // propio entry y no se choquen.
        `gestion_notas:${parcelaId}`,
      ]);
      void refetchParcelas();
      clearDraft();
      // Modal de éxito explícito (reemplaza el toast). Diego: "lo ideal es
      // que aparezca un panel que pregunte si hacer otro registro o ver el
      // panel en modo solo lectura".
      const visitaFbSubmit = visitas[0]?.narrativa ?? null;
      const docLabelsSubmit = docs.filter((d) => done[d.id]).map((d) => d.label);
      const fechaInforme = fmtFechaReportePdf(new Date());
      const reportePdf = buildReporteSeguimientoDTO({
        fechaGeneracion: fechaInforme,
        elaboradoPor: elaboradoPorLabel,
        parcela: {
          parcelaId,
          municipio: String(parcelaMeta?.municipio ?? ''),
          nomNucleo: parcelaMeta?.nom_nucleo ?? '—',
          numParcela: parcelaMeta?.num_parcela ?? null,
          tipoPropiedadParcela:
            parcelaMeta?.tipo_propi
            ?? (sigMeta?.tipo_propi ?? null)
            ?? (tipoDoc === 'privado' ? 'PROP_PRIV' : 'EJIDO'),
          areaHa:
            parcelaMeta?.area_ha != null && Number.isFinite(parcelaMeta.area_ha)
              ? parcelaMeta.area_ha
              : null,
        },
        propietarioNombre: titularNormalizado ?? pickField('propietario'),
        telefono: _telefonoRaw ? orNull(normalizeTelefonoMx(_telefonoRaw)) : null,
        direccion: direccionRaw,
        folioDerechos: folioDerechosRaw,
        folioTierras: folioTierrasRaw,
        claveCatastral: claveCatastralRaw,
        esElPropietario:
          esElPropietario === 'si' ? true : esElPropietario === 'no' ? false : null,
        signatarioNombre: pickField('signatario_nombre'),
        terceroRelacion: pickField('tercero_relacion'),
        calidadPosesionario: pickField('calidad_posesionario'),
        tipoTenencia: pickField('tipo_propietario'),
        relacionEjido: pickField('relacion_ejido'),
        liderazgoEjido: pickField('liderazgo_ejido'),
        actaNacimientoFolio: pickField('acta_nacimiento_folio'),
        estadoGestion: estado,
        bonoFirmaMxn,
        plazoConstruccionAnios,
        notasRawJson: notasJson,
        riesgo: riesgoVal,
        coordenadasText: resolveCoordenadasSync(),
        irregularidadDetalle: irregularidadDetalle.trim() || null,
        visitaNarrativaFallback: visitaFbSubmit,
        expedienteDocsCompletadosLabels: docLabelsSubmit,
      });

      setSuccessInfo({
        parcelaId,
        nomNucleo: parcelaMeta?.nom_nucleo ?? '—',
        numParcela: parcelaMeta?.num_parcela ?? null,
        propietarioNombre: titularNormalizado ?? pickField('propietario'),
        reportePdf,
      });
    } catch (err) {
      const msg = err instanceof BffError
        ? `No se pudo guardar: ${err.message}${err.status === 401 ? ' (sesión expirada)' : ''}`
        : `Error inesperado: ${err instanceof Error ? err.message : String(err)}`;
      showToast(msg, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  // Generación cliente-side del PDF de seguimiento. Lazy-import para no
  // engordar el bundle inicial — la lib pesa ~1.2MB y solo se usa al
  // descargar. Lee de successInfo (ya tiene los datos congelados al
  // momento del submit, porque str()/FormData ya no están vivos aquí).
  const [generandoPdf, setGenerandoPdf] = useState(false);

  /** Elimina segmentos duplicados en textos de colindancias generados por el algoritmo espacial. */
  function dedupeColindancia(val: string | null | undefined): string | null {
    if (!val) return null;
    const parts = val.split(' · ');
    const seen = new Set<string>();
    const unique = parts.filter((p) => { if (seen.has(p)) return false; seen.add(p); return true; });
    return unique.join(' · ') || null;
  }

  /** Resuelve coordenadas SYNC con la cadena de fallback: manual form > sigMeta > visita GPS.
   *  Devuelve string ya formateado "lat, lng" con 6 decimales, o null si todas fallan. */
  function resolveCoordenadasSync(): string | null {
    const visitaConCoords = visitas.find((v) => v.lat != null && v.lng != null);
    const latVal =
      pickField('meta_lat')
      ?? (sigMeta?.lat != null ? sigMeta.lat.toFixed(6) : null)
      ?? (visitaConCoords?.lat != null ? visitaConCoords.lat.toFixed(6) : null);
    const lngVal =
      pickField('meta_lng')
      ?? (sigMeta?.lng != null ? sigMeta.lng.toFixed(6) : null)
      ?? (visitaConCoords?.lng != null ? visitaConCoords.lng.toFixed(6) : null);
    return latVal && lngVal ? `${latVal}, ${lngVal}` : null;
  }

  /** Si el DTO no trae coordenadas tras el build sync, intenta el 4to fallback
   *  async leyendo el centroide del GeoJSON de parcelas. Mutación in-place. */
  async function ensureCoordenadasAsync(dto: ReporteSeguimientoData, pid: string): Promise<void> {
    if (dto.coordenadasText) return;
    const centroid = await fetchParcelaCentroid(pid);
    if (centroid) {
      dto.coordenadasText = `${centroid.lat.toFixed(6)}, ${centroid.lng.toFixed(6)}`;
    }
  }

  /** Fallback de coordenadas: lee el GeoJSON de parcelas y devuelve el centro
   *  de bbox de la parcela. Útil cuando el técnico llegó por URL directa
   *  (sigMeta nulo) o refrescó tras seleccionar — en esos casos las coords
   *  del click ya no están en memoria. El navegador cachea el .geojson tras
   *  el primer load (MapaParcelas ya lo descargó). */
  async function fetchParcelaCentroid(pid: string): Promise<{ lat: number; lng: number } | null> {
    try {
      const resp = await fetch('/maps/parcelas_unificado.geojson');
      if (!resp.ok) return null;
      const data = (await resp.json()) as {
        features?: Array<{
          properties?: { parcela_id?: string };
          geometry?: { type: string; coordinates: unknown };
        }>;
      };
      const feat = data.features?.find((f) => f.properties?.parcela_id === pid);
      const geom = feat?.geometry;
      if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) return null;
      // Tomar el primer ring (anillo exterior) del primer polígono.
      const ring: Array<[number, number]> | undefined = geom.type === 'Polygon'
        ? (geom.coordinates as Array<Array<[number, number]>>)[0]
        : (geom.coordinates as Array<Array<Array<[number, number]>>>)[0]?.[0];
      if (!Array.isArray(ring) || ring.length === 0) return null;
      let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
      for (const c of ring) {
        if (!Array.isArray(c) || c.length < 2) continue;
        const [lng, lat] = c;
        if (typeof lng !== 'number' || typeof lat !== 'number') continue;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
      if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
      return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
    } catch {
      return null;
    }
  }

  /** DTO PDF para parcela cargada sin pasar por el modal de éxito (p. ej. tras recarga). */
  function buildPanelPdfDto(): ReporteSeguimientoData | null {
    if (!parcelaId || !parcelaMeta) return null;
    if (!hydrateData.parcela_existe || !hydrateData.gestion) return null;
    const pr = unmaskedData?.propietario;
    const visitaFb = visitas[0]?.narrativa ?? null;
    const docLb = docs.filter((d) => done[d.id]).map((d) => d.label);
    // Build notas from current form state so the PDF reflects unsaved changes.
    // Mirrors the notasObj construction in the save function.
    const notasForPdf: Record<string, unknown> = {
      bono_firma_mxn: bonoFirmaMxn,
      construccion: {
        plazo_anos: plazoConstruccionAnios,
        tarifa_mxn_unidad: tarifaConstruccionMxn,
        cantidad_unidades: cantidadUnidades,
        subtotal_mxn: modeloNegocio.construccion_operacion.subtotal_mxn,
      },
      pagos_diferidos: {
        meses: modeloNegocio.pagos_diferidos.meses,
        monto_mensual_mxn: modeloNegocio.pagos_diferidos.monto_mensual_mxn,
        seguimiento_mensual: modeloNegocio.pagos_diferidos.seguimiento_mensual,
      },
      modelo_negocio_total_mxn: modeloNegocio.totales.suma_componentes_mxn,
      campo: {
        es_el_propietario: esElPropietario || null,
        signatario_nombre: pickField('signatario_nombre'),
        tercero_relacion: pickField('tercero_relacion'),
        calidad_posesionario: pickField('calidad_posesionario'),
        relacion_ejido: pickField('relacion_ejido'),
        liderazgo_ejido: pickField('liderazgo_ejido'),
        acta_nacimiento_folio: pickField('acta_nacimiento_folio'),
      },
      contrato: {
        // modalidad_pago retirado del form 2026-05-09; se sigue
        // aceptando si viene en notas legacy pero ya no se captura desde UI.
        fecha_firma_est: pickField('fecha_firma_est'),
        situacion_legal: pickField('situacion_legal'),
        colindancias: {
          norte: pickField('col_norte'),
          sur: pickField('col_sur'),
          oriente: pickField('col_oriente'),
          poniente: pickField('col_poniente'),
        },
        condiciones: pickField('condiciones'),
        // Misma marca que el payload de submit (notas reales): el PDF
        // refleja la clase de predio aunque aún no se haya guardado.
        tipo_predio: esUsoComun ? 'USO_COMUN' : null,
      },
      gestion: {
        postura_propietario: pickField('postura_propietario'),
        motivo_inviable: estado === 'inviable' ? pickField('motivo_inviable') : null,
      },
      narrativa: {
        descripcion_visita: pickField('descripcion_visita'),
        descripcion_negociacion: pickField('descripcion_negociacion'),
        reqs_area_legal: pickField('reqs_area_legal'),
        observaciones_tecnico: pickField('observaciones_tecnico'),
      },
      tipo_doc: tipoDoc ?? 'ejido',
    };
    // Dedup colindancias after building (spatial algorithm can return same neighbor twice)
    const pdfContrato = notasForPdf.contrato as { colindancias: Record<string, string | null> };
    const dc = pdfContrato?.colindancias;
    if (dc) {
      dc.norte    = dedupeColindancia(dc.norte);
      dc.sur      = dedupeColindancia(dc.sur);
      dc.oriente  = dedupeColindancia(dc.oriente);
      dc.poniente = dedupeColindancia(dc.poniente);
    }
    const rawNotas = JSON.stringify(notasForPdf);
    // Cadena de fallback sync: manual form > sigMeta > visitas con GPS.
    // El bbox del GeoJSON es 4to fallback async (ensureCoordenadasAsync).
    const coordenadasText = resolveCoordenadasSync();
    const riesgoPick = pickField('riesgo');
    const rv: 'bajo' | 'medio' | 'alto' | null =
      riesgoPick === 'bajo' || riesgoPick === 'medio' || riesgoPick === 'alto'
        ? riesgoPick
        : unmaskedData?.gestion?.riesgo ?? hydrateData.gestion?.riesgo ?? null;
    const irrUm = unmaskedData?.irregularidades?.[0]?.descripcion ?? null;
    const irrHy = hydrateData.irregularidades[0]?.descripcion ?? null;
    const irrText = irregularidadDetalle.trim() || irrUm || irrHy || null;
    // Fallback chain para tipo_propi: BFF view → SIG GeoJSON → derivado de tipoDoc.
    // El BFF view a veces devuelve null para parcelas RAN sin clasificación.
    const tipoPropiResolved =
      parcelaMeta.tipo_propi
      ?? (sigMeta?.tipo_propi ?? null)
      ?? (tipoDoc === 'privado' ? 'PROP_PRIV' : 'EJIDO');
    return buildReporteSeguimientoDTO({
      fechaGeneracion: fmtFechaReportePdf(new Date()),
      elaboradoPor: elaboradoPorLabel,
      parcela: {
        parcelaId,
        municipio: parcelaMeta.municipio,
        nomNucleo: parcelaMeta.nom_nucleo ?? '—',
        numParcela: parcelaMeta.num_parcela,
        tipoPropiedadParcela: tipoPropiResolved,
        areaHa:
          parcelaMeta.area_ha != null && Number.isFinite(Number(parcelaMeta.area_ha))
            ? parcelaMeta.area_ha
            : null,
      },
      propietarioNombre:
        pr?.titular ?? pickField('propietario') ?? hydrateData.propietario?.titular_masked ?? null,
      telefono: pr?.telefono ?? pickField('telefono'),
      direccion: pr?.direccion ?? pickField('direccion'),
      folioDerechos: pr?.folio_derechos ?? pickField('folio_derechos'),
      folioTierras: pr?.folio_tierras ?? pickField('folio_tierras'),
      claveCatastral: pr?.clave_catastral ?? pickField('clave_catastral'),
      esElPropietario:
        esElPropietario === 'si' ? true : esElPropietario === 'no' ? false : null,
      signatarioNombre: pickField('signatario_nombre'),
      terceroRelacion: pickField('tercero_relacion'),
      calidadPosesionario: pickField('calidad_posesionario'),
      tipoTenencia: pickField('tipo_propietario'),
      relacionEjido: pickField('relacion_ejido'),
      liderazgoEjido: pickField('liderazgo_ejido'),
      actaNacimientoFolio: pickField('acta_nacimiento_folio'),
      estadoGestion: estado,
      bonoFirmaMxn,
      plazoConstruccionAnios,
      notasRawJson: rawNotas,
      riesgo: rv,
      coordenadasText,
      irregularidadDetalle: irrText,
      visitaNarrativaFallback: visitaFb,
      expedienteDocsCompletadosLabels: docLb,
    });
  }

  async function ejecutarDescargaPdf(dto: ReporteSeguimientoData, filenameParcelaId: string) {
    if (generandoPdf) return;
    setGenerandoPdf(true);
    try {
      const [{ pdf }, { ReporteSeguimientoDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./ReporteSeguimientoDocument'),
      ]);
      const hoy = new Date();
      const fechaStr = fmtFechaReportePdf(hoy);
      const blob = await pdf(
        <ReporteSeguimientoDocument data={{ ...dto, fechaGeneracion: fechaStr }} />,
      ).toBlob();
      const fechaFile = hoy.toISOString().slice(0, 10);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-${filenameParcelaId}-${fechaFile}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast('Reporte descargado.');
      // NO descargamos docs automáticamente — ahora hay un botón aledaño
      // "Descargar documentos" para que el técnico decida cuándo bajar el
      // expediente completo (puede pesar mucho). Ver handleDescargarDocsZip.
    } catch (err) {
      showToast(
        `No se pudo generar el PDF: ${err instanceof Error ? err.message : 'desconocido'}`,
        { variant: 'error' },
      );
    } finally {
      setGenerandoPdf(false);
    }
  }

  /** Estado del botón "Descargar documentos": cuántos archivos hay subidos
   *  para esta parcela. 0 → disabled. 1 → descarga directa. ≥2 → ZIP. */
  const [descargandoDocs, setDescargandoDocs] = useState(false);
  const docsCountForParcela = useMemo(() => {
    if (!parcelaId) return 0;
    return allExpedientes.filter((e) => e.parcela_id === parcelaId).length;
  }, [parcelaId, allExpedientes]);

  /**
   * Descarga inteligente del expediente:
   *   - 0 archivos: botón disabled (no llega aquí).
   *   - 1 archivo: descarga directa, sin ZIP (sin overhead UX).
   *   - ≥2 archivos: empaqueta en ZIP client-side (jszip).
   * Cada signed URL emite audit `expediente.download` (LFPDPPP).
   */
  /**
   * Descarga el GeoJSON actual del predio privado seleccionado. Llama al BFF
   * GET /v1/captura/parcela-privada/:parcela_id/geojson y dispara una bajada
   * de archivo en el browser. Útil como respaldo antes de reemplazar el
   * polígono.
   */
  async function handleDescargarGeoJSONPrivada() {
    if (descargandoGeoJSON || !parcelaId) return;
    setDescargandoGeoJSON(true);
    try {
      const feature = await bffFetch<GeoJSON.Feature>(
        `/v1/captura/parcela-privada/${encodeURIComponent(parcelaId)}/geojson`,
      );
      const blob = new Blob([JSON.stringify(feature, null, 2)], { type: 'application/geo+json' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${parcelaId}.geojson`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      showToast('GeoJSON descargado.', { variant: 'success' });
    } catch (e) {
      const msg = e instanceof BffError
        ? `No se pudo descargar: ${e.message}`
        : `Error inesperado: ${e instanceof Error ? e.message : String(e)}`;
      showToast(msg, { variant: 'error' });
    } finally {
      setDescargandoGeoJSON(false);
    }
  }

  /**
   * Eliminación TOTAL del predio privado. Llama al BFF DELETE que dispara
   * la RPC captura_delete_parcela_privada (mig 0083): cascade en gestión,
   * propietario exclusivo, irregularidades, expedientes DB rows, visitas,
   * comentarios, parcelas proxy, parcelas_privadas. R2 cleanup en waitUntil.
   *
   * Solo se llama después de que el modal de confirmación valide que el
   * usuario escribió el parcela_id literal — no hay confirmación adicional
   * acá. Si la gestión está `contratada`, el RPC devuelve 409 con
   * 'parcela_privada_has_contract' y el catch muestra copy específico.
   */
  async function handleEliminarPrivada() {
    if (eliminandoPrivada || !parcelaId) return;
    setEliminandoPrivada(true);
    try {
      const response = await bffFetch<{
        parcela_id: string;
        deleted: { gestiones: number; irregularidades: number; visitas: number; comentarios: number; expedientes: number; propietario: boolean };
        r2_files_attempted: number;
      }>(
        `/v1/captura/parcela-privada/${encodeURIComponent(parcelaId)}`,
        { method: 'DELETE' },
      );
      const d = response.deleted;
      const partes = [
        d.expedientes ? `${d.expedientes} doc(s)` : null,
        d.visitas ? `${d.visitas} visita(s)` : null,
        d.comentarios ? `${d.comentarios} comentario(s)` : null,
        d.irregularidades ? `${d.irregularidades} irregularidad(es)` : null,
        d.propietario ? 'propietario' : null,
      ].filter(Boolean);
      const summary = partes.length > 0 ? ` (borrado: ${partes.join(', ')})` : '';
      showToast(`Predio ${response.parcela_id} eliminado${summary}.`, { variant: 'success' });
      // Limpia el wizard y refresca el cache de privadas para que el visor
      // deje de pintar la geometría borrada.
      setShowDeletePrivadaUI(false);
      setDeleteConfirmText('');
      setParcelaId(undefined);
      setSigMeta(null);
      void refetchPrivadas();
    } catch (e) {
      if (e instanceof BffError) {
        const detailObj = typeof e.detail === 'object' && e.detail !== null ? (e.detail as Record<string, unknown>) : {};
        const code = typeof detailObj.error === 'string' ? detailObj.error : '';
        const detailStr = typeof detailObj.detail === 'string' ? detailObj.detail : '';
        let copy: string;
        if (code === 'parcela_privada_has_contract') {
          copy = 'Esta parcela tiene contrato firmado. Marca primero la gestión como inviable.';
        } else if (code === 'parcela_privada_not_found') {
          copy = 'El predio ya no existe.';
        } else {
          // Fallback genérico: exponer el detail del servidor (mensaje
          // PL/pgSQL con SQLSTATE) para que el técnico tenga algo que
          // reportar — sin esto solo veíamos 'parcela_privada_delete_failed'
          // sin contexto.
          copy = detailStr
            ? `No se pudo eliminar (${code || 'error'}): ${detailStr}`
            : `No se pudo eliminar: ${e.message}`;
        }
        showToast(copy, { variant: 'error' });
      } else {
        showToast(`Error inesperado: ${e instanceof Error ? e.message : String(e)}`, { variant: 'error' });
      }
    } finally {
      setEliminandoPrivada(false);
    }
  }

  async function handleDescargarDocsZip() {
    if (descargandoDocs || !parcelaId) return;
    if (!isSupabaseConfigured()) return;
    setDescargandoDocs(true);
    try {
      const sb = getSupabase();
      const { data: rows, error: qErr } = await sb
        .from('expedientes')
        .select('r2_key, filename, parcela:parcelas(parcela_id)')
        .order('created_at', { ascending: true });
      if (qErr) {
        showToast(`Error consultando expedientes: ${qErr.message}`, { variant: 'error' });
        return;
      }
      type ExpRow = { r2_key: string; filename: string | null; parcela: { parcela_id: string | null } | { parcela_id: string | null }[] | null };
      const filtered = (rows as unknown as ExpRow[] | null ?? []).flatMap((r) => {
        const par = Array.isArray(r.parcela) ? r.parcela[0] : r.parcela;
        return par?.parcela_id === parcelaId ? [r] : [];
      });
      if (filtered.length === 0) {
        showToast('Esta parcela no tiene documentos subidos.', { variant: 'info' });
        return;
      }

      const fechaFile = new Date().toISOString().slice(0, 10);

      // Caso N=1: descarga directa.
      if (filtered.length === 1) {
        const only = filtered[0];
        if (!only) return;
        const { url } = await bffFetch<{ url: string; expires_at: string }>(
          '/v1/download/url',
          { method: 'POST', body: { r2_key: only.r2_key } },
        );
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`fetch ${resp.status}`);
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const slash = only.r2_key.lastIndexOf('/');
        const fname = only.filename ?? (slash >= 0 ? only.r2_key.slice(slash + 1) : only.r2_key);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        showToast('Documento descargado.', { variant: 'success' });
        return;
      }

      // Caso N≥2: ZIP client-side.
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      let okCount = 0;
      let failCount = 0;
      for (const r of filtered) {
        try {
          const { url } = await bffFetch<{ url: string; expires_at: string }>(
            '/v1/download/url',
            { method: 'POST', body: { r2_key: r.r2_key } },
          );
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(`fetch ${resp.status}`);
          const blob = await resp.blob();
          const slash = r.r2_key.lastIndexOf('/');
          const fname = r.filename ?? (slash >= 0 ? r.r2_key.slice(slash + 1) : r.r2_key);
          zip.file(fname, blob);
          okCount++;
        } catch (e) {
          failCount++;
          console.warn('[zip] doc failed', r.r2_key, e);
        }
      }
      if (okCount === 0) {
        showToast('No se pudo descargar ningún documento.', { variant: 'error' });
        return;
      }
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const zipUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `documentos-${parcelaId}-${fechaFile}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(zipUrl), 1000);
      showToast(
        failCount > 0
          ? `${okCount} documento(s) en ZIP · ${failCount} falló(aron)`
          : `${okCount} documento(s) descargados en ZIP`,
        { variant: failCount > 0 ? 'error' : 'success' },
      );
    } catch (e) {
      showToast(
        `Error: ${e instanceof Error ? e.message : 'desconocido'}`,
        { variant: 'error' },
      );
    } finally {
      setDescargandoDocs(false);
    }
  }

  async function handleDescargarReportePanel() {
    if (!parcelaId) return;
    const dto = buildPanelPdfDto();
    if (!dto) {
      showToast('Aún no hay una gestión guardada para esta parcela.', { variant: 'error' });
      return;
    }
    await ensureCoordenadasAsync(dto, parcelaId);
    await ejecutarDescargaPdf(dto, parcelaId);
  }

  async function handleDescargarReporte() {
    if (!successInfo) return;
    // El DTO se construyó al guardar (sync fallbacks aplicados), pero por si
    // todas las fuentes sync estaban vacías, intentamos el async GeoJSON.
    await ensureCoordenadasAsync(successInfo.reportePdf, successInfo.parcelaId);
    await ejecutarDescargaPdf(successInfo.reportePdf, successInfo.parcelaId);
  }

  return (
    <div className="captura-split" data-step={step}>

      {/* Modal MFA — se abre cuando el BFF responde 403 mfa_required al
          hidratar PII. Tras success, refetch del endpoint y la PII llena el
          form. Es el único punto del wizard que requiere TOTP reciente —
          el POST de captura usa el access token sin AAL2 prerequisite. */}
      {showMfaModal && (
        <MfaUnlockModal onSuccess={onMfaSuccess} onCancel={onMfaCancel} />
      )}

      {/* Modal de confirmación al cambiar tipo de predio con datos en juego
          (handoff parcelas privadas v2 Fase 1.5). Aparece solo cuando
          requestTipoDocChange detecta step>1, archivos en docFiles, o
          parcela seleccionada. El cambio borra todo el progreso del paso 1
          (parcela + sigMeta + datos económicos) y los archivos del paso 3. */}
      {pendingTipoDocChange !== null && (
        <div
          className="tipodoc-confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tipodoc-confirm-title"
          onClick={cancelTipoDocChange}
        >
          <div
            className="tipodoc-confirm-modal glass-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="tipodoc-confirm-title" className="tipodoc-confirm-title">
              ¿Cambiar tipo de predio a{' '}
              <strong>{pendingTipoDocChange === 'ejido' ? 'Ejidal' : 'Privado'}</strong>?
            </h3>
            <p className="tipodoc-confirm-body">
              Vas a borrar la parcela seleccionada, los datos económicos y los
              documentos subidos hasta ahora. El nuevo flujo del paso 1 es
              distinto y los datos actuales no aplicarían.
            </p>
            <p className="tipodoc-confirm-hint">
              El borrador autoguardado se reiniciará. Esta acción no se puede
              deshacer.
            </p>
            <div className="tipodoc-confirm-actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={cancelTipoDocChange}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmTipoDocChange}
              >
                Sí, cambiar y reiniciar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mapa ─────────────────────────────────────────────── */}
      <div className="captura-map" data-parcela={parcelaId ?? ''}>
        <div className="captura-map-area-filtro glass-card" aria-label="Filtro área mapa">
          <FilterGroup<AreaMapaCaptura>
            label="Área mapa"
            options={[
              { value: 'all',         label: 'Todos' },
              { value: 'san_pedro',   label: 'San Pedro' },
              { value: 'HUIMILPAN',   label: 'Huimilpan' },
              { value: 'CORREGIDORA', label: 'Corregidora' },
            ]}
            active={areaMapaCaptura}
            onChange={setAreaMapaCaptura}
            variant="strong"
          />
        </div>
        <MapaParcelas
          modo="edicion"
          capas={CAPAS_DEMO_FULL}
          filtros={filtrosCapturaMapa}
          onParcelaClick={handleParcelaClick}
          selectedParcelaId={parcelaId}
          parcelasState={parcelasState}
          parcelasGeoJSON={parcelasGeoJSONMerged}
          usoComunGeoJSON={usoComunGeoJSONMerged}
          capasInformativas={capasInformativas}
        />
        {!parcelaId && (
          <div className="map-prompt">
            <div className="map-prompt-pill">
              <span className="map-prompt-dot" />
              Toca una parcela para comenzar
            </div>
          </div>
        )}
      </div>

      {/* ── Panel del formulario ──────────────────────────────── */}
      <aside className="captura-form glass-panel">

        <header className="form-header">
          <div>
            <h2>
              {parcelaId
                ? tituloParcelaConTitular(
                    parcelaId,
                    parcelaMeta?.num_parcela ?? sigMeta?.num_parcela,
                    // Prioridad: cleartext post-unmask (AAL2 fresco) > masked
                    // del inventario. Sin este fallback el header mostraba
                    // "J. F. A." aunque el técnico ya hubiera pasado MFA y
                    // unmaskedData tuviera el nombre completo cargado.
                    // Bug Diego 2026-05-11.
                    unmaskedData?.propietario?.titular ?? parcelaMeta?.titular_masked,
                  )
                : 'Nuevo reporte'}
            </h2>
            <p className="form-sub">
              {parcelasErr && (
                <span style={{ display: 'block', color: '#c96', marginBottom: 6 }}>
                  Sin datos vivos de parcela: {parcelasErr.message}
                </span>
              )}
              {parcelaId
                ? parcelaMeta
                  ? `${parcelaMeta.nom_nucleo ?? sigMeta?.nom_nucleo ?? '—'} · ${
                      parcelaMeta.area_ha != null && Number.isFinite(Number(parcelaMeta.area_ha))
                        ? `${Number(parcelaMeta.area_ha).toFixed(2)} ha`
                        : sigMeta?.area_ha != null && Number.isFinite(Number(sigMeta.area_ha))
                          ? `${Number(sigMeta.area_ha).toFixed(2)} ha`
                          : '—'
                    } · anillo ${String(parcelaMeta.anillo ?? (sigMeta?.raw?.anillo as string | undefined) ?? '—')} · ${GESTOR_MOCK} · ${TODAY}`
                  : parcelasLoading
                    ? 'Cargando ficha desde inventario…'
                    : sigMeta?.nom_nucleo
                      ? `${sigMeta.nom_nucleo} · ${typeof sigMeta.area_ha === 'number' ? sigMeta.area_ha.toFixed(2) + ' ha' : ''}`.trimEnd() +
                        ` · ${GESTOR_MOCK} · ${TODAY}`
                    : `${GESTOR_MOCK} · ${TODAY} · (solo geometría en mapa)`
                : 'Selecciona una parcela en el mapa'}
            </p>
            {/* Pill "Borrador guardado" — feedback de que el sandbox local
                está activo. Solo aparece tras el primer auto-save (cuando
                draftSavedAt != null). Discreto, no compite con el badge de
                estado. Comunica: "puedes irte sin guardar, no se pierde". */}
            {draftSavedAt != null && (
              <span
                title="Tu progreso se guarda localmente cada vez que editas. Solo se publica al cliente cuando presionas Guardar reporte."
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  marginTop: 6,
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'rgba(180, 201, 111, 0.12)',
                  color: 'var(--btn-primary-bg, #b4c96f)',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                }}
              >
                <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Borrador guardado · solo en este navegador hasta que presiones Guardar
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
            <EstadoBadge value={estado} />
            {parcelaId && hydrateData.parcela_existe && hydrateData.gestion ? (
              <button
                type="button"
                className="btn-pdf-download"
                disabled={generandoPdf || hydrateLoading}
                title="PDF con datos de gestión en Supabase; PII igual que GET /v1/captura (MFA cuando aplique). Sin descifrado muestra titular enmascarado."
                onClick={() => { void handleDescargarReportePanel(); }}
              >
                {generandoPdf ? (
                  <span className="export-modal-spinner" style={{ width: 13, height: 13 }} />
                ) : (
                  <svg viewBox="0 0 16 16" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M2 11v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2" />
                    <polyline points="5,7 8,10 11,7" />
                    <line x1="8" y1="2" x2="8" y2="10" />
                  </svg>
                )}
                {generandoPdf ? 'Generando…' : 'Descargar PDF'}
              </button>
            ) : null}
            {parcelaId ? (
              <button
                type="button"
                className="btn-pdf-download"
                disabled={descargandoDocs || docsCountForParcela === 0}
                style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                title={
                  docsCountForParcela === 0 ? 'Sin documentos subidos al expediente'
                  : docsCountForParcela === 1 ? 'Descargar el documento subido'
                  : `Empaquetar y descargar ${docsCountForParcela} documentos en .zip`
                }
                onClick={() => { void handleDescargarDocsZip(); }}
              >
                {descargandoDocs ? (
                  <span className="export-modal-spinner" style={{ width: 13, height: 13 }} />
                ) : (
                  <svg viewBox="0 0 16 16" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M3 2h7l3 3v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
                    <line x1="6" y1="9" x2="10" y2="9" />
                    <line x1="6" y1="6" x2="8" y2="6" />
                    <line x1="6" y1="12" x2="10" y2="12" />
                  </svg>
                )}
                {descargandoDocs
                  ? 'Preparando…'
                  : docsCountForParcela === 0
                    ? 'Sin documentos'
                    : docsCountForParcela === 1
                      ? 'Descargar documento'
                      : `Descargar docs (${docsCountForParcela}) · ZIP`}
              </button>
            ) : null}
          </div>
        </header>

        {/* Barra de progreso */}
        <div className="wizard-bar">
          {STEP_LABELS.map((label, i) => {
            const n = (i + 1) as WizardStep;
            const isActive = step === n;
            const isDone   = step > n;
            return (
              <div key={n} className={`wizard-step${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}>
                <div className="wizard-step-dot">{isDone ? '✓' : n}</div>
                <span className="wizard-step-label">{label}</span>
                {i < STEP_LABELS.length - 1 && <div className="wizard-connector" />}
              </div>
            );
          })}
        </div>

        <form ref={formRef} className="form-content" onSubmit={onSubmit} noValidate>
          <div className="form-body">

            {/* ══ PASO 1 · Propietario ══════════════════════════ */}
            {step === 1 && (
              <>
                {/* Gate "tipo predio primero" (handoff 2026-05-10 Fase 1):
                    el técnico elige ejido/privado ANTES de ver el selector
                    de parcela. Fork A (ejido) → selector RAN existente.
                    Fork B (privado) → placeholder hasta Fase 4 (dropzone
                    GeoJSON). Default 'ejido' se mantiene (decisión §0.2)
                    para no agregar un click en el 99% de casos. */}
                <fieldset className="form-section">
                  <legend>Tipo de predio</legend>
                  <div className="tipo-predio-grid">
                    <button
                      type="button"
                      className={`tipo-predio-card${tipoDoc === 'ejido' ? ' is-selected' : ''}`}
                      onClick={() => requestTipoDocChange('ejido')}
                      aria-pressed={tipoDoc === 'ejido'}
                      disabled={parcelaId !== undefined}
                      title={parcelaId !== undefined
                        ? 'El tipo está determinado por la parcela seleccionada. Cambia la parcela para usar otro tipo.'
                        : undefined}
                    >
                      <span className="tipo-predio-card-icon" aria-hidden>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 21h18M3 21V8l9-5 9 5v13M9 21v-7h6v7" />
                        </svg>
                      </span>
                      <span className="tipo-predio-card-label">Ejidal</span>
                      <span className="tipo-predio-card-hint">{DOCS_EJIDAL.length} documentos requeridos</span>
                    </button>
                    <button
                      type="button"
                      className={`tipo-predio-card${tipoDoc === 'privado' ? ' is-selected' : ''}`}
                      onClick={() => requestTipoDocChange('privado')}
                      aria-pressed={tipoDoc === 'privado'}
                      disabled={parcelaId !== undefined}
                      title={parcelaId !== undefined
                        ? 'El tipo está determinado por la parcela seleccionada. Cambia la parcela para usar otro tipo.'
                        : undefined}
                    >
                      <span className="tipo-predio-card-icon" aria-hidden>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 12L12 4l9 8M5 10v10h14V10" />
                        </svg>
                      </span>
                      <span className="tipo-predio-card-label">Privado</span>
                      <span className="tipo-predio-card-hint">{DOCS_PRIVADA.length} documentos requeridos</span>
                    </button>
                  </div>
                  <p className="tipo-predio-note">
                    {parcelaId === undefined && tipoDoc === null
                      ? 'Elige el tipo de predio para continuar. Define los documentos solicitados en el paso 3.'
                      : 'El tipo define los documentos solicitados en el paso 3. Cámbialo antes de llenar los datos para evitar perderlos.'}
                  </p>
                </fieldset>

                {/* Fork B: Privado — dropzone GeoJSON (Fase 4 handoff v2).
                    POST a /v1/captura/parcela-privada → genera PRV-ELA-#####.
                    onParcelaCreated setea parcelaId y refetch invalida el
                    cache de useParcelasTecnicas para que el resto del
                    wizard pueda leer la parcela recién creada. */}
                {tipoDoc === 'privado' && !parcelaId && (
                  <UploadParcelaPrivada
                    onParcelaCreated={(meta) => {
                      setParcelaId(meta.parcelaId);
                      // Setear sigMeta sintético para autocompletar el wizard
                      // (superficie en Términos económicos, núcleo en header).
                      setSigMeta({
                        parcela_id: meta.parcelaId,
                        num_parcela: meta.numParcela ?? undefined,
                        nom_nucleo: meta.nomNucleo ?? undefined,
                        municipio: meta.municipio,
                        area_ha: meta.areaHa,
                        tipo_parcela: 'PRIVADA',
                        tipo_propi: 'PROP_PRIV',
                        lng: meta.lng,
                        lat: meta.lat,
                        raw: { source: 'cliente' },
                      });
                      refetchParcelas();
                      // Refetch del cache de privadas (ttl 30s) para que la
                      // nueva aparezca en el mapa inmediatamente con border
                      // dashed teal — Fix 2 Fase 5.
                      void refetchPrivadas();
                    }}
                  />
                )}
                {tipoDoc === 'privado' && parcelaId && !showReplacePrivadaUI && (
                  <div className="parcela-selector glass-panel">
                    <div className="parcela-chip">
                      <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <div className="parcela-chip-text">
                        <span className="parcela-chip-num">Predio privado cargado</span>
                        <span className="parcela-chip-id">{parcelaId}</span>
                      </div>
                      <button
                        type="button"
                        className="parcela-chip-clear"
                        onClick={() => {
                          setParcelaId(undefined);
                          setSigMeta(null);
                        }}
                        title="Quitar este predio (no lo borra del sistema)"
                      >×</button>
                    </div>
                    {/* Acciones sobre el polígono guardado. Descargar baja
                        el GeoJSON actual como respaldo. Reemplazar abre la
                        dropzone en modo PUT preservando propietario/gestión. */}
                    <div
                      className="parcela-chip-actions"
                      style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}
                    >
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled={descargandoGeoJSON}
                        onClick={() => { void handleDescargarGeoJSONPrivada(); }}
                        title="Descargar el GeoJSON actual del predio"
                        style={{ fontSize: 12, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {descargandoGeoJSON ? 'Descargando…' : 'Descargar GeoJSON'}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setShowReplacePrivadaUI(true)}
                        title="Cargar un GeoJSON nuevo y reemplazar el polígono actual"
                        style={{ fontSize: 12, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <polyline points="23 4 23 10 17 10" />
                          <polyline points="1 20 1 14 7 14" />
                          <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                        </svg>
                        Reemplazar polígono
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteConfirmText('');
                          setShowDeletePrivadaUI(true);
                        }}
                        title="Eliminar el predio TOTAL — borra gestión, propietario exclusivo, docs, visitas y polígono. No se puede deshacer."
                        style={{
                          fontSize: 12, padding: '4px 10px',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: 'rgba(220, 80, 60, 0.10)',
                          border: '1px solid rgba(220, 80, 60, 0.32)',
                          borderRadius: 8,
                          color: '#e87766',
                          cursor: 'pointer',
                        }}
                      >
                        <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                        Eliminar predio
                      </button>
                    </div>
                    {/* Modal inline de confirmación (no overlay flotante para
                        mantener al técnico en el contexto del wizard).
                        Bloquea el botón final si gestion.estado='contratada'
                        o si el texto escrito no coincide con el parcela_id. */}
                    {showDeletePrivadaUI && (
                      <div
                        className="upload-pp-msg upload-pp-msg--error"
                        style={{
                          marginTop: 10,
                          padding: '12px 14px',
                          background: 'rgba(220, 80, 60, 0.10)',
                          border: '1px solid rgba(220, 80, 60, 0.32)',
                          borderRadius: 8,
                          color: '#e87766',
                          fontSize: 12.5,
                          lineHeight: 1.5,
                        }}
                      >
                        <strong style={{ display: 'block', marginBottom: 4 }}>
                          Eliminar {parcelaId} de forma permanente
                        </strong>
                        <span style={{ display: 'block', marginBottom: 6 }}>
                          Se borrará el polígono, la gestión, los documentos del
                          expediente, las visitas, los comentarios y el propietario
                          (si está vinculado solo a este predio). Esta acción NO
                          se puede deshacer.
                        </span>
                        {estado === 'contratada' && (
                          <div
                            style={{
                              padding: '8px 10px',
                              marginBottom: 8,
                              background: 'rgba(218, 165, 32, 0.14)',
                              border: '1px solid rgba(218, 165, 32, 0.4)',
                              borderRadius: 6,
                              color: '#d4a017',
                            }}
                          >
                            ⚠️ Esta parcela tiene gestión <strong>contratada</strong>.
                            Marca la gestión como <em>inviable</em> antes de poder eliminarla.
                          </div>
                        )}
                        <label style={{ display: 'block', marginBottom: 8 }}>
                          <span style={{ display: 'block', marginBottom: 4, fontSize: 11.5, color: 'rgba(242,244,237,0.7)' }}>
                            Escribe <strong>{parcelaId}</strong> para confirmar:
                          </span>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder={parcelaId}
                            disabled={eliminandoPrivada}
                            style={{ width: '100%' }}
                            autoFocus
                          />
                        </label>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={eliminandoPrivada}
                            onClick={() => {
                              setShowDeletePrivadaUI(false);
                              setDeleteConfirmText('');
                            }}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            disabled={
                              eliminandoPrivada
                              || estado === 'contratada'
                              || deleteConfirmText.trim() !== parcelaId
                            }
                            onClick={() => { void handleEliminarPrivada(); }}
                            style={{
                              padding: '7px 14px',
                              background: '#e87766',
                              color: '#1b1f23',
                              border: 'none',
                              borderRadius: 8,
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              opacity: (eliminandoPrivada || estado === 'contratada' || deleteConfirmText.trim() !== parcelaId) ? 0.5 : 1,
                            }}
                          >
                            {eliminandoPrivada ? 'Eliminando…' : 'Eliminar definitivamente'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {tipoDoc === 'privado' && parcelaId && showReplacePrivadaUI && (
                  <UploadParcelaPrivada
                    // El callback de create no se usa en replace pero React
                    // requiere la prop. Lo dejamos como no-op.
                    onParcelaCreated={() => { /* no-op en replace */ }}
                    mode={{
                      kind: 'replace',
                      parcelaId,
                      areaHaPrevia: sigMeta?.area_ha,
                      onCancel: () => setShowReplacePrivadaUI(false),
                      onParcelaReplaced: (meta) => {
                        // Refresca sigMeta con la nueva area_ha + centroide
                        // sin tocar municipio/núcleo/num (no cambiaron).
                        setSigMeta((prev) => ({
                          ...(prev ?? {}),
                          parcela_id: meta.parcelaId,
                          area_ha: meta.areaHa,
                          lng: meta.lng,
                          lat: meta.lat,
                          tipo_parcela: 'PRIVADA',
                          tipo_propi: 'PROP_PRIV',
                          raw: { ...(prev?.raw ?? {}), source: 'cliente' },
                        }));
                        setShowReplacePrivadaUI(false);
                        showToast(
                          `Polígono reemplazado. Área nueva: ${meta.areaHa.toFixed(2)} ha (antes ${meta.areaHaPrevious.toFixed(2)} ha).`,
                          { variant: 'success' },
                        );
                        // Refresca el cache de privadas para que el mapa
                        // del visor pinte la geometría nueva inmediatamente.
                        void refetchPrivadas();
                      },
                    }}
                  />
                )}

                {/* Fork A: Ejidal — selector RAN existente, sin cambios. */}
                {tipoDoc === 'ejido' && (
                /* ── Selector de parcela ────────────────────────── */
                <div className="parcela-selector glass-panel">
                  {parcelaId ? (
                    /* Parcela ya seleccionada — chip confirmado */
                    <div className="parcela-chip">
                      <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <div className="parcela-chip-text">
                        <span className="parcela-chip-num">
                          {tituloParcelaConTitular(
                            parcelaId,
                            parcelaMeta?.num_parcela ?? sigMeta?.num_parcela,
                            // Mismo fallback unmasked > masked que el h2 del
                            // header (línea ~2487). Mantener ambos sincronizados.
                            unmaskedData?.propietario?.titular ?? parcelaMeta?.titular_masked,
                          )}
                        </span>
                        <span className="parcela-chip-id">{parcelaId}</span>
                      </div>
                      <button
                        type="button"
                        className="parcela-chip-clear"
                        onClick={() => {
                          setParcelaId(undefined);
                          setSigMeta(null);
                        }}
                        title="Cambiar parcela"
                      >×</button>
                    </div>
                  ) : (
                    /* Sin parcela — mostrar opciones de selección */
                    <>
                      <div className="parcela-sel-tabs">
                        <button type="button" className={`psel-tab${selMode === 'mapa'   ? ' is-active' : ''}`} onClick={() => setSelMode('mapa')}>
                          Desde el mapa
                        </button>
                        <button type="button" className={`psel-tab${selMode === 'id'     ? ' is-active' : ''}`} onClick={() => setSelMode('id')}>
                          Número o ID
                        </button>
                        <button type="button" className={`psel-tab${selMode === 'manual' ? ' is-active' : ''}`} onClick={() => setSelMode('manual')}>
                          Datos manuales
                        </button>
                      </div>

                      {selMode === 'mapa' && (
                        <p className="psel-hint">
                          <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="3"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2"/>
                          </svg>
                          Toca una parcela en el mapa de la izquierda para seleccionarla.
                        </p>
                      )}

                      {selMode === 'id' && (
                        <div className="psel-id-block">
                          {/* Selector de ejido — desambigua "70" cuando el número
                              existe en varios núcleos. 14 ejidos en el dataset
                              Elawan; <select> nativo es suficientemente usable
                              y soporta navegación por teclado (tipea-para-saltar). */}
                          <label className="psel-ejido-label">
                            <span>Ejido / núcleo (opcional)</span>
                            <select
                              className="psel-ejido-select"
                              value={ejidoFilter ? `${ejidoFilter.municipio}::${ejidoFilter.nom_nucleo}` : ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (!v) {
                                  setEjidoFilter(null);
                                } else {
                                  const sep = v.indexOf('::');
                                  if (sep > 0) {
                                    setEjidoFilter({
                                      municipio: v.slice(0, sep),
                                      nom_nucleo: v.slice(sep + 2),
                                    });
                                  }
                                }
                                // Cualquier cambio de filtro invalida los candidatos
                                // mostrados — el contexto de búsqueda cambió.
                                setAmbiguousCandidates([]);
                              }}
                            >
                              <option value="">— Sin filtro —</option>
                              {ejidos.map((ej) => (
                                <option
                                  key={`${ej.municipio}::${ej.nom_nucleo}`}
                                  value={`${ej.municipio}::${ej.nom_nucleo}`}
                                >
                                  {ej.nom_nucleo} · {ej.municipio} ({ej.count})
                                </option>
                              ))}
                            </select>
                          </label>

                          {/* Chip dismissible cuando hay filtro activo — feedback
                              inmediato de que el resolver va a restringir la búsqueda. */}
                          {ejidoFilter && (
                            <div className="psel-ejido-chip" role="status">
                              <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 8l9-5 9 5v8l-9 5-9-5V8z"/>
                              </svg>
                              <span>Buscando solo en <strong>{ejidoFilter.nom_nucleo}</strong></span>
                              <button
                                type="button"
                                className="psel-ejido-chip-clear"
                                onClick={() => {
                                  setEjidoFilter(null);
                                  setAmbiguousCandidates([]);
                                }}
                                title="Quitar filtro de ejido"
                                aria-label="Quitar filtro de ejido"
                              >×</button>
                            </div>
                          )}

                          <div className="psel-id-row">
                            <input
                              className="psel-id-input"
                              placeholder={ejidoFilter ? `Ej: 70 (en ${ejidoFilter.nom_nucleo})` : 'Ej: 70 · HUM-P0124'}
                              value={idInput}
                              onChange={(e) => {
                                setIdInput(e.target.value.toUpperCase());
                                // No auto-resolver mientras tipea — solo limpiar la
                                // lista de ambigüedades viejas; el resolve se
                                // dispara en Enter/Confirmar.
                                if (ambiguousCandidates.length > 0) setAmbiguousCandidates([]);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleConfirmIdInput();
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn-primary psel-id-btn"
                              disabled={!idInput.trim()}
                              onClick={handleConfirmIdInput}
                            >
                              Confirmar
                            </button>
                          </div>

                          {/* Lista de candidatos cuando el resolver devolvió `ambiguous`.
                              Cubre la única colisión interna real del RAN
                              (PURISIMA · 47 → COR-P0586 + COR-P0587) y el caso
                              "el técnico no eligió ejido y el número se repite
                              entre núcleos". Click selecciona el parcela_id. */}
                          {ambiguousCandidates.length > 0 && (
                            <div className="psel-amb-block" role="region" aria-label="Resultados ambiguos">
                              <p className="psel-amb-hint">
                                Hay {ambiguousCandidates.length} parcelas con ese número.
                                Selecciona la correcta:
                              </p>
                              <ul className="psel-amb-list">
                                {ambiguousCandidates.map((c) => (
                                  <li key={c.parcela_id}>
                                    <button
                                      type="button"
                                      className="psel-amb-item"
                                      onClick={() => {
                                        const guard = evaluateParcelaClickGuard({
                                          currentTipoDoc: tipoDoc,
                                          clickedParcelaId: c.parcela_id,
                                        });
                                        if (guard.kind === 'reject') {
                                          showToast(guard.reason, { variant: 'error' });
                                          return;
                                        }
                                        setParcelaId(c.parcela_id);
                                        setIdInput('');
                                        setAmbiguousCandidates([]);
                                      }}
                                    >
                                      <span className="psel-amb-num">{tituloParcelaCampo(c.parcela_id, c.num_parcela)}</span>
                                      <span className="psel-amb-meta">
                                        {c.nom_nucleo} · {c.municipio} · {c.parcela_id}
                                      </span>
                                      <span className="psel-amb-extra">
                                        {c.area_ha.toFixed(2)} ha · {c.titular_masked ?? '— sin titular'}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {selMode === 'manual' && (
                        <div className="psel-meta-form">
                          <div className="form-row">
                            <label>
                              <span>Municipio</span>
                              <select name="meta_municipio" {...bindInput('meta_municipio')}>
                                <option value="">— selecciona —</option>
                                <option value="huimilpan">Huimilpan</option>
                                <option value="corregidora">Corregidora</option>
                              </select>
                            </label>
                            <label>
                              <span>Ejido / núcleo agrario</span>
                              <input name="meta_ejido" {...bindInput('meta_ejido')} placeholder="Nombre del ejido o comunidad" />
                            </label>
                          </div>
                          <div className="form-row">
                            <label>
                              <span>Folio catastral / referencia</span>
                              <input name="meta_folio" {...bindInput('meta_folio')} placeholder="Folio del registro catastral" />
                            </label>
                            <label>
                              <span>Superficie aproximada (ha)</span>
                              <input name="meta_sup" {...bindInput('meta_sup')} type="number" min={0} step={0.01} placeholder="0.00" />
                            </label>
                          </div>
                          <div className="form-row">
                            <label>
                              <span>Latitud</span>
                              <input name="meta_lat" {...bindInput('meta_lat')} type="number" step="any" placeholder="20.xxxxx" />
                            </label>
                            <label>
                              <span>Longitud</span>
                              <input name="meta_lng" {...bindInput('meta_lng')} type="number" step="any" placeholder="-100.xxxxx" />
                            </label>
                          </div>
                          <button
                            type="button"
                            className="btn-primary psel-meta-confirm"
                            onClick={() => {
                              // Lectura desde state (sobrevive unmount); antes hacía
                              // querySelector que fallaba si los inputs estaban
                              // remontándose o el user había navegado.
                              const folio = (formData.meta_folio ?? '').trim();
                              const ejido = (formData.meta_ejido ?? '').trim();
                              const ref = folio || ejido || 'MANUAL';
                              setParcelaId(`REF-${ref.slice(0, 12).toUpperCase()}`);
                            }}
                          >
                            Usar estos datos
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}

                {/* Metadata del SIG (al click sobre parcela en mapa).
                    Se muestra solo cuando hay seleccion y la fuente de los
                    datos es el geojson; los campos siguen siendo manuales. */}
                {sigMeta && (
                  <div className="sig-meta-panel" aria-label="Metadatos SIG del predio">
                    <div className="sig-meta-head">
                      <span className="sig-meta-kicker">Metadatos del predio · SIG</span>
                    </div>
                    <div className="sig-meta-grid">
                      {parcelaId ? (
                        <div className="sig-meta-cell">
                          <span className="sig-meta-k">Número de parcela</span>
                          <strong>
                            {numParcelaCampo(parcelaId, parcelaMeta?.num_parcela ?? sigMeta?.num_parcela)}
                          </strong>
                        </div>
                      ) : null}
                      <div className="sig-meta-cell">
                        <span className="sig-meta-k">Área</span>
                        <strong>
                          {typeof sigMeta.area_ha === 'number' ? `${sigMeta.area_ha.toFixed(2)} ha` : '—'}
                        </strong>
                      </div>
                      <div className="sig-meta-cell">
                        <span className="sig-meta-k">Polígono</span>
                        <strong>{fmtSigPoligono(sigMeta.poligono)}</strong>
                      </div>
                      <div className="sig-meta-cell">
                        <span className="sig-meta-k">Zona</span>
                        <strong>{sigMeta.zona != null ? `Z${sigMeta.zona}` : '—'}</strong>
                      </div>
                      <div className="sig-meta-cell">
                        <span className="sig-meta-k">Núcleo</span>
                        <strong>{sigMeta.nom_nucleo ?? '—'}</strong>
                      </div>
                      <div className="sig-meta-cell">
                        <span className="sig-meta-k">Municipio</span>
                        <strong>{sigMeta.municipio ?? '—'}</strong>
                      </div>
                      <div className="sig-meta-cell">
                        <span className="sig-meta-k">Tipo</span>
                        <strong>{sigMeta.tipo_parcela ?? '—'}</strong>
                      </div>
                      {/* Coordenadas: punto donde el técnico hizo click sobre
                          la parcela (lng/lat capturados por MapaParcelas en
                          buildParcelaFeatureMeta). Se muestra en el orden
                          convencional «lat, lng» con 6 decimales (~10 cm). */}
                      <div className="sig-meta-cell sig-meta-cell--coords">
                        <span className="sig-meta-k">Coordenadas</span>
                        <strong className="sig-meta-coords-mono">
                          {typeof sigMeta.lat === 'number' && typeof sigMeta.lng === 'number'
                            ? `${sigMeta.lat.toFixed(6)}, ${sigMeta.lng.toFixed(6)}`
                            : '—'}
                        </strong>
                      </div>
                    </div>
                  </div>
                )}

                <fieldset className="form-section" disabled={!parcelaId}>
                  <legend>{esUsoComun ? 'Comunidad / órgano de representación' : 'Datos del propietario'}</legend>
                  {esUsoComun && (
                    <p className="hint" style={{ marginTop: -4, marginBottom: 10, fontSize: 12, opacity: 0.75 }}>
                      Las tierras de uso común pertenecen al núcleo agrario: captura aquí al
                      representante (p. ej. presidente del comisariado ejidal) o el nombre del
                      órgano. Los datos personales son opcionales — no inventes folios RAN
                      individuales para uso común.
                    </p>
                  )}

                  {/* Banner: el CURP tipeado matchea un propietario que YA es
                      titular de otra(s) parcela(s) del tenant. Permite cargar
                      sus datos PII (descifra via BFF con MFA) en lugar de
                      re-tipear. Click "Es otra persona" lo descarta. */}
                  {propietarioMatch && !propietarioMatchDismissed && (
                    <div
                      role="status"
                      style={{
                        marginBottom: 12, padding: '12px 14px',
                        borderRadius: 10, border: '1px solid rgba(180, 201, 111, 0.4)',
                        background: 'rgba(180, 201, 111, 0.08)', display: 'flex',
                        alignItems: 'center', gap: 12, flexWrap: 'wrap',
                      }}
                    >
                      <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ color: 'var(--btn-primary-bg, #b4c96f)', flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                      </svg>
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <strong style={{ display: 'block', marginBottom: 2 }}>
                          {propietarioMatch.titular_masked ?? 'Titular existente'} · ya es titular de {propietarioMatch.parcelas_count} parcela{propietarioMatch.parcelas_count === 1 ? '' : 's'}
                        </strong>
                        <span style={{ fontSize: 12, opacity: 0.85 }}>
                          {propietarioMatch.parcelas.slice(0, 4).join(' · ')}
                          {propietarioMatch.parcelas.length > 4 ? ` · +${propietarioMatch.parcelas.length - 4} más` : ''}
                          {' — ¿cargar sus datos en este formulario?'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ fontSize: 12, padding: '6px 12px' }}
                          onClick={() => { void loadFromPropietarioMatch(propietarioMatch); }}
                        >
                          Cargar datos del titular
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          style={{ fontSize: 12, padding: '6px 10px' }}
                          onClick={() => setPropietarioMatchDismissed(true)}
                        >
                          Es otra persona
                        </button>
                      </div>
                    </div>
                  )}
                  {propietarioMatchLoading && !propietarioMatch && (
                    <div style={{ marginBottom: 8, fontSize: 12, opacity: 0.6 }}>
                      Buscando titular existente por CURP…
                    </div>
                  )}

                  <div className="form-row">
                    <label>
                      <span>{esUsoComun ? 'Representante / órgano de representación' : 'Nombre completo'}</span>
                      <input
                        name="propietario"
                        placeholder={esUsoComun ? 'Ej. COMISARIADO EJIDAL — se cifra al guardar' : 'Se cifra al guardar'}
                        maxLength={200}
                        {...bindInput('propietario')}
                      />
                    </label>
                    <label>
                      <span>Tipo de tenencia</span>
                      <select name="tipo_propietario" {...bindInput('tipo_propietario')}>
                        {tipoDoc === 'privado' ? (
                          <option value="pp">Pequeña propiedad (RPP)</option>
                        ) : esUsoComun ? (
                          <>
                            <option value="comisariado">Comisariado / órgano de representación</option>
                            <option value="comunero">Comunero</option>
                          </>
                        ) : (
                          <>
                            <option value="ejidatario">Ejidatario</option>
                            <option value="posesionario">Posesionario</option>
                          </>
                        )}
                      </select>
                    </label>
                  </div>
                  <div className="form-row">
                    <label className={fieldErrors.telefono ? 'has-error' : undefined}>
                      <span>Teléfono <small className="hint-mini">10 dígitos</small></span>
                      <input
                        name="telefono"
                        type="tel"
                        placeholder="442 123 4567"
                        inputMode="tel"
                        maxLength={20}
                        autoComplete="tel"
                        {...bindFieldValidated('telefono')}
                        aria-invalid={!!fieldErrors.telefono}
                        aria-describedby={fieldErrors.telefono ? 'err-telefono' : undefined}
                      />
                      {fieldErrors.telefono && (
                        <span className="field-error-msg" id="err-telefono" role="alert">{fieldErrors.telefono}</span>
                      )}
                    </label>
                    <label className={fieldErrors.email ? 'has-error' : undefined}>
                      <span>Correo electrónico</span>
                      <input
                        name="email"
                        type="email"
                        placeholder="ejemplo@correo.com"
                        autoComplete="email"
                        {...bindFieldValidated('email')}
                        aria-invalid={!!fieldErrors.email}
                        aria-describedby={fieldErrors.email ? 'err-email' : undefined}
                      />
                      {fieldErrors.email && (
                        <span className="field-error-msg" id="err-email" role="alert">{fieldErrors.email}</span>
                      )}
                    </label>
                  </div>
                  <div className="form-row">
                    <label className="full">
                      <span>Dirección</span>
                      <input
                        name="direccion"
                        placeholder="Calle, colonia, municipio"
                        maxLength={500}
                        {...bindInput('direccion')}
                      />
                    </label>
                  </div>
                  <div className="form-row">
                    <label className={fieldErrors.numero_ine ? 'has-error' : undefined}>
                      <span>Clave de elector INE <small className="hint-mini">18 chars</small></span>
                      <input
                        name="numero_ine"
                        placeholder="Frente de la credencial"
                        maxLength={18}
                        autoCapitalize="characters"
                        spellCheck={false}
                        {...bindFieldValidated('numero_ine')}
                        aria-invalid={!!fieldErrors.numero_ine}
                        aria-describedby={fieldErrors.numero_ine ? 'err-numero_ine' : undefined}
                      />
                      {fieldErrors.numero_ine && (
                        <span className="field-error-msg" id="err-numero_ine" role="alert">{fieldErrors.numero_ine}</span>
                      )}
                    </label>
                    <label className={fieldErrors.curp ? 'has-error' : undefined}>
                      <span>CURP <small className="hint-mini">18 chars</small></span>
                      <input
                        name="curp"
                        placeholder="Se cifra al guardar"
                        maxLength={18}
                        autoCapitalize="characters"
                        spellCheck={false}
                        {...bindFieldValidated('curp')}
                        aria-invalid={!!fieldErrors.curp}
                        aria-describedby={fieldErrors.curp ? 'err-curp' : undefined}
                      />
                      {fieldErrors.curp && (
                        <span className="field-error-msg" id="err-curp" role="alert">{fieldErrors.curp}</span>
                      )}
                    </label>
                  </div>
                  <div className="form-row">
                    <label className={fieldErrors.rfc ? 'has-error' : undefined}>
                      <span>RFC con homoclave <small className="hint-mini">12-13 chars</small></span>
                      <input
                        name="rfc"
                        placeholder="Se cifra al guardar"
                        maxLength={13}
                        autoCapitalize="characters"
                        spellCheck={false}
                        {...bindFieldValidated('rfc')}
                        aria-invalid={!!fieldErrors.rfc}
                        aria-describedby={fieldErrors.rfc ? 'err-rfc' : undefined}
                      />
                      {fieldErrors.rfc && (
                        <span className="field-error-msg" id="err-rfc" role="alert">{fieldErrors.rfc}</span>
                      )}
                    </label>
                    <label>
                      <span>Folio acta de nacimiento</span>
                      <input
                        name="acta_nacimiento_folio"
                        maxLength={40}
                        {...bindInput('acta_nacimiento_folio')}
                      />
                    </label>
                  </div>
                  {/* Folios RAN federales — formato `^[0-9]{2}(TM|FD)[0-9]{8}$`.
                      Se normalizan a uppercase + sin espacios al perder foco;
                      tanto el form (Zod) como el BFF revalidan al persistir. */}
                  <div className="form-row">
                    <label className={fieldErrors.folio_derechos ? 'has-error' : undefined}>
                      <span>Folio de derechos <small className="hint-mini">RAN · 12 chars (ej. 22FD00059032)</small></span>
                      <input
                        name="folio_derechos"
                        placeholder="22FD00059032"
                        maxLength={12}
                        autoCapitalize="characters"
                        spellCheck={false}
                        {...bindFieldValidated('folio_derechos')}
                        onBlur={(e) => {
                          // Normalizar uppercase + sin espacios antes de validar
                          const normalized = normalizeFolioRan(e.target.value);
                          if (normalized !== e.target.value) {
                            setFormData((prev) => ({ ...prev, folio_derechos: normalized }));
                          }
                          handleFieldBlur(e);
                        }}
                        aria-invalid={!!fieldErrors.folio_derechos}
                        aria-describedby={fieldErrors.folio_derechos ? 'err-folio_derechos' : undefined}
                      />
                      {fieldErrors.folio_derechos && (
                        <span className="field-error-msg" id="err-folio_derechos" role="alert">{fieldErrors.folio_derechos}</span>
                      )}
                    </label>
                    <label className={fieldErrors.folio_tierras ? 'has-error' : undefined}>
                      <span>Folio de tierras <small className="hint-mini">RAN · 12 chars (ej. 22TM00012345)</small></span>
                      <input
                        name="folio_tierras"
                        placeholder="22TM00012345"
                        maxLength={12}
                        autoCapitalize="characters"
                        spellCheck={false}
                        {...bindFieldValidated('folio_tierras')}
                        onBlur={(e) => {
                          const normalized = normalizeFolioRan(e.target.value);
                          if (normalized !== e.target.value) {
                            setFormData((prev) => ({ ...prev, folio_tierras: normalized }));
                          }
                          handleFieldBlur(e);
                        }}
                        aria-invalid={!!fieldErrors.folio_tierras}
                        aria-describedby={fieldErrors.folio_tierras ? 'err-folio_tierras' : undefined}
                      />
                      {fieldErrors.folio_tierras && (
                        <span className="field-error-msg" id="err-folio_tierras" role="alert">{fieldErrors.folio_tierras}</span>
                      )}
                    </label>
                  </div>
                  <div className="form-row">
                    <label className={`full${fieldErrors.clave_catastral ? ' has-error' : ''}`}>
                      <span>Clave catastral <small className="hint-mini">8–20 alfanuméricos uppercase (ej. F14C75I1349AD)</small></span>
                      <input
                        name="clave_catastral"
                        placeholder="F14C75I1349AD"
                        maxLength={20}
                        autoCapitalize="characters"
                        spellCheck={false}
                        {...bindFieldValidated('clave_catastral')}
                        onBlur={(e) => {
                          const normalized = normalizeClaveCatastral(e.target.value);
                          if (normalized !== e.target.value) {
                            setFormData((prev) => ({ ...prev, clave_catastral: normalized }));
                          }
                          handleFieldBlur(e);
                        }}
                        aria-invalid={!!fieldErrors.clave_catastral}
                        aria-describedby={fieldErrors.clave_catastral ? 'err-clave_catastral' : undefined}
                      />
                      {fieldErrors.clave_catastral && (
                        <span className="field-error-msg" id="err-clave_catastral" role="alert">{fieldErrors.clave_catastral}</span>
                      )}
                    </label>
                  </div>
                  <div className="form-row">
                    <label className={`full${fieldErrors.clabe ? ' has-error' : ''}`}>
                      <span>CLABE interbancaria <small className="hint-mini">18 dígitos · validamos checksum</small></span>
                      <input
                        name="clabe"
                        placeholder="Se cifra al guardar"
                        maxLength={18}
                        inputMode="numeric"
                        pattern="\d{18}"
                        spellCheck={false}
                        value={formData.clabe ?? ''}
                        onBlur={handleFieldBlur}
                        onChange={(e) => {
                          // Solo dígitos en CLABE — sanitiza al state controlado.
                          const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 18);
                          setFormData((prev) => ({ ...prev, clabe: onlyDigits }));
                          handleFieldChange(e);
                        }}
                        aria-invalid={!!fieldErrors.clabe}
                        aria-describedby={fieldErrors.clabe ? 'err-clabe' : undefined}
                      />
                      {fieldErrors.clabe && (
                        <span className="field-error-msg" id="err-clabe" role="alert">{fieldErrors.clabe}</span>
                      )}
                    </label>
                  </div>
                </fieldset>

                <fieldset className="form-section" disabled={!parcelaId}>
                  <legend>Quién atendió en campo</legend>
                  <div className="form-row">
                    <label>
                      <span>¿Atendió el propietario?</span>
                      <select name="es_el_propietario" value={esElPropietario}
                        onChange={(e) => setEsElPropietario(e.target.value as 'si' | 'no' | '')}>
                        <option value="">— selecciona —</option>
                        <option value="si">Sí, es el propietario</option>
                        <option value="no">No, atendió un tercero</option>
                      </select>
                    </label>
                    <label>
                      <span>{esUsoComun ? 'Signatario (representante del núcleo)' : 'Signatario del contrato'}</span>
                      <input
                        name="signatario_nombre"
                        placeholder={esUsoComun ? 'Quien firma por la comunidad (mandato de asamblea)' : 'Si difiere del propietario'}
                        {...bindInput('signatario_nombre')}
                      />
                    </label>
                  </div>
                  {esElPropietario === 'no' && (
                    <div className="form-row">
                      <label className="full">
                        <span>Relación del tercero con el propietario</span>
                        <input
                          name="tercero_relacion"
                          placeholder="Hijo, apoderado, representante ejidal…"
                          {...bindInput('tercero_relacion')}
                        />
                      </label>
                    </div>
                  )}
                  {tipoDoc === 'ejido' && (
                    <>
                      <div className="form-row">
                        <label>
                          <span>Calidad del posesionario</span>
                          <select name="calidad_posesionario" {...bindInput('calidad_posesionario')}>
                            <option value="">— selecciona —</option>
                            <option value="ejidatario_titular">Ejidatario titular</option>
                            <option value="posesionario">Posesionario reconocido</option>
                            <option value="avecindado">Avecindado</option>
                            <option value="heredero">Heredero (sucesión en trámite)</option>
                            <option value="cesionario">Cesionario (cesión no formalizada)</option>
                          </select>
                        </label>
                        <label>
                          <span>Relación con el ejido</span>
                          <input
                            name="relacion_ejido"
                            placeholder="Comisariado, vocal, ejidatario activo…"
                            {...bindInput('relacion_ejido')}
                          />
                        </label>
                      </div>
                      <div className="form-row">
                        <label className="full">
                          <span>Liderazgo al interior del ejido</span>
                          <input
                            name="liderazgo_ejido"
                            placeholder="Cargo o influencia relevante"
                            {...bindInput('liderazgo_ejido')}
                          />
                        </label>
                      </div>
                    </>
                  )}
                </fieldset>
              </>
            )}

            {/* ══ PASO 2 · Negociación y estado ════════════════ */}
            {step === 2 && (
              <>
                <fieldset className="form-section">
                  <legend>Términos económicos</legend>

                  {/* ── Sub-bloque 1 · Preliminar ─────────────────── */}
                  <div className="term-block">
                    <h4 className="form-subsection-head">Preliminar</h4>
                    <div className="form-row">
                      <label>
                        <span>Superficie total de la parcela (ha)</span>
                        <input
                          name="preliminar_superficie_ha"
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder={superficieEsAutomatica ? '' : 'Toca una parcela en el mapa'}
                          value={
                            superficieEsAutomatica
                              ? formatHa(superficieParcelaHa)
                              : superficieManualHa == null
                                ? ''
                                : formatHa(superficieManualHa)
                          }
                          readOnly={superficieEsAutomatica}
                          title={superficieEsAutomatica ? 'Auto desde GeoJSON de la parcela' : undefined}
                          onChange={(e) => {
                            if (superficieEsAutomatica) return;
                            const n = parseHaInput(e.target.value);
                            setSuperficieManualHa(n);
                          }}
                        />
                      </label>
                      <label>
                        <span>Tarifa preliminar (MXN/ha)</span>
                        <input
                          name="preliminar_tarifa_mxn_ha"
                          type="text"
                          inputMode="numeric"
                          value={formatMxn(PRELIMINAR_MXN_POR_HA, '')}
                          readOnly
                        />
                      </label>
                    </div>
                    <div className="form-row">
                      <label className="full">
                        <span>Monto preliminar (calculado)</span>
                        <output className="form-output">{formatMxn(modeloNegocio.preliminar.monto_mxn)}</output>
                      </label>
                    </div>
                  </div>

                  {/* ── Sub-bloque 2 · Bono por firma ─────────────── */}
                  <div className="term-block">
                    <h4 className="form-subsection-head">Bono por firma</h4>
                    <div className="form-row">
                      <label className="full">
                        <span>Bono por firma (MXN)</span>
                        <input
                          name="bono_firma_mxn"
                          type="text"
                          inputMode="numeric"
                          placeholder="$0"
                          value={bonoFirmaMxn == null ? '' : formatMxn(bonoFirmaMxn, '')}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            if (raw === '') return setBonoFirmaMxn(null);
                            const n = Number(raw);
                            if (Number.isFinite(n) && n >= 0) setBonoFirmaMxn(n);
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  {/* ── Sub-bloque 3 · Construcción y operación ───── */}
                  <div className="term-block">
                    <h4 className="form-subsection-head">Construcción y operación</h4>
                    <div className="form-row">
                      <label>
                        <span>Plazo de referencia (años)</span>
                        <input
                          name="construccion_plazo_anios"
                          type="number"
                          min={1}
                          max={50}
                          step={1}
                          value={plazoConstruccionAnios}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            if (Number.isFinite(v) && v > 0) setPlazoConstruccionAnios(Math.round(v));
                          }}
                        />
                      </label>
                      <label>
                        <span>Tarifa (MXN/unidad)</span>
                        <input
                          name="construccion_tarifa_mxn_unidad"
                          type="text"
                          inputMode="numeric"
                          placeholder="$0"
                          value={formatMxn(tarifaConstruccionMxn, '')}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            if (raw === '') return setTarifaConstruccionMxn(0);
                            const n = Number(raw);
                            if (Number.isFinite(n) && n >= 0) setTarifaConstruccionMxn(n);
                          }}
                        />
                      </label>
                    </div>
                    <div className="form-row">
                      <label>
                        <span>Superficie aplicable (ha o unidades)</span>
                        <input
                          name="construccion_cantidad_unidades"
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="Total parcela u otra cifra"
                          value={cantidadUnidades == null ? '' : formatHa(cantidadUnidades)}
                          onChange={(e) => {
                            setCantidadUnidadesDirty(true);
                            const n = parseHaInput(e.target.value);
                            setCantidadUnidades(n);
                          }}
                        />
                      </label>
                      <label>
                        <span>Subtotal (calculado)</span>
                        <output className="form-output">{formatMxn(modeloNegocio.construccion_operacion.subtotal_mxn)}</output>
                      </label>
                    </div>

                    {/* Diferimiento mensual: un solo control concreto.
                        Antes vivía un sub-bloque con N tramos arbitrarios; el
                        feedback de campo fue que en la práctica todos los
                        acuerdos se diferían a un número fijo de mensualidades
                        del subtotal de construcción. Ahora el técnico ingresa
                        meses y la mensualidad se calcula sola. */}
                    <div
                      className="diferir-mensual"
                      role="group"
                      aria-labelledby="diferir-mensual-heading"
                    >
                      <label className="diferir-mensual-field">
                        <span id="diferir-mensual-heading">Diferir en (meses)</span>
                        <input
                          type="number"
                          name="diferir_meses"
                          min={0}
                          max={300}
                          step={1}
                          inputMode="numeric"
                          autoComplete="off"
                          placeholder="0 = al contado"
                          value={diferirMeses ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value.trim();
                            if (raw === '') return setDiferirMeses(null);
                            const n = Number(raw);
                            if (!Number.isFinite(n) || n <= 0) return setDiferirMeses(null);
                            setDiferirMeses(Math.min(300, Math.round(n)));
                          }}
                        />
                      </label>
                      <div className="diferir-mensual-preview" aria-live="polite">
                        <span className="diferir-mensual-preview-label">Mensualidad</span>
                        <strong
                          className={`diferir-mensual-preview-amount${
                            modeloNegocio.pagos_diferidos.monto_mensual_mxn != null ? '' : ' is-empty'
                          }`}
                        >
                          {modeloNegocio.pagos_diferidos.monto_mensual_mxn != null
                            ? `${formatMxn(modeloNegocio.pagos_diferidos.monto_mensual_mxn)} / mes`
                            : 'Al contado'}
                        </strong>
                      </div>
                      <label className="diferir-mensual-track">
                        <input
                          type="checkbox"
                          name="seguimiento_mensual"
                          checked={seguimientoMensual}
                          onChange={(e) => setSeguimientoMensual(e.target.checked)}
                          disabled={diferirMeses == null}
                        />
                        <span>Seguimiento mensual</span>
                      </label>
                    </div>
                  </div>
                  {/* Resumen: solo total a pagar + plazo (+ mensualidad si aplica).
                      Antes mostraba 4 tarjetas (Preliminar/Bono/Construcción/Total)
                      + grilla de pagos diferidos vs al contado. Ahora una sola
                      pieza con la cifra que el técnico necesita ver de un vistazo. */}
                  <div className="terminos-resumen" aria-label="Resumen de términos económicos">
                    <div className="terminos-resumen-head">
                      <span>Resumen</span>
                      <div className="terminos-resumen-head-actions">
                        <span className="terminos-resumen-head-meta">
                          {modeloNegocio.moneda} · v{modeloNegocio.version}
                        </span>
                        {/*
                          Contexto compartido entre los botones Excel y PDF.
                          Trae identificación, agrarios y fecha de firma.
                          Para titular_masked priorizamos los datos descifrados
                          (unmaskedData) si MFA reciente está activo; fallback
                          al masked del hydrate. Folios/clave_catastral van por
                          el mismo path que el form (form > unmasked > hydrate).
                        */}
                        {/* PRETTIER-IGNORE: bloque compartido por los 2 botones */}
                        {(() => null)()}
                        {/* Excel — workbook contable con 3 hojas:
                            Resumen / Calendario de pagos / Datos crudos.
                            Reemplaza el CSV legacy (eliminado 2026-05-09):
                            cubre el mismo caso de uso de "datos crudos" via
                            la Hoja 3 + suma vista contable presentable. */}
                        <button
                          type="button"
                          className="terminos-resumen-export-btn terminos-resumen-export-btn--xlsx"
                          title="Descargar Excel contable · 3 hojas (resumen, calendario de pagos, datos crudos)"
                          aria-label="Descargar resumen económico como Excel"
                          onClick={async () => {
                            await downloadResumenEconomicoXlsx(modeloNegocio, {
                              parcela_id: parcelaId ?? null,
                              num_parcela: parcelaMeta?.num_parcela ?? null,
                              nom_nucleo: parcelaMeta?.nom_nucleo ?? null,
                              municipio: parcelaMeta?.municipio ?? null,
                              area_ha:
                                parcelaMeta?.area_ha != null ? Number(parcelaMeta.area_ha) : null,
                              fecha_firma_est: pickField('fecha_firma_est') ?? null,
                              folio_derechos:
                                unmaskedData?.propietario?.folio_derechos
                                ?? pickField('folio_derechos')
                                ?? hydrateData.propietario?.folio_derechos
                                ?? null,
                              folio_tierras:
                                unmaskedData?.propietario?.folio_tierras
                                ?? pickField('folio_tierras')
                                ?? hydrateData.propietario?.folio_tierras
                                ?? null,
                              clave_catastral:
                                unmaskedData?.propietario?.clave_catastral
                                ?? pickField('clave_catastral')
                                ?? hydrateData.propietario?.clave_catastral
                                ?? null,
                              titular_masked:
                                hydrateData.propietario?.titular_masked
                                ?? unmaskedData?.propietario?.titular_masked
                                ?? null,
                              // PII descifrada (solo si MFA del técnico vigente).
                              // El descifre lo audita el BFF al hidratar.
                              titular_completo: unmaskedData?.propietario?.titular ?? null,
                              telefono: unmaskedData?.propietario?.telefono ?? null,
                            });
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width={12}
                            height={12}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.4}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <rect x={3} y={3} width={18} height={18} rx={2} ry={2} />
                            <line x1="9" y1="9" x2="15" y2="15" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                          </svg>
                          Excel
                        </button>
                        {/* PDF — resumen visual ejecutivo con logos, ideal
                            para imprimir / mandar al cliente. */}
                        <button
                          type="button"
                          className="terminos-resumen-export-btn terminos-resumen-export-btn--pdf"
                          title="Descargar PDF ejecutivo con logos · ideal para cliente o impresión"
                          aria-label="Descargar resumen económico como PDF"
                          onClick={async () => {
                            const ctx = {
                              parcela_id: parcelaId ?? null,
                              num_parcela: parcelaMeta?.num_parcela ?? null,
                              nom_nucleo: parcelaMeta?.nom_nucleo ?? null,
                              municipio: parcelaMeta?.municipio ?? null,
                              area_ha:
                                parcelaMeta?.area_ha != null ? Number(parcelaMeta.area_ha) : null,
                              fecha_firma_est: pickField('fecha_firma_est') ?? null,
                              folio_derechos:
                                unmaskedData?.propietario?.folio_derechos
                                ?? pickField('folio_derechos')
                                ?? hydrateData.propietario?.folio_derechos
                                ?? null,
                              folio_tierras:
                                unmaskedData?.propietario?.folio_tierras
                                ?? pickField('folio_tierras')
                                ?? hydrateData.propietario?.folio_tierras
                                ?? null,
                              clave_catastral:
                                unmaskedData?.propietario?.clave_catastral
                                ?? pickField('clave_catastral')
                                ?? hydrateData.propietario?.clave_catastral
                                ?? null,
                              titular_masked:
                                hydrateData.propietario?.titular_masked
                                ?? unmaskedData?.propietario?.titular_masked
                                ?? null,
                              // PII descifrada (solo si MFA del técnico vigente).
                              // El descifre lo audita el BFF al hidratar.
                              titular_completo: unmaskedData?.propietario?.titular ?? null,
                              telefono: unmaskedData?.propietario?.telefono ?? null,
                            };
                            const [{ pdf }, { ResumenEconomicoDocument }] = await Promise.all([
                              import('@react-pdf/renderer'),
                              import('./ResumenEconomicoDocument'),
                            ]);
                            const blob = await pdf(
                              <ResumenEconomicoDocument modelo={modeloNegocio} ctx={ctx} />,
                            ).toBlob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            const stamp = new Date().toISOString().slice(0, 10);
                            const tag = parcelaId ? `-${parcelaId}` : '';
                            a.download = `resumen-economico${tag}-${stamp}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width={12}
                            height={12}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.4}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <path d="M9 13h6" />
                            <path d="M9 17h6" />
                          </svg>
                          PDF
                        </button>
                      </div>
                    </div>

                    {/* Cards por fase: Preliminar · Bono firma · Construcción.
                        Tipografía menor que el total para que la cifra final
                        siga siendo el ancla visual. Cada card muestra label
                        en uppercase + monto; "—" cuando el componente no tiene
                        valor (sin pisar la mirada del técnico). */}
                    <div
                      className="terminos-resumen-phases"
                      role="group"
                      aria-label="Acumulado por fase"
                    >
                      {([
                        { key: 'preliminar',   label: 'Preliminar',   amount: modeloNegocio.preliminar.monto_mxn },
                        { key: 'bono_firma',   label: 'Bono firma',   amount: modeloNegocio.bono_firma.monto_mxn },
                        { key: 'construccion', label: 'Construcción', amount: modeloNegocio.construccion_operacion.subtotal_mxn },
                      ] as const).map((phase) => {
                        const hasValue = phase.amount != null;
                        return (
                          <div
                            key={phase.key}
                            className={`terminos-resumen-phase${hasValue ? ' has-value' : ''}`}
                          >
                            <span className="terminos-resumen-phase-label">{phase.label}</span>
                            <strong
                              className={`terminos-resumen-phase-amount${hasValue ? '' : ' is-empty'}`}
                            >
                              {formatMxn(phase.amount)}
                            </strong>
                          </div>
                        );
                      })}
                    </div>

                    <div className="terminos-resumen-total">
                      <span className="terminos-resumen-total-label">Total a pagar</span>
                      <strong
                        className={`terminos-resumen-total-amount${
                          modeloNegocio.totales.suma_componentes_mxn != null ? '' : ' is-empty'
                        }`}
                      >
                        {formatMxn(modeloNegocio.totales.suma_componentes_mxn)}
                      </strong>
                    </div>
                    <p className="terminos-resumen-meta">
                      <span>
                        Construcción y operación · plazo {modeloNegocio.construccion_operacion.plazo_anios} año
                        {modeloNegocio.construccion_operacion.plazo_anios === 1 ? '' : 's'}
                      </span>
                      {modeloNegocio.pagos_diferidos.monto_mensual_mxn != null &&
                       modeloNegocio.pagos_diferidos.meses != null ? (
                        <>
                          <span className="terminos-resumen-meta-sep">·</span>
                          <span>
                            {modeloNegocio.pagos_diferidos.meses} mensualidades de{' '}
                            {formatMxn(modeloNegocio.pagos_diferidos.monto_mensual_mxn)}
                            {modeloNegocio.pagos_diferidos.seguimiento_mensual ? ' · seguimiento' : ''}
                          </span>
                        </>
                      ) : null}
                    </p>
                  </div>

                  {/* ── Otros datos contractuales ─────────────────── */}
                  {/* Compromiso clave: fecha estimada de firma. Antes
                      compartía fila con un select "Modalidad de pago"
                      (anual/semestral/mensual) que se retiró 2026-05-09.
                      Ahora la fecha vive sola en una card destacada con
                      kicker uppercase teal + accent-border lateral, font
                      más grande y helper text — UX da peso visual sin
                      gritar, alineado al patrón Elawan. */}
                  <div className="captura-firma-field">
                    <span className="captura-firma-kicker">Compromiso clave</span>
                    <label className="captura-firma-label">
                      <span className="captura-firma-label-text">
                        <svg
                          viewBox="0 0 24 24"
                          width={14}
                          height={14}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <rect x={3} y={4} width={18} height={18} rx={2} ry={2} />
                          <line x1={16} y1={2} x2={16} y2={6} />
                          <line x1={8} y1={2} x2={8} y2={6} />
                          <line x1={3} y1={10} x2={21} y2={10} />
                        </svg>
                        Fecha estimada de firma
                      </span>
                      <input
                        name="fecha_firma_est"
                        type="date"
                        className="captura-firma-input"
                        {...bindInput('fecha_firma_est')}
                      />
                    </label>
                    <p className="captura-firma-hint">
                      Hito comercial · alimenta reportes ejecutivos y proyección de flujos del proyecto.
                    </p>
                  </div>
                  <div className="form-row">
                    <label className="full">
                      <span>Situación legal del predio</span>
                      <select name="situacion_legal" {...bindInput('situacion_legal')}>
                        <option value="">— selecciona —</option>
                        <option value="libre">Libre de gravamen</option>
                        <option value="hipoteca">Hipoteca activa</option>
                        <option value="embargo">Embargo</option>
                        <option value="litigio">En litigio</option>
                        <option value="sucesion">Sucesión pendiente</option>
                        <option value="otro">Otro</option>
                      </select>
                    </label>
                  </div>
                  {/* Colindancias — texto libre por punto cardinal.
                      Origen preferente: GeoJSON `properties.colindancias`
                      (autollenado conservador en handleParcelaClick + botón
                      explícito de re-aplicar abajo). Si el GeoJSON no trae
                      el campo, se muestra empty-state honesto y los inputs
                      quedan editables como siempre. */}
                  <div className="colindancias-toolbar" role="group" aria-label="Acciones de colindancias">
                    <span className="colindancias-toolbar-label">Colindancias</span>
                    {sigMeta?.colindancias ? (
                      <button
                        type="button"
                        className="btn-ghost colindancias-toolbar-apply"
                        onClick={applyColindanciasFromMeta}
                        disabled={!parcelaId}
                        title="Reemplaza los 4 campos con los valores declarados en la capa GeoJSON"
                      >
                        Aplicar colindancias desde capa
                      </button>
                    ) : (
                      <span className="colindancias-toolbar-hint" role="note">
                        {parcelaId
                          ? 'Esta parcela no tiene colindancias en el GeoJSON. Captúralas manualmente.'
                          : 'Selecciona una parcela para ver sus colindancias.'}
                      </span>
                    )}
                  </div>
                  <div className="form-row">
                    <label><span>Norte</span><input name="col_norte" {...bindInput('col_norte')} /></label>
                    <label><span>Sur</span><input name="col_sur" {...bindInput('col_sur')} /></label>
                  </div>
                  <div className="form-row">
                    <label><span>Oriente</span><input name="col_oriente" {...bindInput('col_oriente')} /></label>
                    <label><span>Poniente</span><input name="col_poniente" {...bindInput('col_poniente')} /></label>
                  </div>
                  <div className="form-row">
                    <label className="full">
                      <span>Condiciones especiales</span>
                      <textarea name="condiciones" rows={2} {...bindInput('condiciones')} />
                    </label>
                  </div>
                </fieldset>

                <fieldset className="form-section">
                  <legend>Estado y gestión</legend>
                  {/* Estado de gestión: chip-selector visual con dot de
                      semáforo. Reemplaza al <select> para que la elección sea
                      inmediata + comunique que controla el color del estado. */}
                  <div className="form-row">
                    <div className="form-field-full">
                      <span className="form-field-label">Estado de gestión</span>
                      <div
                        className="estado-chips"
                        role="radiogroup"
                        aria-label="Estado de gestión"
                      >
                        {ESTADOS_GESTION.map((s) => {
                          const isSelected = estado === s;
                          return (
                            <button
                              key={s}
                              type="button"
                              role="radio"
                              aria-checked={isSelected}
                              className={`estado-chip${isSelected ? ' is-selected' : ''}`}
                              style={{ ['--chip-color' as never]: ESTADO_COLOR[s] }}
                              onClick={() => setEstado(s)}
                              title={ESTADO_LABEL[s]}
                            >
                              <span className="estado-chip-dot" aria-hidden />
                              <span className="estado-chip-label">{ESTADO_CHIP_LABEL[s]}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <label>
                      <span>Postura del propietario</span>
                      <select name="postura_propietario" {...bindInput('postura_propietario')}>
                        <option value="">— selecciona —</option>
                        <option value="interesado">Interesado · dispuesto a firmar</option>
                        <option value="en_analisis">En análisis · pendiente de decisión</option>
                        <option value="neutral">Neutral · requiere más información</option>
                        <option value="reticente">Reticente · con objeciones</option>
                        <option value="rechaza">Rechaza la negociación</option>
                      </select>
                    </label>
                    <label>
                      <span>Nivel de riesgo</span>
                      <select name="riesgo" {...bindInput('riesgo')}>
                        <option value="">— sin evaluar —</option>
                        <option value="bajo">Bajo</option>
                        <option value="medio">Medio</option>
                        <option value="alto">Alto</option>
                      </select>
                    </label>
                  </div>
                  <div className="form-row">
                    <label className="full">
                      <span>Irregularidad detectada</span>
                      <textarea
                        name="irregularidad_detalle"
                        rows={3}
                        maxLength={IRREGULARIDAD_DESCRIPCION_MAX}
                        value={irregularidadDetalle}
                        placeholder="Describe la irregularidad (campo libre). Déjalo vacío si no aplica."
                        onChange={(e) => {
                          setIrregularidadDetalle(e.target.value);
                          if (!e.target.value.trim()) setGravedad('');
                        }}
                      />
                    </label>
                  </div>
                  {irregularidadDetalle.trim().length >= 2 && (
                    <div className="form-row">
                      <label className="full">
                        <span>Gravedad</span>
                        <select
                          name="gravedad"
                          value={gravedad}
                          onChange={(e) => setGravedad(e.target.value)}
                        >
                          <option value="">— selecciona —</option>
                          {GRAVEDADES.map((g) => (
                            <option key={g} value={g}>{GRAVEDAD_LABEL[g]}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  {estado === 'inviable' && (
                    <div className="form-row">
                      <label className="full">
                        <span>Motivo de inviabilidad</span>
                        <textarea
                          name="motivo_inviable"
                          rows={2}
                          placeholder="Describe el motivo…"
                          {...bindInput('motivo_inviable')}
                        />
                      </label>
                    </div>
                  )}
                </fieldset>

                <fieldset className="form-section" disabled={!parcelaId}>
                  <legend>Narrativa · visita y negociación</legend>
                  <p className="form-hint" style={{ marginTop: 0, marginBottom: 12 }}>
                    Persiste en gestión (notas JSON). Máximo {NARRATIVA_FIELD_MAX} caracteres por campo.
                  </p>
                  <div className="form-row">
                    <label className="full">
                      <span>Descripción general de la visita</span>
                      <textarea
                        name="descripcion_visita"
                        rows={4}
                        maxLength={NARRATIVA_FIELD_MAX}
                        placeholder="Recorrido, contexto del predio, personas presentes…"
                        {...bindInput('descripcion_visita')}
                      />
                    </label>
                  </div>
                  <div className="form-row">
                    <label className="full">
                      <span>Descripción de la negociación</span>
                      <textarea
                        name="descripcion_negociacion"
                        rows={4}
                        maxLength={NARRATIVA_FIELD_MAX}
                        placeholder="Ofertas, acuerdos tentativos, puntos abiertos…"
                        {...bindInput('descripcion_negociacion')}
                      />
                    </label>
                  </div>
                  <div className="form-row">
                    <label className="full">
                      <span>Requerimientos u observaciones para el área legal</span>
                      <textarea
                        name="reqs_area_legal"
                        rows={3}
                        maxLength={NARRATIVA_FIELD_MAX}
                        placeholder="Temas a validar por legal…"
                        {...bindInput('reqs_area_legal')}
                      />
                    </label>
                  </div>
                  <div className="form-row">
                    <label className="full">
                      <span>Observaciones del técnico</span>
                      <textarea
                        name="observaciones_tecnico"
                        rows={3}
                        maxLength={NARRATIVA_FIELD_MAX}
                        placeholder="Riesgos operativos, notas de campo…"
                        {...bindInput('observaciones_tecnico')}
                      />
                    </label>
                  </div>
                </fieldset>

                {/* ── Línea de avance: timeline horizontal de visitas ──
                    Estados: idle (sin parcela) · loading · empty · has-visits.
                    Siempre que haya parcela, se renderiza el marcador "hoy" al
                    extremo derecho como ancla temporal pulsante. */}
                {(() => {
                  const timelineState: 'idle' | 'loading' | 'empty' | 'has-visits' =
                    !parcelaId ? 'idle'
                    : visitasLoading ? 'loading'
                    : visitas.length === 0 ? 'empty'
                    : 'has-visits';
                  return (
                    <div
                      className={`visita-timeline visita-timeline--${timelineState}`}
                      role={timelineState === 'has-visits' ? 'list' : 'status'}
                      aria-label="Historial de visitas"
                    >
                      {timelineState === 'idle' && (
                        <div className="visita-timeline-message">
                          <svg className="visita-timeline-icon" viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          <span>Selecciona una parcela para ver su historial.</span>
                        </div>
                      )}
                      {timelineState === 'loading' && (
                        <div className="visita-timeline-message">
                          <span className="visita-timeline-spinner" aria-hidden="true" />
                          <span>Cargando historial…</span>
                        </div>
                      )}
                      {timelineState === 'empty' && (
                        <div className="visita-timeline-message">
                          <svg className="visita-timeline-icon" viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="9" />
                            <polyline points="12 7 12 12 15 14" />
                          </svg>
                          <div className="visita-timeline-message-text">
                            <strong>Aún no hay visitas registradas</strong>
                            <small>La primera quedará marcada en la línea →</small>
                          </div>
                        </div>
                      )}
                      {timelineState === 'has-visits' &&
                        /* Storage DESC; render ASC para flujo temporal izquierda→derecha. */
                        [...visitas].reverse().map((v, i, arr) => {
                          const isLatest = i === arr.length - 1;
                          const isExpanded = visitaExpandedId === v.id;
                          return (
                            <button
                              key={v.id}
                              type="button"
                              role="listitem"
                              className={`visita-timeline-node${isLatest ? ' is-latest' : ''}${isExpanded ? ' is-active' : ''}`}
                              onClick={() => setVisitaExpandedId(isExpanded ? null : v.id)}
                              title={formatVisitaFechaFull(v.fecha_visita)}
                              aria-pressed={isExpanded}
                            >
                              <span className="visita-timeline-dot" aria-hidden />
                              <span className="visita-timeline-label">{formatVisitaFechaNodo(v.fecha_visita)}</span>
                            </button>
                          );
                        })
                      }
                      {/* Ancla "hoy": siempre presente cuando hay parcela. Pulse animado
                          indica el punto donde caerá la próxima visita. */}
                      {timelineState !== 'idle' && timelineState !== 'loading' && (
                        <div className="visita-timeline-now" aria-label="Hoy">
                          <span className="visita-timeline-now-pulse" aria-hidden="true" />
                          <span className="visita-timeline-now-label">hoy</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <fieldset className="form-section">
                  <legend>Visitas de esta parcela</legend>
                  <div className="captura-visita-cta">
                    <p className="captura-visita-cta-text">
                      El registro completo de la visita (narrativa, fecha, GPS y trazabilidad) vive en el módulo{' '}
                      <strong>Visitas</strong>.
                      {parcelaId
                        ? ' Al abrirlo desde aquí, esta parcela queda seleccionada automáticamente.'
                        : ' Primero elige una parcela en el mapa; después podrás enlazarla al formulario.'}
                    </p>
                    <div className="captura-visita-cta-row">
                      <button
                        type="button"
                        className="btn-primary visita-save-btn"
                        onClick={() =>
                          navigate(
                            parcelaId
                              ? `/visitas?parcela_id=${encodeURIComponent(parcelaId)}`
                              : '/visitas',
                          )
                        }
                      >
                        Ir a Visitas
                      </button>
                    </div>
                  </div>

                  {/* ── Visitas anteriores (lista expandible) ────────── */}
                  {parcelaId && visitas.length > 0 && (
                    <div className="visitas-list" aria-label="Visitas anteriores">
                      <div className="visitas-list-head">
                        Visitas anteriores · {visitas.length}
                      </div>
                      <ul>
                        {visitas.map((v) => {
                          const expanded = visitaExpandedId === v.id;
                          const preview = narrativaPreview(v.narrativa) || v.notas?.slice(0, 80) || '';
                          return (
                            <li key={v.id} className={`visitas-list-item${expanded ? ' is-expanded' : ''}`}>
                              <button
                                type="button"
                                className="visitas-list-toggle"
                                onClick={() => setVisitaExpandedId(expanded ? null : v.id)}
                                aria-expanded={expanded}
                              >
                                <span className="visitas-list-fecha">{formatVisitaFechaFull(v.fecha_visita)}</span>
                                <span className="visitas-list-preview">{preview || <em>(sin contenido)</em>}</span>
                                <span className="visitas-list-caret" aria-hidden>{expanded ? '−' : '+'}</span>
                              </button>
                              {expanded && v.narrativa && (
                                <dl className="visitas-list-detail">
                                  <dt>Descripción general</dt>
                                  <dd>{v.narrativa.descripcion_visita || <em>—</em>}</dd>
                                  <dt>Negociación</dt>
                                  <dd>{v.narrativa.descripcion_negociacion || <em>—</em>}</dd>
                                  <dt>Área legal</dt>
                                  <dd>{v.narrativa.reqs_area_legal || <em>—</em>}</dd>
                                  <dt>Observaciones técnico</dt>
                                  <dd>{v.narrativa.observaciones_tecnico || <em>—</em>}</dd>
                                </dl>
                              )}
                              {expanded && !v.narrativa && v.notas && (
                                <p className="visitas-list-detail-legacy">{v.notas}</p>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </fieldset>
              </>
            )}

            {/* ══ PASO 3 · Documentación ═══════════════════════ */}
            {step === 3 && (
              <>
                <div className="form-section">
                  <div className="docs-checklist-header">
                    <span className="docs-checklist-label">
                      Documentos requeridos ·{' '}
                      <strong style={{ color: 'var(--teal-bright)' }}>{doneCount}/{docs.length}</strong>
                      <span
                        className="docs-checklist-tipo-pill"
                        title="Tipo definido en «Tipo de predio» al inicio. Vuelve al paso 1 para cambiarlo."
                      >
                        {tipoDoc === 'privado' ? 'Privado' : 'Ejidal'} ({docs.length})
                      </span>
                    </span>
                  </div>
                  {/* Iteramos por grupo — cada grupo abre con un label discreto
                      (li.doc-group-label) seguido de sus items. Mantenemos un
                      solo <ul> para no romper el spacing/animaciones del
                      checklist-list ni el indicador de "is-done". */}
                  <ul className="checklist-list">
                    {docGroups.map((group, gi) => (
                      <Fragment key={group.label}>
                        <li
                          className={`doc-group-label${gi === 0 ? ' is-first' : ''}`}
                          role="presentation"
                        >
                          {group.label}
                        </li>
                        {group.items.map((d) => (
                          <li key={d.id} id={`doc-row-${d.id}`} className={done[d.id] ? 'is-done' : ''}>
                            {/* Indicador de completado — solo visual, se activa al subir archivo */}
                            <span className="doc-check-toggle" aria-hidden>
                              {done[d.id] && (
                                <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                            </span>

                            {/* Etiqueta y nombre de archivo subido */}
                            <span className="doc-label-wrap">
                              {d.label}
                              {d.note && <small className="doc-note-inline">{d.note}</small>}
                              {docFiles[d.id] && (
                                <small className="doc-filename">
                                  ↑ {docFiles[d.id]?.name}
                                </small>
                              )}
                              {!docFiles[d.id] && existingDocs[d.id] && (
                                <small className="doc-filename" title="Archivo ya subido al expediente. Sube uno nuevo para reemplazar.">
                                  ✓ {existingDocs[d.id]?.filename}
                                </small>
                              )}
                            </span>

                            {/* Acciones: subir/cambiar + dar de baja (solo si hay
                                archivo persistido en server). docFiles[d.id]
                                tiene precedencia (reemplazo pendiente) — en
                                ese caso no mostramos baja, mostramos solo
                                "Cambiar" para evitar UI compleja. */}
                            <span className="doc-actions" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                              <label className="doc-upload-btn" title="Subir archivo para este documento">
                                {docFiles[d.id] ? (
                                  <>
                                    <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    Cambiar
                                  </>
                                ) : (
                                  <>
                                    <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    Subir
                                  </>
                                )}
                                <input
                                  type="file"
                                  hidden
                                  accept=".pdf,.jpg,.jpeg,.png,.kml,.kmz"
                                  onChange={(e) => handleDocFile(d.id, e.target.files)}
                                />
                              </label>
                              {!docFiles[d.id] && existingDocs[d.id] && (
                                <button
                                  type="button"
                                  className="doc-remove-btn"
                                  title="Dar de baja este archivo (queda registro en bitácora)"
                                  aria-label={`Dar de baja ${existingDocs[d.id]?.filename}`}
                                  onClick={() => {
                                    const doc = existingDocs[d.id];
                                    if (doc) void handleRemoveExistingDoc(d.id, doc);
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                    <path d="M10 11v6"/>
                                    <path d="M14 11v6"/>
                                    <path d="M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"/>
                                  </svg>
                                </button>
                              )}
                            </span>
                          </li>
                        ))}
                      </Fragment>
                    ))}
                  </ul>
                </div>

                <div className="form-section">
                  <p className="upload-section-label">Subir archivos al expediente</p>
                  <div
                    className={`upload-inline${drag ? ' is-dragover' : ''}`}
                    onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragOver={(e)  => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
                  >
                    <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--teal)', flexShrink: 0 }}>
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <div className="upload-inline-text">
                      <strong>Suelta archivos aquí</strong>
                      <span>PDF · JPG · PNG · KML/KMZ · máx 25 MB</span>
                    </div>
                    <label className="btn-ghost upload-inline-btn" style={{ cursor: 'pointer' }}>
                      Seleccionar
                      <input type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
                    </label>
                  </div>
                  {/* Archivos sueltos (tipo_doc='otro') YA persistidos en R2.
                      Mismo patrón que existingContratos — sin esto el técnico
                      no veía qué tenía subido y duplicaba (bug Diego 2026-05-11). */}
                  {existingOtros.length > 0 && (
                    <ul className="queued-list" aria-label="Archivos ya subidos al expediente">
                      {existingOtros.map((o) => (
                        <li key={o.id}>
                          <span className="queued-name" title="Archivo persistido en el expediente. Sube uno nuevo para agregar otro, o usa la papelera para darlo de baja.">
                            ✓ {o.filename}
                          </span>
                          <button
                            type="button"
                            className="doc-remove-btn"
                            title="Dar de baja este archivo (queda registro en bitácora)"
                            aria-label={`Dar de baja ${o.filename}`}
                            onClick={() => void handleRemoveExistingOtro(o)}
                          >
                            <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                              <path d="M10 11v6"/>
                              <path d="M14 11v6"/>
                              <path d="M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"/>
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {queued.length > 0 && (
                    <ul className="queued-list">
                      {queued.map((f) => (
                        <li key={f.name}>
                          <span className="queued-name">{f.name}</span>
                          <span className="queued-size">{(f.size / 1024).toFixed(0)} KB</span>
                          <button type="button" className="queued-remove" onClick={() => removeQueued(f.name)} aria-label="Quitar">×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="form-section">
                  <p className="upload-section-label">Contrato firmado</p>
                  <div
                    className={`upload-inline${contratoFirmadoDrag ? ' is-dragover' : ''}`}
                    onDragEnter={(e) => { e.preventDefault(); setContratoFirmadoDrag(true); }}
                    onDragOver={(e)  => { e.preventDefault(); setContratoFirmadoDrag(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setContratoFirmadoDrag(false); }}
                    onDrop={(e) => { e.preventDefault(); setContratoFirmadoDrag(false); handleContratoFirmadoFiles(e.dataTransfer.files); }}
                  >
                    <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--teal)', flexShrink: 0 }}>
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <path d="m9 15 2 2 4-4" />
                    </svg>
                    <div className="upload-inline-text">
                      <strong>Suelta el contrato firmado aquí</strong>
                      <span>PDF · ZIP · imágenes · máx 50 MiB · mismo archivo que ve el cliente en «Ver contrato»</span>
                    </div>
                    <label className="btn-ghost upload-inline-btn" style={{ cursor: 'pointer' }}>
                      Seleccionar
                      <input
                        type="file"
                        multiple
                        hidden
                        accept=".pdf,.zip,.jpg,.jpeg,.png"
                        onChange={(e) => handleContratoFirmadoFiles(e.target.files)}
                      />
                    </label>
                  </div>
                  {/* Contratos firmados YA persistidos en R2 para esta parcela.
                      Sin este bloque, el técnico no veía qué había subido y
                      duplicaba archivos (bug Diego 2026-05-11). Cada item con
                      botón "dar de baja" igual al patrón del checklist. */}
                  {existingContratos.length > 0 && (
                    <ul className="queued-list" aria-label="Contratos firmados ya subidos">
                      {existingContratos.map((c) => (
                        <li key={c.id}>
                          <span className="queued-name" title="Archivo persistido en el expediente. Sube uno nuevo para agregar otra versión, o usa la papelera para darlo de baja.">
                            ✓ {c.filename}
                          </span>
                          <button
                            type="button"
                            className="doc-remove-btn"
                            title="Dar de baja este contrato (queda registro en bitácora)"
                            aria-label={`Dar de baja ${c.filename}`}
                            onClick={() => void handleRemoveExistingContrato(c)}
                          >
                            <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                              <path d="M10 11v6"/>
                              <path d="M14 11v6"/>
                              <path d="M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"/>
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {contratoFirmadoQueued.length > 0 && (
                    <ul className="queued-list">
                      {contratoFirmadoQueued.map((f) => (
                        <li key={f.name}>
                          <span className="queued-name">{f.name}</span>
                          <span className="queued-size">{(f.size / 1024).toFixed(0)} KB</span>
                          <button type="button" className="queued-remove" onClick={() => removeContratoFirmadoQueued(f.name)} aria-label="Quitar">×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

          </div>{/* /form-body */}

          {/* Navegación del wizard */}
          <div className="form-actions">
            {step > 1
              ? <button type="button" className="btn-ghost" onClick={goPrev}>← Anterior</button>
              : <span />
            }
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
              {step < 3 ? (
                <button type="button" className="btn-primary" onClick={goNext}>Siguiente →</button>
              ) : (
                <button type="submit" className="btn-primary"
                  disabled={submitting || successInfo !== null}>
                  {submitting && <span className="btn-spinner" aria-hidden="true" />}
                  {submitting
                    ? 'Guardando…'
                    : successInfo !== null
                      ? 'Reporte guardado'
                      : 'Guardar reporte'}
                </button>
              )}
            </div>
          </div>
        </form>
      </aside>

      {/* Modal de éxito post-submit (solo se monta tras el submit del paso 3).
          Colores HARDCODEADOS (no var()) para garantizar contraste correcto
          en ambos themes: el wizard puede estar en light theme y los tokens
          --text/--bg-deep cambian de valor — el modal vive como overlay
          oscuro independiente del theme de la app. */}
      {successInfo && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="captura-success-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSuccessInfo(null);
          }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16,
          }}
        >
          <div
            style={{
              background: '#0c1216',
              color: '#f2f4ed',
              border: '1px solid rgba(255,255,255,0.16)',
              borderRadius: 14,
              padding: '28px 30px',
              maxWidth: 480,
              width: '100%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
              fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(180, 201, 111, 0.22)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg viewBox="0 0 24 24" width={20} height={20} fill="none"
                     stroke="#b4c96f" strokeWidth={2.5}
                     strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 id="captura-success-title" style={{
                margin: 0, fontSize: 17, fontWeight: 600, color: '#f2f4ed',
              }}>
                Reporte guardado
              </h3>
            </div>
            <p style={{
              margin: '0 0 18px',
              fontSize: 13.5,
              color: 'rgba(242,244,237,0.92)',
              lineHeight: 1.55,
            }}>
              <strong style={{ color: '#ffffff', fontWeight: 600 }}>
                {tituloParcelaConTitular(
                  successInfo.parcelaId,
                  successInfo.numParcela,
                  successInfo.propietarioNombre,
                )}
              </strong>
              <span style={{ color: 'rgba(242,244,237,0.7)' }}>
                {' · '}{successInfo.nomNucleo}
              </span>
              <br />
              <span style={{ color: 'rgba(242,244,237,0.78)' }}>
                Los datos están en Supabase. El cliente Elawan ya ve los cambios actualizados.
              </span>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  if (!successInfo) return;
                  // Diego: "mejor que me lleve al expediente de esa parcela".
                  // Navega internamente a /expedientes/{parcelaId} dentro del
                  // mismo SPA del equipo (la ruta ya existe en App.tsx). El
                  // user puede ver el detalle completo + docs subidos +
                  // adjuntar más sin perder contexto.
                  setSuccessInfo(null);
                  navigate(`/expedientes/${successInfo.parcelaId}`);
                }}
                style={{
                  padding: '11px 16px',
                  background: '#b4c96f',
                  color: '#0c1216',
                  border: 'none', borderRadius: 8,
                  fontSize: 13.5, fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Ver expediente de esta parcela
              </button>
              <button
                type="button"
                onClick={() => { void handleDescargarReporte(); }}
                disabled={generandoPdf}
                style={{
                  padding: '11px 16px',
                  background: 'transparent',
                  color: 'var(--btn-primary-bg, #b4c96f)',
                  border: '1px solid var(--btn-primary-bg, #b4c96f)',
                  borderRadius: 8,
                  fontSize: 13.5,
                  fontWeight: 500,
                  cursor: generandoPdf ? 'wait' : 'pointer',
                  opacity: generandoPdf ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {generandoPdf ? 'Generando…' : 'Descargar reporte'}
              </button>
              <button
                type="button"
                onClick={resetWizardForNew}
                style={{
                  padding: '11px 16px',
                  background: 'transparent',
                  color: '#f2f4ed',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 8,
                  fontSize: 13.5, fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Cargar otro
              </button>
              <button
                type="button"
                onClick={() => setSuccessInfo(null)}
                style={{
                  padding: '7px 16px',
                  background: 'transparent',
                  color: 'rgba(242,244,237,0.6)',
                  border: 'none',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Cerrar y seguir editando esta parcela
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtSigPoligono(p: string | number | undefined): string {
  if (p == null) return '—';
  if (p === 1 || p === '1') return 'P1';
  if (p === 2 || p === '2') return 'P2';
  if (typeof p === 'string' && p.toUpperCase().includes('PRIVADA')) return 'Prop. privada';
  return String(p);
}
